import {
    FileUploadPicker,
    FlexColumn,
    FlexRow,
    FlexRowBetween,
    LoaderBar,
} from 'components'
import { GetAuditById, GetPublicInspection, useNotification } from 'hooks'
import {
    InspectionSectionList,
    CustomerClaimPortalInspection,
} from 'page-components'
import { InspectionMaintenanceBanner } from 'page-components/maintenance/InspectionMaintenanceBanner'
import { QuestionThreadsProvider, QuestionThreadsSummary } from 'page-components/question-threads'
import {
    SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
    isSpecialCustomerClaimTemplate as matchSpecialCustomerClaimTemplate,
    buildClaimRowsFromAuditSections,
    extractQualityFormFromSection,
    extractCompletionFormFromSection,
    extractSupplierOptionsFromAudit,
    findQuestionByAliases,
    findSectionByName,
    buildClaimLiveSectionIdsByPhase,
    buildQuestionMapByFaq,
    questionToDropdownOptions,
    buildCustomerClaimPayload,
    buildCustomerClaimCompletionErrors,
    ensureCustomerClaimServerSectionsLoaded,
    mergeClaimRowsWithPortalDraft,
    resolveActiveClaimPortalRow,
    isClaimPortalRowFilled,
    portalRowToDraft,
    mergePortalDraftPreservingLocal,
    mergeQuestionFilesFromUpdatedSections,
    listClaimPortalSectionsFromAudit,
    findInspectionQuestionByTemplateRef,
    PORTAL_SECTION_NAME,
    QUALITY_SECTION_NAME,
    COMPLETION_SECTION_NAME,
    PORTAL_FAQ,
    QUALITY_FAQ,
    COMPLETION_FAQ,
    isCustomerClaimNonRmaTemplateId,
    isSpecialCustomerClaimTemplateId,
    readLayoutCode,
} from 'page-components/customer-complaint-log/customer-claim-inspection-utils'
import {
    normalizeSectionStatusEntry,
    overlayWorkflowStatusesOntoLiveSections,
    parseSectionStatusResponse,
    reportCompletedSectionEdits,
} from 'page-components/customer-complaint-log/ClaimSectionWorkflow'
import {
    SPECIAL_FINISH_MAT_TEMPLATE_ID,
    FINISH_MAT_GENERAL_INFO_SECTION_ID,
    FINISH_MAT_GENERAL_INFO_SECTION_NAME,
    filterFinishMatGeneralInfoFromSections,
    buildFinishMatOrderedRenderGroups,
} from 'page-components/finish-mat/finish-mat-inspection-utils'
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Input, Modal, Checkbox, Table, Form, Button } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { Eye, X } from 'phosphor-react'
import styled, { createGlobalStyle } from 'styled-components'
import { CustomePreview } from 'page-components/inspection/Elements'
import {
    AcceptFileType,
    AuthService,
    ControlType,
    ErrorConstant,
    getQuestionMediaList,
    InspectionService,
    linkUploadedMediaCache,
    mapInspectionQuestionMediaFiles,
    mergeQuestionFilesPreservingMeta,
    syncQuestionMediaFields,
    isDisplayOnlyInspectionQuestion,
    assignAutoNumberForInspectionQuestion,
    StatusType,
    TemplateQuestionResponseType,
    TemplateResponseType,
    getParentChildQuestionScore,
    normalizeParentChildResponseAnswers,
    applyParentChildResponseScores,
    getQuestionScoreFallback,
    hasParentChildAnswerSelection,
    getQuestionDisplayScore,
    applyInspectionScoreTotals,
    clearInspectionKeepingPressSelection,
    getPressResetTarget,
    isPressDownTemplate,
    isPressSelectionResetEnabled,
    isSectionNAAvailable,
    applyUniformRadioToSection,
    clearSectionAllAllow,
    isUniformRadioAnswer,
    readUniformRadioDisplayText,
    shouldClearSectionAllAllow,
    SectionWorkflowService,
    isPostCompleteEditBlocked,
    getTemplateSettingIdFromAudit,
    parsePostCompleteEditDisabledError,
    POST_COMPLETE_EDIT_DISABLED_MESSAGE,
    isAuditReadOnly,
    isCrossUserAccessExemptAudit,
    getAuditOwnerName,
    getAuditReadOnlyReason,
    getAuditReadOnlyMessage,
    parseAuditAccessError,
    applyLiveFormulas,
    omitFormulaAnswerValues,
    applyServerFormulaResults,
    readServerFormulaList,
    isFormulaQuestion,
} from 'utility'
import { TemplateService } from 'utility/services/template'
import { TranslationServiceInstance as TranslationService } from 'utility/services/translation'

const ScrollableContent = styled(Modal.Content)`
    max-height: 60vh;
    overflow-y: auto;

    .ui.table {
        border: none;
        margin-top: 0;
    }
`

const SMALL_DAILY_PLATEN_RESULT_MENU_Z_INDEX = 12000

const SmallDailyPlatenSectionWrap = styled.div`
    width: 100%;
    max-width: 100%;
    min-width: 0;
    margin-bottom: 1.5rem;
    position: relative;
    z-index: 2;

    &:has(.small-daily-platen-result-dropdown.active.visible) {
        z-index: 10050;
    }
`

const SmallDailyPlatenResultDropdown = ({
    options = [],
    value,
    onChange,
    disabled = false,
    className,
    style,
    placeholder = '-- Select --',
}) => {
    const [open, setOpen] = useState(false)
    const triggerRef = useRef(null)
    const [menuStyle, setMenuStyle] = useState({})

    const selectedOption = options.find(
        opt => String(opt.value) === String(value),
    )
    const displayText = selectedOption?.text || ''

    const updateMenuPosition = useCallback(() => {
        if (!triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        setMenuStyle({
            position: 'fixed',
            top: `${rect.bottom}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            minWidth: `${rect.width}px`,
            zIndex: SMALL_DAILY_PLATEN_RESULT_MENU_Z_INDEX,
        })
    }, [])

    useEffect(() => {
        if (!open) return undefined

        updateMenuPosition()
        const handleReposition = () => updateMenuPosition()
        window.addEventListener('scroll', handleReposition, true)
        window.addEventListener('resize', handleReposition)

        return () => {
            window.removeEventListener('scroll', handleReposition, true)
            window.removeEventListener('resize', handleReposition)
        }
    }, [open, updateMenuPosition])

    useEffect(() => {
        if (!open) return undefined

        const handleOutsideClick = event => {
            const { target } = event
            if (
                triggerRef.current?.contains(target) ||
                target?.closest?.('.small-daily-platen-result-menu')
            ) {
                return
            }
            setOpen(false)
        }

        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [open])

    const menu =
        open && !disabled
            ? createPortal(
                  <div
                      className="ui visible active menu transition small-daily-platen-result-menu"
                      style={menuStyle}
                  >
                      {options.map(opt => (
                          <div
                              key={opt.key ?? opt.value}
                              role="option"
                              aria-selected={
                                  String(opt.value) === String(value)
                              }
                              className={`item${
                                  String(opt.value) === String(value)
                                      ? ' selected active'
                                      : ''
                              }`}
                              onMouseDown={event => {
                                  event.preventDefault()
                                  onChange?.(opt.value)
                                  setOpen(false)
                              }}
                          >
                              {opt.text}
                          </div>
                      ))}
                  </div>,
                  document.body,
              )
            : null

    const mergedClassName = [
        'ui',
        'fluid',
        'selection',
        'dropdown',
        'small-daily-platen-result-dropdown',
        open ? 'active visible' : '',
        disabled ? 'disabled' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <>
            <div
                ref={triggerRef}
                className={mergedClassName}
                style={style}
                onClick={() => {
                    if (disabled) return
                    setOpen(prev => {
                        const next = !prev
                        if (next) {
                            requestAnimationFrame(updateMenuPosition)
                        }
                        return next
                    })
                }}
            >
                <i aria-hidden="true" className="dropdown icon" />
                <div className={`text${displayText ? '' : ' default'}`}>
                    {displayText || placeholder}
                </div>
            </div>
            {menu}
        </>
    )
}

const SmallDailyPlatenTableScroll = styled.div`
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
    overflow-x: auto;
    overflow-y: visible;
    margin-bottom: 0;

    .small-daily-platen-table.ui.table > thead > tr > th {
        background-color: ${({ theme }) => theme.colors.primary} !important;
        color: #ffffff !important;
        border-bottom: 2px solid ${({ theme }) => theme.colors.primary} !important;
        border-right-color: rgba(255, 255, 255, 0.12) !important;
        border-top: none !important;
        font-weight: 700 !important;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 0.78rem;
    }

    .small-daily-platen-table.ui.table > tbody > tr > td {
        vertical-align: top;
        border-color: #e8eaf0 !important;
        box-shadow: none !important;
        overflow: visible !important;
        position: relative;
    }

    .small-daily-platen-table.ui.table {
        border-radius: 6px !important;
        overflow: visible;
        border: 1px solid #dfe3eb !important;
        box-shadow: 0 1px 3px rgba(23, 58, 115, 0.06);
        background: #fff !important;
    }

    .small-daily-platen-table .platen-dd-accent.platen-dd-accent-conforming.dropdown i.dropdown.icon {
        color: #2e7d32 !important;
    }

    .small-daily-platen-table .platen-dd-accent.platen-dd-accent-nonconforming.dropdown i.dropdown.icon {
        color: #d32f2f !important;
    }

    .small-daily-platen-table .platen-dd-accent.platen-dd-accent-bypass.dropdown i.dropdown.icon {
        color: #e65100 !important;
    }

    .small-daily-platen-table .platen-dd-accent.platen-dd-accent-neutral.dropdown i.dropdown.icon {
        color: #9aa3ae !important;
    }

    .small-daily-platen-table .horizontal-media-icons {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        flex-wrap: nowrap;
    }
    .small-daily-platen-table .horizontal-media-action-picker .ui.buttons,
    .small-daily-platen-table .horizontal-media-action-picker .ui.buttons .button,
    .small-daily-platen-table .horizontal-media-action-picker .ui.button {
        margin: 0 !important;
    }
    .small-daily-platen-table .horizontal-media-action-picker > .field > .field > div {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: nowrap !important;
        align-items: center !important;
    }
    .small-daily-platen-table .horizontal-media-action-picker .ui.mini.button {
        white-space: nowrap !important;
        flex-shrink: 0 !important;
        margin: 0 !important;
    }
    .small-daily-platen-table .horizontal-media-action-picker .ui.mini.button i.icon {
        margin: 0 4px 0 0 !important;
    }
    .small-daily-platen-table .horizontal-media-view-btn.ui.button {
        min-width: 28px !important;
        width: 28px !important;
        height: 28px !important;
        padding: 0 !important;
        border-radius: 8px !important;
        background: #eef2f7 !important;
        color: #3f4d63 !important;
        border: 1px solid #d8e0ea !important;
        box-shadow: none !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
    }
    .small-daily-platen-table .horizontal-media-view-btn.ui.button:hover {
        background: #e1e9f2 !important;
    }
`

const SpecialPlatenTableScroll = styled.div`
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
    overflow-x: auto;
    overflow-y: visible;
    margin-bottom: 0;

    .special-platen-table.ui.table > thead > tr > th {
        background-color: ${({ theme }) => theme.colors.primary} !important;
        color: #ffffff !important;
        border-bottom: 2px solid ${({ theme }) => theme.colors.primary} !important;
        border-right-color: rgba(255, 255, 255, 0.12) !important;
        border-top: none !important;
        font-weight: 700 !important;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-size: 0.78rem;
    }

    .special-platen-table.ui.table > tbody > tr > td {
        vertical-align: top;
        border-color: #e8eaf0 !important;
        box-shadow: none !important;
        overflow: visible !important;
        position: relative;
    }

    .special-platen-table.ui.table > tbody > tr:not(.positive):not(.negative):not(.warning) > td {
        background-color: #ffffff !important;
    }

    .special-platen-table.ui.table > tbody > tr.positive > td {
        background-color: #eef9f0 !important;
    }

    .special-platen-table.ui.table > tbody > tr.negative > td {
        background-color: #fdeff2 !important;
    }

    .special-platen-table.ui.table > tbody > tr.warning > td {
        background-color: #fffbf0 !important;
    }

    .special-platen-table.ui.dropdown.selection .menu > .item {
        border-top: 1px solid #eef1f6;
    }

    .special-platen-table.ui.dropdown.selection .menu > .item:first-of-type {
        border-top: none;
    }

    .special-platen-table.ui.dropdown.selection .menu {
        border-radius: 8px;
        border-color: #bcd4ea !important;
    }

    .special-platen-table .platen-dd-accent.platen-dd-accent-conforming.dropdown i.dropdown.icon {
        color: #2e7d32 !important;
    }

    .special-platen-table .platen-dd-accent.platen-dd-accent-nonconforming.dropdown i.dropdown.icon {
        color: #d32f2f !important;
    }

    .special-platen-table .platen-dd-accent.platen-dd-accent-bypass.dropdown i.dropdown.icon {
        color: #e65100 !important;
    }

    .special-platen-table .platen-dd-accent.platen-dd-accent-neutral.dropdown i.dropdown.icon {
        color: #9aa3ae !important;
    }

    .special-platen-table.ui.table {
        border-radius: 6px !important;
        overflow: visible;
        border: 1px solid #dfe3eb !important;
        box-shadow: 0 1px 3px rgba(23, 58, 115, 0.06);
        background: #fff !important;
        position: relative;
        z-index: 1;
    }

    .special-platen-table .ui.selection.dropdown {
        position: relative;
        z-index: 2;
    }

    .special-platen-table .ui.selection.dropdown.active,
    .special-platen-table .ui.selection.dropdown.active.visible {
        z-index: 10060 !important;
    }

    .special-platen-table .ui.dropdown .menu,
    .special-platen-table .ui.dropdown.active.visible .menu {
        z-index: 12000 !important;
    }

    .special-platen-table.ui.striped.table > tbody > tr:nth-child(2n),
    .special-platen-table.ui.basic.table tbody tr {
        background-color: inherit !important;
    }

    .horizontal-media-icons {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
    }
    .horizontal-media-action-picker .ui.buttons,
    .horizontal-media-action-picker .ui.buttons .button,
    .horizontal-media-action-picker .ui.button {
        margin: 0 !important;
    }
    .horizontal-media-action-picker .ui.mini.button {
        min-width: 28px !important;
        width: 28px !important;
        height: 28px !important;
        padding: 0 !important;
        border-radius: 8px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        box-shadow: none !important;
    }
    .horizontal-media-view-btn.ui.button {
        min-width: 28px !important;
        width: 28px !important;
        height: 28px !important;
        padding: 0 !important;
        border-radius: 8px !important;
        background: #eef2f7 !important;
        color: #3f4d63 !important;
        border: 1px solid #d8e0ea !important;
        box-shadow: none !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
    }
    .horizontal-media-view-btn.ui.button:hover {
        background: #e1e9f2 !important;
    }
`

const SmallDailyPlatenResultMenuGlobalStyle = createGlobalStyle`
    .small-daily-platen-result-menu.ui.menu {
        display: block !important;
        margin: 0 !important;
        padding: 0 !important;
        border-radius: 0.28571429rem !important;
        border: 1px solid rgba(34, 36, 38, 0.15) !important;
        background: #fff !important;
        max-height: 200px;
        overflow-y: auto !important;
        box-shadow:
            0 2px 4px 0 rgba(34, 36, 38, 0.12),
            0 2px 10px 0 rgba(34, 36, 38, 0.08) !important;
    }

    .small-daily-platen-result-menu.ui.menu > .item {
        padding: 0.78571429rem 1.14285714rem !important;
        font-size: 1rem !important;
        line-height: 1.21428571em !important;
        color: rgba(0, 0, 0, 0.87) !important;
        cursor: pointer;
        white-space: normal !important;
        word-wrap: break-word !important;
        border-top: 1px solid #fafafa !important;
    }

    .small-daily-platen-result-menu.ui.menu > .item:first-child {
        border-top: none !important;
    }

    .small-daily-platen-result-menu.ui.menu > .item:hover {
        background: rgba(0, 0, 0, 0.05) !important;
    }

    .small-daily-platen-result-menu.ui.menu > .item.selected.active {
        background: rgba(0, 0, 0, 0.03) !important;
        font-weight: 700 !important;
    }

    .small-daily-platen-result-dropdown.ui.selection.dropdown {
        position: relative;
        width: 100%;
        min-height: 38px;
        display: flex !important;
        align-items: center;
        border: 1px solid rgba(34, 36, 38, 0.15);
        border-radius: 0.28571429rem;
        background: #fff;
        padding: 0.67857143em 2.1em 0.67857143em 1em;
        line-height: 1.21428571em;
        min-width: 0;
        cursor: pointer;
        box-sizing: border-box;
    }

    .small-daily-platen-result-dropdown.ui.selection.dropdown.disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    .small-daily-platen-result-dropdown > .text {
        font-size: 1rem;
        line-height: 1.21428571em;
        color: rgba(0, 0, 0, 0.87);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .small-daily-platen-result-dropdown > .text.default {
        color: rgba(0, 0, 0, 0.4);
    }

    .small-daily-platen-result-dropdown > .dropdown.icon {
        position: absolute;
        right: 1em;
        top: 50%;
        margin-top: -0.5em;
        opacity: 0.8;
    }
`

const HorizontalMediaModalGlobalStyle = createGlobalStyle`
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item {
        width: 110px;
        height: 110px;
        position: relative;
        flex-shrink: 0;
        overflow: visible;
    }
    .ui.modal.horizontal-media-modal .comment-text-area-popup > div {
        width: 110px;
        height: 110px;
        display: block;
    }
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .img.col-4,
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .horizontal-modal-preview-thumb {
        width: 110px !important;
        min-width: 110px !important;
        max-width: 110px !important;
        height: 110px !important;
        flex: 0 0 110px !important;
        padding: 0 !important;
        margin: 0 !important;
        display: block !important;
        box-sizing: border-box;
        position: relative !important;
        overflow: hidden;
    }
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .img > div:not(.delete-icon):not(.rotate-icon) {
        width: 100% !important;
        height: 110px !important;
        min-height: 110px !important;
    }
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .img img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover;
        border-radius: 6px;
    }
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .delete-icon,
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .rotate-icon {
        position: absolute !important;
        top: 5px !important;
        bottom: auto !important;
        width: 26px !important;
        height: 26px !important;
        min-width: 26px !important;
        min-height: 26px !important;
        max-width: 26px !important;
        max-height: 26px !important;
        padding: 4px !important;
        margin: 0 !important;
        flex: none !important;
        z-index: 5;
    }
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .delete-icon {
        right: 5px !important;
        left: auto !important;
    }
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .rotate-icon {
        left: 5px !important;
        right: auto !important;
    }
    .ui.modal.horizontal-media-modal .horizontal-media-preview-item .rotate-icon i.icon {
        font-size: 12px !important;
        width: auto !important;
        height: auto !important;
        margin: 0 !important;
        line-height: 1 !important;
    }
`

const SmallDailyPlatenMediaCell = ({
    mediaQuestion,
    sectionId,
    sections,
    isReadOnly,
    addMedia,
    removeMedia,
}) => {
    const [files, setFiles] = useState([])
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [isMediaUploading, setIsMediaUploading] = useState(false)
    const canUploadMedia = !isReadOnly && typeof addMedia === 'function'
    const hasFiles = Array.isArray(files) && files.length > 0

    const mediaSyncKey = useMemo(() => {
        const list = getQuestionMediaList(mediaQuestion)
        return list
            .map(
                m =>
                    `${m.MediaId ?? ''}:${m.FileKey ?? ''}:${m.SignedURL ?? m.FileURL ?? m.url ?? ''}:${m.Caption ?? ''}:${m.Comment ?? ''}`,
            )
            .join('|')
    }, [
        mediaQuestion?.Files,
        mediaQuestion?.Answers?.MediaResponse,
        mediaQuestion?.MediaAnswers,
    ])

    useEffect(() => {
        setFiles(mapInspectionQuestionMediaFiles(mediaQuestion))
    }, [mediaSyncKey, mediaQuestion?.Id])

    const onFileUpload = async uploadFiles => {
        if (!canUploadMedia || !mediaQuestion?.Id) return
        setIsMediaUploading(true)
        try {
            await addMedia(
                mediaQuestion.Id,
                uploadFiles,
                sectionId,
                sections,
            )
        } catch (error) {
            console.error('Error uploading media:', error)
        } finally {
            setIsMediaUploading(false)
        }
    }

    const saveComment = async (mediaItem, meta) => {
        if (!canUploadMedia || !mediaQuestion?.Id) return null

        const hasChanged =
            (meta.Comment ?? '') !== (mediaItem.Comment ?? '') ||
            (meta.Caption ?? '') !== (mediaItem.Caption ?? '')
        if (!hasChanged) return null

        setFiles(prev =>
            prev.map(f => {
                const match =
                    (mediaItem.MediaId && f.MediaId === mediaItem.MediaId) ||
                    (mediaItem.id && f.id === mediaItem.id)
                return match ? { ...f, ...meta } : f
            }),
        )

        try {
            const result = await addMedia(
                mediaQuestion.Id,
                [{ ...mediaItem, ...meta, local: false }],
                sectionId,
                sections,
            )
            const updated = result?.[0]
            if (updated) {
                setFiles(prev =>
                    prev.map(f => {
                        const match =
                            (updated.MediaId &&
                                f.MediaId === updated.MediaId) ||
                            (mediaItem.id && f.id === mediaItem.id)
                        return match
                            ? {
                                  ...f,
                                  ...updated,
                                  Caption: String(updated?.Caption ?? '').trim()
                                      ? updated.Caption
                                      : meta?.Caption ?? f.Caption ?? '',
                                  Comment: String(updated?.Comment ?? '').trim()
                                      ? updated.Comment
                                      : meta?.Comment ?? f.Comment ?? '',
                                  id: updated.MediaId || f.id,
                                  url:
                                      updated.FileURL ||
                                      updated.SignedURL ||
                                      f.url,
                                  name: updated.FileKey || f.name,
                              }
                            : f
                    }),
                )
            }
            return result
        } catch (e) {
            console.error('Error saving media caption/comment:', e)
            return null
        }
    }

    const onRemoveFile = async file => {
        if (!canUploadMedia || !mediaQuestion?.Id) return
        if (file?.url?.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(file.url)
            } catch (e) {
                // ignore
            }
        }
        if (typeof removeMedia === 'function') {
            const updated = await removeMedia(
                mediaQuestion.Id,
                file,
                sectionId,
            )
            setFiles(mapInspectionQuestionMediaFiles(updated || mediaQuestion))
            return
        }
        setFiles(prev =>
            prev.filter(
                ele =>
                    ele.id !== file.id && ele.MediaId !== file.MediaId,
            ),
        )
    }

    const previewGridStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 110px)',
        gap: '12px',
        maxHeight: '52vh',
        overflow: 'auto',
        alignItems: 'start',
        justifyContent: 'start',
    }
    const previewItemStyle = {
        position: 'relative',
        width: 110,
        height: 110,
    }

    return (
        <>
            <div
                className="horizontal-media-icons"
                style={{ alignItems: 'center' }}
            >
                {canUploadMedia ? (
                    <div className="horizontal-media-action-picker">
                        <FileUploadPicker
                            label=""
                            customeLabel
                            dense
                            miniActions
                            stackMediaButtons={false}
                            mediaActionIcons
                            maxFiles={5}
                            maxFileSize={500}
                            accept={AcceptFileType.all}
                            files={files}
                            addedNewFiles={onFileUpload}
                            onRemove={onRemoveFile}
                            isUploading={isMediaUploading}
                            noPreview
                            uploadButtonText="Upload"
                            captureButtonText="Capture"
                            showTakePicture
                            disabled={false}
                        />
                    </div>
                ) : (
                    <div className="horizontal-media-action-picker">
                        <FileUploadPicker
                            label=""
                            customeLabel
                            dense
                            miniActions
                            stackMediaButtons={false}
                            mediaActionIcons
                            maxFiles={5}
                            maxFileSize={500}
                            accept={AcceptFileType.all}
                            files={files}
                            addedNewFiles={() => {}}
                            noPreview
                            uploadButtonText="Upload"
                            captureButtonText="Capture"
                            showTakePicture
                            disabled
                        />
                    </div>
                )}
                {hasFiles && (
                    <Button
                        type="button"
                        size="mini"
                        icon
                        className="horizontal-media-view-btn"
                        title="View uploaded media"
                        onClick={() => setIsPreviewOpen(true)}
                    >
                        <Eye size={14} weight="bold" />
                    </Button>
                )}
            </div>
            {isPreviewOpen && <HorizontalMediaModalGlobalStyle />}
            <Modal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                className="horizontal-media-modal"
                size="small"
                style={{ maxWidth: 520 }}
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <span>Uploaded Media</span>
                        <X
                            className="pointer"
                            size={28}
                            onClick={() => setIsPreviewOpen(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content scrolling>
                    {hasFiles ? (
                        <div
                            className="horizontal-media-preview-grid"
                            style={previewGridStyle}
                        >
                            {files.map((file, index) => {
                                const key = String(
                                    file?.id ||
                                        file?.MediaId ||
                                        file?.url ||
                                        file?.SignedURL ||
                                        file?.FileURL ||
                                        index,
                                )
                                return (
                                    <div
                                        className="horizontal-media-preview-item"
                                        key={key}
                                        style={{
                                            ...previewItemStyle,
                                            overflow: 'visible',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 110,
                                                height: 110,
                                                display: 'block',
                                            }}
                                        >
                                            <CustomePreview
                                                item={file}
                                                saveComment={
                                                    canUploadMedia
                                                        ? saveComment
                                                        : () => {}
                                                }
                                                remove={canUploadMedia}
                                                onRemove={
                                                    canUploadMedia
                                                        ? onRemoveFile
                                                        : undefined
                                                }
                                                className="horizontal-modal-preview-thumb"
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div>No media uploaded yet.</div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type="button"
                        onClick={() => setIsPreviewOpen(false)}
                    >
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    )
}

const SpecialPlatenMediaCell = ({
    files: externalFiles,
    isReadOnly,
    onUpload,
    onRemove,
    onSaveComment,
}) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [isMediaUploading, setIsMediaUploading] = useState(false)
    const files = Array.isArray(externalFiles) ? externalFiles : []
    const canUploadMedia = !isReadOnly && typeof onUpload === 'function'
    const hasFiles = files.length > 0

    const onFileUpload = async uploadFiles => {
        if (!canUploadMedia) return
        setIsMediaUploading(true)
        try {
            await onUpload(uploadFiles)
        } catch (error) {
            console.error('Error uploading media:', error)
        } finally {
            setIsMediaUploading(false)
        }
    }

    const onRemoveFile = async file => {
        if (!canUploadMedia || typeof onRemove !== 'function') return
        if (file?.url?.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(file.url)
            } catch (e) {
                // ignore
            }
        }
        await onRemove(file)
    }

    const saveComment =
        typeof onSaveComment === 'function' ? onSaveComment : () => {}

    const previewGridStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 110px)',
        gap: '12px',
        maxHeight: '52vh',
        overflow: 'auto',
        alignItems: 'start',
        justifyContent: 'start',
    }
    const previewItemStyle = {
        position: 'relative',
        width: 110,
        height: 110,
    }

    return (
        <>
            <div
                className="horizontal-media-icons"
                style={{ alignItems: 'center' }}
            >
                {canUploadMedia ? (
                    <FileUploadPicker
                        label=""
                        customeLabel
                        dense
                        miniActions
                        stackMediaButtons={false}
                        mediaActionIcons
                        maxFiles={5}
                        maxFileSize={500}
                        accept="image/*,application/pdf"
                        files={files}
                        addedNewFiles={onFileUpload}
                        onRemove={onRemoveFile}
                        isUploading={isMediaUploading}
                        noPreview
                        uploadButtonText="Upload"
                        captureButtonText="Capture"
                        showTakePicture
                        disabled={false}
                    />
                ) : (
                    <FileUploadPicker
                        label=""
                        customeLabel
                        dense
                        miniActions
                        stackMediaButtons={false}
                        mediaActionIcons
                        maxFiles={5}
                        maxFileSize={500}
                        accept="image/*,application/pdf"
                        files={files}
                        addedNewFiles={() => {}}
                        noPreview
                        uploadButtonText="Upload"
                        captureButtonText="Capture"
                        showTakePicture
                        disabled
                    />
                )}
                {hasFiles && (
                    <Button
                        type="button"
                        size="mini"
                        icon
                        className="horizontal-media-view-btn"
                        title="View uploaded media"
                        onClick={() => setIsPreviewOpen(true)}
                    >
                        <Eye size={14} weight="bold" />
                    </Button>
                )}
            </div>
            {isPreviewOpen && <HorizontalMediaModalGlobalStyle />}
            <Modal
                open={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                className="horizontal-media-modal"
                size="small"
                style={{ maxWidth: 520 }}
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <span>Uploaded Media</span>
                        <X
                            className="pointer"
                            size={28}
                            onClick={() => setIsPreviewOpen(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content scrolling>
                    {hasFiles ? (
                        <div
                            className="horizontal-media-preview-grid"
                            style={previewGridStyle}
                        >
                            {files.map((file, index) => {
                                const key = String(
                                    file?.id ||
                                        file?.MediaId ||
                                        file?.url ||
                                        file?.SignedURL ||
                                        file?.FileURL ||
                                        index,
                                )
                                return (
                                    <div
                                        className="horizontal-media-preview-item"
                                        key={key}
                                        style={{
                                            ...previewItemStyle,
                                            overflow: 'visible',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 110,
                                                height: 110,
                                                display: 'block',
                                            }}
                                        >
                                            <CustomePreview
                                                item={file}
                                                saveComment={
                                                    canUploadMedia
                                                        ? saveComment
                                                        : () => {}
                                                }
                                                remove={canUploadMedia}
                                                onRemove={
                                                    canUploadMedia
                                                        ? onRemoveFile
                                                        : undefined
                                                }
                                                className="horizontal-modal-preview-thumb"
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div>No media uploaded yet.</div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type="button"
                        onClick={() => setIsPreviewOpen(false)}
                    >
                        Close
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    )
}

const LanguageSelectorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 16px;
    width: 100%;

    .language-label {
        font-weight: 500;
        color: ${({ theme }) => theme.colors.black || '#333'};
    }

    .language-dropdown {
        min-width: 150px;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
        width: 100%;

        .language-dropdown {
            min-width: unset;
            width: 100%;
        }
    }
`

const extractShowSectionDropdownFlag = source => {
    if (!source || typeof source !== 'object') {
        return undefined
    }

    const possibleKeys = [
        'ShowSectionAsDropdown',
        'ShowSectionAsDropDown',
        'ShowSectionsAsDropdown',
        'ShowSectionsAsDropDown',
        'ShowSectionDropdown',
        'ShowSectionDropDown',
        'showSectionAsDropdown',
        'showSectionAsDropDown',
        'showSectionDropdown',
        'showSectionDropDown',
    ]

    const matchedKey = possibleKeys.find(key =>
        Object.prototype.hasOwnProperty.call(source, key),
    )

    if (!matchedKey) {
        return undefined
    }

    const value = source[matchedKey]

    if (value === undefined || value === null) {
        return undefined
    }

    if (typeof value === 'string') {
        const lowered = value.toLowerCase()
        if (lowered === 'true') {
            return true
        }
        if (lowered === 'false') {
            return false
        }
    }

    if (typeof value === 'number') {
        if (value === 1) {
            return true
        }
        if (value === 0) {
            return false
        }
    }

    return Boolean(value)
}

const normalizeBooleanFlag = value => {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
        const lowered = value.trim().toLowerCase()
        return lowered === 'true' || lowered === '1'
    }
    return false
}

const isQuestionAnswered = question => {
    if (!question) {
        return false
    }

    const answers = question.Answers || {}

    const hasOtherResponse = () => {
        if (!question.IsOtherAnswerSelected) {
            return false
        }

        if (!Array.isArray(answers.OtherAnswerResponse)) {
            return false
        }

        return answers.OtherAnswerResponse.some(other => {
            if (!other) {
                return false
            }

            const value =
                typeof other.OtherAnswer === 'string'
                    ? other.OtherAnswer.trim()
                    : other.OtherAnswer

            return value !== undefined && value !== null && value !== ''
        })
    }

    const hasDropdownOther = () => {
        if (!Array.isArray(question.dropdownValue)) {
            return false
        }

        if (!question.dropdownValue.length) {
            return false
        }

        const normalIds = (answers.MultipleResponse || []).map(item =>
            String(item.Id),
        )
        const firstValue = question.dropdownValue[0]

        if (firstValue === undefined || firstValue === null) {
            return false
        }

        return !normalIds.includes(String(firstValue))
    }

    const hasParentChildSelection = () =>
        hasParentChildAnswerSelection(
            answers.ParentChildResponse,
            answers,
        )

    if (
        question.TypeOfResponse === TemplateResponseType.Multiple ||
        question.MultiResponseType === TemplateQuestionResponseType.Multiple ||
        question.MultiResponseType === TemplateQuestionResponseType.Single
    ) {
        const selectedCount = (answers.MultipleResponse || []).filter(
            option => option?.IsSelected,
        ).length

        return (
            selectedCount > 0 ||
            hasOtherResponse() ||
            hasDropdownOther()
        )
    }

    if (question.QuestionResponseId === ControlType.Media) {
        return Array.isArray(question.Files) && question.Files.length > 0
    }

    if (hasParentChildSelection()) {
        return true
    }

    if (
        question.TypeOfResponse === TemplateResponseType.Single &&
        answers.SingleResponse
    ) {
        const response = answers.SingleResponse.Response
        return (
            response !== undefined &&
            response !== null &&
            response !== ''
        )
    }

    if (answers.SingleResponse && answers.SingleResponse.Response !== undefined) {
        const response = answers.SingleResponse.Response
        return (
            response !== undefined &&
            response !== null &&
            response !== ''
        )
    }

    return false
}

const isMultipleChoiceQuestion = question =>
    question?.TypeOfResponse === TemplateResponseType.Multiple ||
    question?.MultiResponseType === TemplateQuestionResponseType.Multiple ||
    question?.MultiResponseType === TemplateQuestionResponseType.Single

const getMultipleChoiceSelectionCount = question => {
    const answers = question?.Answers || {}
    let selectedCount = (answers.MultipleResponse || []).filter(
        option => option?.IsSelected,
    ).length

    if (question?.IsOtherAnswerSelected && Array.isArray(answers.OtherAnswerResponse)) {
        const hasOtherText = answers.OtherAnswerResponse.some(other => {
            if (!other) return false
            if (question.Id && other.QuestionId !== question.Id) return false
            const value =
                typeof other.OtherAnswer === 'string'
                    ? other.OtherAnswer.trim()
                    : other.OtherAnswer
            return value !== undefined && value !== null && value !== ''
        })
        if (hasOtherText) {
            selectedCount += 1
        }
    }

    const dropdownValue = question?.dropdownValue || []
    if (Array.isArray(dropdownValue) && dropdownValue.length > 0) {
        const normalIds = (answers.MultipleResponse || []).map(item =>
            String(item.Id),
        )
        if (
            typeof dropdownValue[0] === 'string' &&
            !normalIds.includes(dropdownValue[0])
        ) {
            selectedCount += 1
        }
    }

    return selectedCount
}

const getMinSelectionValidationMessage = (question, minAnswer, maxAnswer) => {
    if (question.RequiredMessage) {
        return question.RequiredMessage
    }
    if (minAnswer <= 0) {
        return 'This question is required.'
    }
    if (minAnswer === maxAnswer) {
        return `Please select exactly ${minAnswer} option(s).`
    }
    return `Minimum ${minAnswer} required.`
}

const getMaxSelectionValidationMessage = (question, minAnswer, maxAnswer) => {
    if (question.RequiredMessage) {
        return question.RequiredMessage
    }
    if (minAnswer === maxAnswer) {
        return `Please select exactly ${minAnswer} option(s).`
    }
    return `Maximum ${maxAnswer} allowed.`
}

/** Required-field validation: IsRequired true must be answered; false is always skipped. */
const getRequiredQuestionValidationError = question => {
    if (!question?.IsRequired) {
        return null
    }

    // Section-level N/A marks questions NotApplicable — skip required checks
    if (question?.NotApplicable) {
        return null
    }

    if (isDisplayOnlyInspectionQuestion(question) || isFormulaQuestion(question)) {
        return null
    }

    const answers = question?.Answers || {}

    if (question.IsOtherAnswerSelected && Array.isArray(answers.OtherAnswerResponse)) {
        const hasOtherSelectedButEmpty = answers.OtherAnswerResponse.some(other => {
            if (!other) return false
            if (question.Id && other.QuestionId !== question.Id) return false
            const value =
                typeof other.OtherAnswer === 'string'
                    ? other.OtherAnswer.trim()
                    : other.OtherAnswer
            return value === undefined || value === null || value === ''
        })
        if (hasOtherSelectedButEmpty) {
            return {
                message: 'Please enter a value for the Other option.',
                type: 'error',
            }
        }
    }

    if (isMultipleChoiceQuestion(question)) {
        const totalSelected = getMultipleChoiceSelectionCount(question)
        const minAnswer = Number(question.MinAnswer) || 0
        const maxAnswer = Number(question.MaxAnswer) || 0
        // IsRequired means at least one option, even when template MinAnswer is 0.
        const effectiveMin = Math.max(minAnswer, 1)

        if (totalSelected < effectiveMin) {
            return {
                message: getMinSelectionValidationMessage(
                    question,
                    minAnswer,
                    maxAnswer,
                ),
                type: 'error',
            }
        }
        if (maxAnswer > 0 && totalSelected > maxAnswer) {
            return {
                message: getMaxSelectionValidationMessage(
                    question,
                    minAnswer,
                    maxAnswer,
                ),
                type: 'error',
            }
        }
        return null
    }

    if (!isQuestionAnswered(question)) {
        return {
            message: question.RequiredMessage || 'This question is required.',
            type: 'error',
        }
    }

    return null
}

const SPECIAL_PLATEN_TEMPLATE_ID = 114638
const RECEIVING_CARPET_TEMPLATE_ID = 171370
const RECEIVING_CARPET_AUDIT_TEMPLATE_GUID =
    'cf5c699c-2663-4fec-9e4a-6e513ba9ba9f'
const FREEZER_SAMPLES_TEMPLATE_ID = 172497
const FREEZER_SAMPLES_AUDIT_TEMPLATE_GUID =
    '56de7859-474d-4d48-ad1a-ef2573ff566a'
const FREEZER_SAMPLES_BAG_INFO_QUESTION_ORIGINAL_ID =
    'b3020dba-c77e-47ca-b679-bef0ef6fdc34'
const RECEIVING_CARPET_DETAILS_ROLL_QUESTION_ORIGINAL_ID =
    'dd7fed67-bde5-4292-846a-464bbbf2fe76'
const DAILY_AUDIT_PLATEN_ROW_COUNT = 20
const SMALL_DAILY_AUDIT_PLATEN_QUESTION_ORIGINAL_IDS = [
    'aa1e72e5-c453-43f9-a95d-6baa7719d1fd',
    '4f75a3bf-c484-498d-b367-654869b4c126',
    '53c83dee-4f77-40e0-aabf-64e7bbd00340',
    'b80b3c88-f600-4016-8641-acfe6d1e4ae1',
    'e1e919ba-0bee-49ad-9a59-b548df90955a',
    'f919320e-35b9-4f18-b509-5901b4b6d48b',
    'dd3f1d86-2fce-48a4-9657-ddeba62744eb',
]
const SMALL_DAILY_PLATEN_FIELD_BY_ORIGINAL_ID = {
    'aa1e72e5-c453-43f9-a95d-6baa7719d1fd': 'platen',
    '4f75a3bf-c484-498d-b367-654869b4c126': 'mold',
    '53c83dee-4f77-40e0-aabf-64e7bbd00340': 'pressure',
    'b80b3c88-f600-4016-8641-acfe6d1e4ae1': 'temp',
    'e1e919ba-0bee-49ad-9a59-b548df90955a': 'result',
    'f919320e-35b9-4f18-b509-5901b4b6d48b': 'comments',
    'dd3f1d86-2fce-48a4-9657-ddeba62744eb': 'media',
}

/** Platen Audit inspection grid (Press + General rows) */
const SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH = {
    rowIndex: '5%',
    platen: '12%',
    pressure: '10.5%',
    temp: '10.5%',
    result: '13%',
    comments: '34%',
    media: '14%',
}

const PLATEN_DECK_FAQ_ALIASES = ['deck #', 'deck#']
const PLATEN_NUMBER_FAQ_ALIASES = ['platen #', 'platen#', 'mold #']
const PLATEN_GRID_MODE = {
    SPECIAL_AUDIT: 'special',
    DAILY_AUDIT: 'daily',
}

const findPlatenQuestionByFaqAliases = (questionMap, aliases) => {
    const map = questionMap || {}
    for (let i = 0; i < aliases.length; i += 1) {
        const key = normalizeFaq(aliases[i])
        if (map[key]) return map[key]
    }
    return null
}

/** Daily audit has both Deck # and Platen #; legacy templates used Platen # only for deck. */
const sectionHasExplicitDeckColumn = questionMap =>
    Boolean(questionMap?.['deck #'] || questionMap?.['deck#'])

const resolvePlatenDeckQuestion = questionMap => {
    const map = questionMap || {}
    const explicit = findPlatenQuestionByFaqAliases(map, PLATEN_DECK_FAQ_ALIASES)
    if (explicit) return explicit
    if (!sectionHasExplicitDeckColumn(map)) {
        return findPlatenQuestionByFaqAliases(map, ['platen #', 'platen#'])
    }
    return null
}

const resolvePlatenNumberQuestion = questionMap => {
    const map = questionMap || {}
    if (sectionHasExplicitDeckColumn(map)) {
        return (
            findPlatenQuestionByFaqAliases(map, PLATEN_NUMBER_FAQ_ALIASES) ||
            null
        )
    }
    return findPlatenQuestionByFaqAliases(map, ['mold #', 'mold#'])
}

const isPlatenDeckFaqKey = (key, questionMap) => {
    if (key === 'deck #' || key === 'deck#') return true
    if (key === 'platen #' || key === 'platen#') {
        return !sectionHasExplicitDeckColumn(questionMap)
    }
    return false
}

const isPlatenNumberFaqKey = (key, questionMap) => {
    if (key === 'mold #' || key === 'mold#') return true
    if (key === 'platen #' || key === 'platen#') {
        return sectionHasExplicitDeckColumn(questionMap)
    }
    return false
}

/** Client request: Deck # rows display highest deck first (20→1, 10→1, etc.). */
const sortPlatenRowsByDeckDescending = rows => {
    const list = Array.isArray(rows) ? rows : []
    return [...list]
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
            const an = Number(String(a.row?.platenLabel ?? '').trim())
            const bn = Number(String(b.row?.platenLabel ?? '').trim())
            if (!Number.isNaN(an) && !Number.isNaN(bn) && an !== bn) {
                return bn - an
            }
            return b.index - a.index
        })
}

/** Legacy small-daily platen table widths (superseded by unified platen grid). */
const SMALL_DAILY_PLATEN_TABLE_COLUMN_WIDTH = {
    rowIndex: '5%',
    platen: '10.5%',
    mold: '12%',
    pressure: '10.5%',
    temp: '10.5%',
    result: '13%',
    comments: '34%',
    media: '20%',
}

/** Stable template id for Press (works if Press lives in General or Press Selection). */
const SPECIAL_PLATEN_PRESS_QUESTION_ORIGINAL_ID =
    '196fd62e-88ad-4df8-8764-e052861548b9'

const normalizeFaq = faq => (faq || '').trim().toLowerCase()

/** Primary `General` plus legacy `General (n)` from older client saves (hydration / rollup). */
const isPlatenGeneralSectionName = name => {
    const n = normalizeFaq(name)
    if (n === 'general') return true
    return /^general\s*\(\d+\)\s*$/.test(n)
}

/** Receiving Carpet repeating block: `Details` and CopySection names like `Details (2)`. */
const isReceivingCarpetDetailsSectionName = name => {
    const n = normalizeFaq(name)
    if (n === 'details') return true
    return /^details\s*\(\d+\)\s*$/.test(n)
}

const isReceivingCarpetDetailsSection = section => {
    if (!section) return false
    if (isReceivingCarpetDetailsSectionName(section.SectionName)) return true
    const rollQuestionId = RECEIVING_CARPET_DETAILS_ROLL_QUESTION_ORIGINAL_ID
    return (section.Questions || []).some(
        question =>
            String(question?.QuestionOriginalId || '').toLowerCase() ===
            rollQuestionId,
    )
}

/** Freezer Samples repeating blocks, including CopySection names like `Batch (2)`. */
const isFreezerSamplesNamedSection = (name, baseName) => {
    const n = normalizeFaq(name)
    const base = normalizeFaq(baseName)
    if (!n || !base) return false
    if (n === base) return true
    const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(`^${escaped}\\s*\\(\\d+\\)\\s*$`).test(n)
}

const sectionHasQuestionOriginalId = (section, questionOriginalId) =>
    (section?.Questions || []).some(
        question =>
            String(question?.QuestionOriginalId || '').toLowerCase() ===
            questionOriginalId,
    )

const isFreezerSamplesBagInfoSection = section =>
    isFreezerSamplesNamedSection(section?.SectionName, 'Bag Info') ||
    sectionHasQuestionOriginalId(
        section,
        FREEZER_SAMPLES_BAG_INFO_QUESTION_ORIGINAL_ID,
    )

/** Server CopySection renames copies to "Platen (2)", same pattern as General. */
const isDailyAuditPlatenSectionName = name => {
    const n = normalizeFaq(name)
    if (n === 'platen') return true
    return /^platen\s*\(\d+\)\s*$/.test(n)
}

const platenDailySectionSortRank = section => {
    const n = normalizeFaq(section?.SectionName)
    if (n === 'platen') return 0
    const m = n.match(/^platen\s*\((\d+)\)\s*$/)
    return m ? parseInt(m[1], 10) : 999
}

const normalizeTemplateGuid = value =>
    String(value || '')
        .trim()
        .toLowerCase()

const DAILY_AUDIT_PLATEN_SIGNATURE_IDS = [
    'aa1e72e5-c453-43f9-a95d-6baa7719d1fd',
    '4f75a3bf-c484-498d-b367-654869b4c126',
    '53c83dee-4f77-40e0-aabf-64e7bbd00340',
    'e1e919ba-0bee-49ad-9a59-b548df90955a',
    'f919320e-35b9-4f18-b509-5901b4b6d48b',
    'dd3f1d86-2fce-48a4-9657-ddeba62744eb',
]

const hasDailyAuditPlatenSectionSignature = sections => {
    if (!Array.isArray(sections) || sections.length === 0) return false

    const expectedIds = new Set(
        DAILY_AUDIT_PLATEN_SIGNATURE_IDS.map(normalizeTemplateGuid),
    )

    return sections.some(section => {
        const sectionQuestionIds = new Set(
            (section?.Questions || []).map(q =>
                normalizeTemplateGuid(q?.QuestionOriginalId),
            ),
        )

        return [...expectedIds].every(id => sectionQuestionIds.has(id))
    })
}

const hasSmallDailyPlatenSectionSignature = hasDailyAuditPlatenSectionSignature

/**
 * All Platen sections (original + CopySection renames like "Platen (2)"),
 * same pattern as listGeneralSectionsFromAudit for Special Platen.
 */
const listDailyAuditPlatenSectionsFromAudit = sections => {
    const list = (sections || []).filter(s =>
        isDailyAuditPlatenSectionName(s?.SectionName),
    )
    return list.sort((a, b) => {
        const ra = platenDailySectionSortRank(a)
        const rb = platenDailySectionSortRank(b)
        if (ra !== rb) return ra - rb
        const ac = a?.IsCopiedSection ? 1 : 0
        const bc = b?.IsCopiedSection ? 1 : 0
        if (ac !== bc) return ac - bc
        return (Number(a?.Order) || 0) - (Number(b?.Order) || 0)
    })
}

/** Primary Platen section on the audit (never duplicate from a copy row). */
const resolvePrimaryDailyAuditPlatenSectionFromAudit = sections => {
    const list = Array.isArray(sections) ? sections : []
    return (
        list.find(
            s =>
                normalizeFaq(s?.SectionName) === 'platen' &&
                !s?.IsCopiedSection,
        ) ||
        list.find(s => normalizeFaq(s?.SectionName) === 'platen') ||
        list.find(s => isDailyAuditPlatenSectionName(s?.SectionName)) ||
        null
    )
}

const isPlatenGridSectionEntry = (section, gridMode) => {
    if (gridMode === PLATEN_GRID_MODE.SPECIAL_AUDIT) {
        return isPlatenGeneralSectionName(section?.SectionName)
    }
    if (gridMode === PLATEN_GRID_MODE.DAILY_AUDIT) {
        return isDailyAuditPlatenSectionName(section?.SectionName)
    }
    return false
}

const sectionHasDedicatedPlatenMediaQuestion = section => {
    const map = buildQuestionMapByFaq(section?.Questions || [])
    return Boolean(map.media)
}

const platenGeneralSectionSortRank = section => {
    const n = normalizeFaq(section?.SectionName)
    if (n === 'general') return 0
    const m = n.match(/^general\s*\((\d+)\)\s*$/)
    return m ? parseInt(m[1], 10) : 999
}

const findSpecialPlatenPressQuestion = sections => {
    const list = Array.isArray(sections) ? sections : []
    const target = String(SPECIAL_PLATEN_PRESS_QUESTION_ORIGINAL_ID || '')
        .trim()
        .toLowerCase()
    if (target) {
        for (let s = 0; s < list.length; s += 1) {
            const qs = list[s]?.Questions || []
            for (let i = 0; i < qs.length; i += 1) {
                const q = qs[i]
                const oid = String(q?.QuestionOriginalId || '')
                    .trim()
                    .toLowerCase()
                if (oid && oid === target) {
                    return q
                }
            }
        }
    }
    const legacySection = list.find(
        sec => normalizeFaq(sec?.SectionName) === 'press selection',
    )
    if (legacySection) {
        const hit = (legacySection.Questions || []).find(
            q => normalizeFaq(q?.FAQ) === 'press',
        )
        if (hit) return hit
    }
    return null
}

/**
 * Map live section → translated snapshot by stable keys. Pure index merge breaks
 * after a section is removed/reordered (e.g. delete Press Selection): General would
 * pair with the wrong translated block and MultipleResponse labels/options can disappear.
 */
const resolveTranslatedSectionForMerge = (section, translatedSections) => {
    const list = Array.isArray(translatedSections) ? translatedSections : []
    if (!section) return null

    const sid = section?.SectionId
    if (sid != null && sid !== '') {
        const byId = list.find(
            ts =>
                ts?.SectionId != null &&
                String(ts.SectionId) === String(sid),
        )
        if (byId) return byId
    }

    const name = normalizeFaq(section?.SectionName)
    if (name) {
        const byName = list.find(
            ts => normalizeFaq(ts?.SectionName) === name,
        )
        if (byName) return byName
    }

    const ord = Number(section?.Order ?? section?.SectionOrder ?? NaN)
    if (!Number.isNaN(ord)) {
        const byOrder = list.find(
            ts =>
                Number(ts?.Order ?? ts?.SectionOrder ?? NaN) === ord,
        )
        if (byOrder) return byOrder
    }

    /* No index fallback: new / unmatched sections stay on live data only. */
    return null
}

const resolveTranslatedQuestionForMerge = (
    question,
    translatedQuestions,
) => {
    const list = Array.isArray(translatedQuestions)
        ? translatedQuestions
        : []
    if (!question) return null

    const oid = String(question?.QuestionOriginalId || '')
        .trim()
        .toLowerCase()
    if (oid) {
        const byOid = list.find(
            tq =>
                String(tq?.QuestionOriginalId || '')
                    .trim()
                    .toLowerCase() === oid,
        )
        if (byOid) return byOid
    }

    if (question?.Id != null && question?.Id !== '') {
        const byQid = list.find(tq => String(tq?.Id) === String(question.Id))
        if (byQid) return byQid
    }

    return null
}

/**
 * Hydrate Answers.MultipleResponse from TemplateResponseType when the audit
 * payload omits empty option arrays (Inspection preview Screen does this; list must too).
 */
const hydrateInspectionParentChildResponses = sections => {
    if (!Array.isArray(sections)) return sections || []
    return sections.map(section => ({
        ...section,
        Questions: Array.isArray(section?.Questions)
            ? section.Questions.map(question => {
                const ans = { ...(question.Answers || {}) }
                const pcr = ans.ParentChildResponse
                if (!Array.isArray(pcr) || !pcr.length) {
                    return question
                }
                const nextQuestion = {
                    ...question,
                    Answers: {
                        ...ans,
                        ParentChildResponse:
                            applyParentChildResponseScores(
                                normalizeParentChildResponseAnswers(pcr),
                            ),
                    },
                }
                const answered = isQuestionAnswered(nextQuestion)
                nextQuestion.IsAnswered = answered
                nextQuestion.Answers.IsAnswered = answered
                return nextQuestion
            })
            : section.Questions,
    }))
}

const hydrateInspectionMultipleResponsesFromTemplate = (sections, auditData) => {
    if (!Array.isArray(sections) || !auditData) return sections || []
    const standardOpts =
        auditData?.TemplateResponseType?.MultipleResponse || []
    const userOpts =
        auditData?.TemplateResponseType?.UserMultipleResponse || []

    return sections.map(section => ({
        ...section,
        Questions: Array.isArray(section?.Questions)
            ? section.Questions.map(question => {
                const ans = { ...(question.Answers || {}) }
                const existing = Array.isArray(ans.MultipleResponse)
                    ? ans.MultipleResponse
                    : []
                if (existing.length > 0) {
                    return { ...question, Answers: ans }
                }

                const optionMatcher = opt => {
                    const qResp = question.QuestionResponseId
                    const qId = question.Id
                    const eq = (a, b) =>
                        a != null && b != null && String(a) === String(b)

                    if (eq(opt.GroupId, qResp) || eq(opt.Id, qResp)) {
                        return true
                    }
                    if (
                        qResp === 0 ||
                        qResp === '0' ||
                        qResp == null
                    ) {
                        return eq(opt.GroupId, qId) || eq(opt.Id, qId)
                    }
                    return false
                }

                const matchedUser = userOpts.filter(optionMatcher)
                const matchedStandard = standardOpts.filter(optionMatcher)
                const base =
                    matchedUser.length > 0 ? matchedUser : matchedStandard

                if (!base.length) {
                    return question
                }

                ans.MultipleResponse = base.map(opt => ({
                    ...opt,
                    IsSelected: !!opt?.IsSelected,
                }))

                return { ...question, Answers: ans }
            })
            : section.Questions,
    }))
}

/**
 * Whether `section` is the same General block as `generalRef` from state.
 * Section Id may be 0 — never use truthy checks on Id.
 */
const isSameSpecialGeneralSection = (section, generalRef) => {
    if (!section || !generalRef) return false
    const a = section.Id
    const b = generalRef.Id
    if (a !== undefined && a !== null && b !== undefined && b !== null) {
        if (String(a) === String(b)) return true
    }
    const sidA = section.SectionId
    const sidB = generalRef.SectionId
    if (sidA != null && sidB != null && String(sidA) === String(sidB)) {
        return true
    }
    return (
        normalizeFaq(section.SectionName) === 'general' &&
        normalizeFaq(generalRef.SectionName) === 'general'
    )
}

const classifySpecialPlatenResult = (selectedText, resultQuestion) => {
    const trimmed = String(selectedText || '').trim().toLowerCase()
    if (!trimmed) {
        return 'none'
    }

    const opts = resultQuestion?.Answers?.MultipleResponse || []
    const matchedOpt = opts.find(
        o =>
            String(o.DisplayText || '').trim().toLowerCase()
            === trimmed,
    )

    const textPool = matchedOpt
        ? String(matchedOpt.DisplayText || '').trim().toLowerCase()
        : trimmed

    const slug = trimmed.replace(/\s+/g, ' ')
    const textSlug = textPool.replace(/\s+/g, ' ')

    const isNonConfirm = /non\s*[-]?\s*(conforming|confirm)/i.test(
        textSlug,
    ) || /non\s+confirm/i.test(trimmed.replace(/\s+/g, ''))

    if (isNonConfirm) return 'nonconforming'
    if (/\bby\s*pass\b|bypass/i.test(slug) || /\bby\s*pass\b|bypass/i.test(textSlug)) {
        return 'bypass'
    }
    if (textSlug.includes('conforming')) return 'conforming'

    return 'unknown'
}

const WHITE_FORE_ONLY =
    /^#?(fff|ffffff)$/i

const getSpecialResultAccent = (selectedTextRaw, resultQuestion) => {
    const raw = String(selectedTextRaw || '').trim()
    if (!raw) {
        return null
    }

    const trimmedLower = raw.toLowerCase()
    const opts = resultQuestion?.Answers?.MultipleResponse || []

    const matched = opts.find(
        o =>
            String(o.DisplayText || '').trim().toLowerCase()
            === trimmedLower,
    )

    let fg =
        matched?.ForeColorCode && typeof matched.ForeColorCode === 'string'
            ? matched.ForeColorCode.trim()
            : ''

    if (fg && WHITE_FORE_ONLY.test(fg)) {
        fg = ''
    }

    const cat = classifySpecialPlatenResult(raw, resultQuestion)

    const DEFAULT_FG = {
        conforming: '#2e7d32',
        nonconforming: '#d32f2f',
        bypass: '#e65100',
    }

    if (cat === 'conforming') return fg || DEFAULT_FG.conforming
    if (cat === 'nonconforming') return fg || DEFAULT_FG.nonconforming
    if (cat === 'bypass') return fg || DEFAULT_FG.bypass

    return fg || '#546e7a'
}

const SPECIAL_RESULT_PALETTE_FILL = {
    conforming: '#e8f5e9',
    nonconforming: '#ffebee',
    bypass: '#fff8e1',
}

const getSpecialResultTriggerStyle = (selectedText, resultQuestion) => {
    const trimmedRaw = String(selectedText || '').trim()
    const trimmedLower = trimmedRaw.toLowerCase()

    /* Before selection: black label + light-blue frame (matches reference mocks) */
    if (!trimmedLower) {
        return {
            border: '1px solid #bcd4ea',
            backgroundColor: '#ffffff',
            color: '#111111',
            fontWeight: 600,
            borderRadius: 8,
            boxSizing: 'border-box',
        }
    }

    const cat = classifySpecialPlatenResult(trimmedRaw, resultQuestion)
    const accent = getSpecialResultAccent(trimmedRaw, resultQuestion)
    const tonalRow =
        cat === 'conforming' || cat === 'nonconforming' || cat === 'bypass'
            ? SPECIAL_RESULT_PALETTE_FILL[cat]
            : '#ffffff'

    const fill = tonalRow

    /* After selection: tinted fill inside the trigger + matched border/text (reference image 1 top 3 rows) */
    return {
        border: `2px solid ${accent}`,
        backgroundColor: fill,
        color: accent,
        fontWeight: 600,
        borderRadius: 8,
        boxSizing: 'border-box',
    }
}

const getSelectedMultipleText = question => {
    const options = question?.Answers?.MultipleResponse || []
    const selected = options.find(opt => opt?.IsSelected)
    return selected?.DisplayText ? String(selected.DisplayText).trim() : ''
}

const buildPlatenResultDropdownOptionsFromQuestion = question => {
    const options = question?.Answers?.MultipleResponse || []
    const blank = [
        {
            key: '__platen_result_blank',
            value: '',
            text: '-- Select --',
            content: (
                <span style={{ fontWeight: 600, color: '#111111' }}>
                    -- Select --
                </span>
            ),
        },
    ]
    const items = options.map(opt => {
        const label = String(opt.DisplayText || '').trim()
        return {
            key: opt.Id ?? label,
            text: label,
            value: label,
            content: (
                <span
                    style={{
                        padding: '6px 0',
                        fontWeight: 600,
                        color: '#111111',
                        backgroundColor: 'transparent',
                    }}
                >
                    {label}
                </span>
            ),
        }
    })
    return [...blank, ...items]
}

/** Map API result text to canonical option DisplayText so dropdown value matches options. */
const normalizePlatenResultDisplayFromQuestion = (rawText, resultQuestion) => {
    const trimmed = String(rawText || '').trim()
    if (!trimmed) return ''
    const opts = resultQuestion?.Answers?.MultipleResponse || []
    const lower = trimmed.toLowerCase()
    const match = opts.find(
        o => String(o.DisplayText || '').trim().toLowerCase() === lower,
    )
    return match ? String(match.DisplayText || '').trim() : trimmed
}

const setMultipleSelectionByText = (question, selectedText) => {
    const selectedNormalized = String(selectedText || '').trim().toLowerCase()
    const options = (question?.Answers?.MultipleResponse || []).map(opt => ({
        ...opt,
        IsSelected:
            !!selectedNormalized &&
            String(opt?.DisplayText || '')
                .trim()
                .toLowerCase() === selectedNormalized,
    }))
    return {
        ...question,
        Answers: {
            ...(question?.Answers || {}),
            MultipleResponse: options,
        },
        IsAnswered: options.some(opt => !!opt.IsSelected),
    }
}

const setSingleResponseText = (question, textValue) => {
    const value = String(textValue || '')
    return {
        ...question,
        Answers: {
            ...(question?.Answers || {}),
            SingleResponse: {
                ...(question?.Answers?.SingleResponse || {}),
                Response: value,
                IsSelected: value.trim() !== '',
            },
        },
        IsAnswered: value.trim() !== '',
    }
}

const getDeckLabelFromQuestion = question => {
    const fromMulti = getSelectedMultipleText(question)
    if (fromMulti) return fromMulti
    const fromSingle = question?.Answers?.SingleResponse?.Response
    return fromSingle != null && fromSingle !== undefined
        ? String(fromSingle).trim()
        : ''
}

const setDeckAnswerOnQuestion = (question, deckLabel) => {
    const options = question?.Answers?.MultipleResponse || []
    if (options.length > 0) {
        return setMultipleSelectionByText(question, deckLabel)
    }
    return setSingleResponseText(question, deckLabel)
}

const applyPlatenMediaToQuestion = (question, mediaFiles) => {
    const files = Array.isArray(mediaFiles) ? [...mediaFiles] : []
    return syncQuestionMediaFields({
        ...question,
        Files: files,
        Answers: {
            ...(question.Answers || {}),
            MediaResponse: files,
        },
        IsAnswered: files.length > 0,
    })
}

/** All General sections (original first, then copies) for resume / merge. */
const listGeneralSectionsFromAudit = sections => {
    const list = (sections || []).filter(s =>
        isPlatenGeneralSectionName(s?.SectionName),
    )
    return list.sort((a, b) => {
        const ra = platenGeneralSectionSortRank(a)
        const rb = platenGeneralSectionSortRank(b)
        if (ra !== rb) return ra - rb
        const ac = a?.IsCopiedSection ? 1 : 0
        const bc = b?.IsCopiedSection ? 1 : 0
        if (ac !== bc) return ac - bc
        return (Number(a?.Order) || 0) - (Number(b?.Order) || 0)
    })
}

/**
 * Merge each General section (original + copy-section rows) into platen rows by Deck # answer.
 * Falls back to section order in `generalSections` when Deck # is not yet persisted (avoids dropping a row).
 */
const buildPlatenRowsFromAuditSections = (generalSections, rowLabels) => {
    const rows = (rowLabels || []).map(label => ({
        platenLabel: String(label || '').trim(),
        mold: '',
        pressure: '',
        temp: '',
        result: '',
        comments: '',
        mediaFiles: [],
    }))

    if (!Array.isArray(generalSections) || !generalSections.length) {
        return rows
    }

    generalSections.forEach(section => {
        const m = buildQuestionMapByFaq(section?.Questions)
        const deckQ = resolvePlatenDeckQuestion(m)
        const deckText = deckQ ? getDeckLabelFromQuestion(deckQ) : ''
        const idx = rows.findIndex(
            r =>
                String(r.platenLabel || '').trim().toLowerCase()
                === String(deckText || '').trim().toLowerCase(),
        )
        if (idx < 0) return

        const platenNumberQ = resolvePlatenNumberQuestion(m)
        if (platenNumberQ) {
            const v = platenNumberQ.Answers?.SingleResponse?.Response
            rows[idx].mold = v != null && v !== undefined ? String(v) : ''
        }
        if (m.pressure) {
            const v = m.pressure.Answers?.SingleResponse?.Response
            rows[idx].pressure =
                v != null && v !== undefined ? String(v) : ''
        }
        if (m.temp) {
            const v = m.temp.Answers?.SingleResponse?.Response
            rows[idx].temp = v != null && v !== undefined ? String(v) : ''
        }
        if (m.result) {
            rows[idx].result = normalizePlatenResultDisplayFromQuestion(
                getSelectedMultipleText(m.result),
                m.result,
            )
        }
        if (m.comments) {
            const c = m.comments
            const v = c.Answers?.SingleResponse?.Response
            rows[idx].comments =
                v != null && v !== undefined ? String(v) : ''

            let files = []
            if (m.media) {
                const mediaQ = m.media
                if (Array.isArray(mediaQ.Files) && mediaQ.Files.length > 0) {
                    files = [...mediaQ.Files]
                } else if (Array.isArray(mediaQ.Answers?.MediaResponse)) {
                    files = [...mediaQ.Answers.MediaResponse]
                }
            }
            if (!files.length) {
                files =
                    Array.isArray(c.Files) && c.Files.length > 0
                        ? [...c.Files]
                        : []
                if (!files.length && Array.isArray(c.Answers?.MediaResponse)) {
                    files = [...c.Answers.MediaResponse]
                }
            }
            rows[idx].mediaFiles = files
        } else if (m.media) {
            const mediaQ = m.media
            let files =
                Array.isArray(mediaQ.Files) && mediaQ.Files.length > 0
                    ? [...mediaQ.Files]
                    : []
            if (!files.length && Array.isArray(mediaQ.Answers?.MediaResponse)) {
                files = [...mediaQ.Answers.MediaResponse]
            }
            rows[idx].mediaFiles = files
        }
    })

    return rows
}

/** After GET / save, server payload can lag; keep local non-empty fields so the grid does not flash blank. */
const mergePlatenServerRowsWithLocalDraft = (serverRows, localRows) => {
    if (!Array.isArray(serverRows) || !serverRows.length) {
        return Array.isArray(localRows) ? localRows : serverRows
    }
    if (!Array.isArray(localRows) || !localRows.length) {
        return serverRows
    }
    if (serverRows.length !== localRows.length) {
        return serverRows
    }

    const strPick = (serverVal, localVal) => {
        const fromServer = String(serverVal == null ? '' : serverVal).trim()
        if (fromServer !== '') {
            return String(serverVal)
        }
        return String(localVal == null ? '' : localVal)
    }

    return serverRows.map((sr, i) => {
        const lr = localRows[i]
        if (!lr) return sr
        const localMedia = Array.isArray(lr.mediaFiles) ? lr.mediaFiles : []
        const serverMedia = Array.isArray(sr.mediaFiles) ? sr.mediaFiles : []
        const files =
            localMedia.length > 0 || serverMedia.length === 0
                ? [...localMedia]
                : [...serverMedia]
        return {
            ...sr,
            mold: strPick(sr.mold, lr.mold),
            pressure: strPick(sr.pressure, lr.pressure),
            temp: strPick(sr.temp, lr.temp),
            result: strPick(sr.result, lr.result),
            comments: strPick(sr.comments, lr.comments),
            mediaFiles: files,
        }
    })
}

/** Match `filterQuestionMedia` — used for platen grid row files and inspection questions. */
const shouldKeepMediaFileEntry = (entry, fileToRemove) => {
    const mediaId = fileToRemove?.MediaId ?? fileToRemove?.id
    const fileKey = fileToRemove?.FileKey
    const fileUrl =
        fileToRemove?.url || fileToRemove?.FileURL || fileToRemove?.SignedURL

    if (mediaId != null && mediaId !== '') {
        const entryId = entry?.MediaId ?? entry?.id
        if (entryId != null && String(entryId) === String(mediaId)) {
            return false
        }
    }
    if (
        fileToRemove?.id != null &&
        entry?.id != null &&
        String(entry.id) === String(fileToRemove.id)
    ) {
        return false
    }
    if (fileKey && entry?.FileKey === fileKey) {
        return false
    }
    if (fileToRemove?.local && entry?.local) {
        const entryUrl = entry?.url
        const removeUrl = fileToRemove?.url
        if (entryUrl && removeUrl && entryUrl === removeUrl) {
            return false
        }
    }
    if (fileUrl) {
        const entryUrl = entry?.FileURL || entry?.SignedURL || entry?.url
        if (entryUrl && entryUrl === fileUrl) {
            return false
        }
    }
    return true
}

const filterMediaFilesList = (files, fileToRemove) =>
    (Array.isArray(files) ? files : []).filter(entry =>
        shouldKeepMediaFileEntry(entry, fileToRemove),
    )

const syncSpecialPlatenRowCommentsMediaInAudit = (
    auditData,
    rowIndex,
    mediaFiles,
    gridMode = PLATEN_GRID_MODE.SPECIAL_AUDIT,
    row = null,
) => {
    if (!auditData?.Sections?.length) return auditData

    const rowSection =
        findPlatenGridSectionForRow(
            auditData.Sections,
            gridMode,
            row,
            rowIndex,
        ).section ||
        listPlatenGridSectionsFromAudit(auditData.Sections, gridMode)[rowIndex]
    if (!rowSection) return auditData

    const mediaQ = resolvePlatenRowMediaQuestion(rowSection)
    if (!mediaQ?.Id) return auditData

    const questionId = mediaQ.Id
    const sectionId = rowSection.SectionId ?? rowSection.Id
    const files = Array.isArray(mediaFiles) ? mediaFiles : []

    return {
        ...auditData,
        Sections: auditData.Sections.map(section => {
            const inSection =
                String(section.SectionId ?? '') === String(sectionId ?? '') ||
                String(section.Id ?? '') === String(sectionId ?? '')
            if (!inSection) return section

            return {
                ...section,
                Questions: (section.Questions || []).map(q => {
                    if (String(q.Id) !== String(questionId)) return q
                    return syncQuestionMediaFields({
                        ...q,
                        Files: files,
                        Answers: {
                            ...(q.Answers || {}),
                            MediaResponse: files,
                        },
                    })
                }),
            }
        }),
    }
}

/**
 * After CopySection + GET, server `Sections` can miss unsaved Press / other non-General
 * edits. Keep local non-General sections; replace only General-like blocks from the server.
 */
const mergeAuditSectionsAfterPlatenEnsure = (
    localSections,
    serverSections,
    gridMode = PLATEN_GRID_MODE.SPECIAL_AUDIT,
) => {
    const loc = Array.isArray(localSections) ? localSections : []
    const srv = Array.isArray(serverSections) ? serverSections : []
    const serverGridSections = listPlatenGridSectionsFromAudit(srv, gridMode)
    const out = []
    let injectedServerGridSections = false
    loc.forEach(s => {
        if (!isPlatenGridSectionEntry(s, gridMode)) {
            out.push(s)
            return
        }
        if (!injectedServerGridSections) {
            serverGridSections.forEach(g => out.push(g))
            injectedServerGridSections = true
        }
    })
    if (!injectedServerGridSections && serverGridSections.length > 0) {
        serverGridSections.forEach(g => out.push(g))
    }
    return out
}

/** FAQ order for synthetic General sections (matches table / template layout). */
const PLATEN_GENERAL_FAQ_ORDER = [
    { field: 'deck' },
    { field: 'platenNumber' },
    { field: 'pressure' },
    { field: 'temp' },
    { field: 'result' },
    { field: 'comments' },
]

const getFilledSpecialPlatenRows = rows => {
    const list = Array.isArray(rows) ? rows : []
    return list.filter(row => {
        const mediaCount = Array.isArray(row?.mediaFiles)
            ? row.mediaFiles.length
            : 0
        return Boolean(
            row?.mold?.trim() ||
                row?.pressure?.trim() ||
                row?.temp?.trim() ||
                row?.result?.trim() ||
                row?.comments?.trim() ||
                mediaCount > 0,
        )
    })
}

/** Primary General section on the audit (never duplicate from a copy row). */
const resolvePrimaryPlatenGeneralSectionFromAudit = sections => {
    const list = Array.isArray(sections) ? sections : []
    return (
        list.find(
            s =>
                normalizeFaq(s?.SectionName) === 'general' &&
                !s?.IsCopiedSection,
        ) ||
        list.find(s => normalizeFaq(s?.SectionName) === 'general') ||
        null
    )
}

const listPlatenGridSectionsFromAudit = (sections, gridMode) => {
    if (gridMode === PLATEN_GRID_MODE.SPECIAL_AUDIT) {
        return listGeneralSectionsFromAudit(sections)
    }
    if (gridMode === PLATEN_GRID_MODE.DAILY_AUDIT) {
        return listDailyAuditPlatenSectionsFromAudit(sections)
    }
    return []
}

const resolvePrimaryPlatenGridSectionFromAudit = (sections, gridMode) => {
    if (gridMode === PLATEN_GRID_MODE.SPECIAL_AUDIT) {
        return resolvePrimaryPlatenGeneralSectionFromAudit(sections)
    }
    if (gridMode === PLATEN_GRID_MODE.DAILY_AUDIT) {
        return resolvePrimaryDailyAuditPlatenSectionFromAudit(sections)
    }
    return null
}

/** Match a platen grid row to its server section by Deck # answer (never by raw row index). */
const findPlatenGridSectionForRow = (
    sections,
    gridMode,
    row,
    rowIndex = -1,
) => {
    const gridSections = listPlatenGridSectionsFromAudit(sections, gridMode)
    const label = String(row?.platenLabel ?? '').trim().toLowerCase()

    if (label) {
        for (let i = 0; i < gridSections.length; i += 1) {
            const section = gridSections[i]
            const m = buildQuestionMapByFaq(section?.Questions)
            const deckQ = resolvePlatenDeckQuestion(m)
            const deckText = deckQ ? getDeckLabelFromQuestion(deckQ) : ''
            if (String(deckText || '').trim().toLowerCase() === label) {
                return { section, gridIndex: i }
            }
        }
    }

    if (
        rowIndex != null &&
        rowIndex >= 0 &&
        rowIndex < gridSections.length &&
        gridSections.length === 1
    ) {
        return { section: gridSections[0], gridIndex: 0 }
    }

    return { section: null, gridIndex: -1 }
}

const resolvePlatenRowMediaQuestion = section => {
    const rowQuestionMap = buildQuestionMapByFaq(section?.Questions || [])
    return rowQuestionMap.media || rowQuestionMap.comments || null
}

/**
 * Match server `copySection` semantics: real question Ids, FAQId = question Id on
 * copies, distinct MultipleResponse Ids. Call CopySection until enough General
 * blocks exist, then merge row answers into those sections (no client-only Id:0 rows).
 */
const mergePlatenRowIntoServerGeneralSection = (section, row) => {
    const out = JSON.parse(JSON.stringify(section))
    if (!Array.isArray(out.Questions)) {
        return out
    }
    const useDedicatedMedia = sectionHasDedicatedPlatenMediaQuestion(out)
    const faqMap = buildQuestionMapByFaq(out.Questions)
    out.Questions = out.Questions.map(q => {
        const key = normalizeFaq(q?.FAQ)
        if (isPlatenDeckFaqKey(key, faqMap)) {
            return setDeckAnswerOnQuestion(q, row.platenLabel)
        }
        if (isPlatenNumberFaqKey(key, faqMap)) {
            return setSingleResponseText(q, row.mold)
        }
        if (key === 'pressure') {
            return setSingleResponseText(q, row.pressure)
        }
        if (key === 'temp') {
            return setSingleResponseText(q, row.temp)
        }
        if (key === 'result') {
            return setMultipleSelectionByText(
                q,
                normalizePlatenResultDisplayFromQuestion(row.result, q),
            )
        }
        if (key === 'media') {
            return applyPlatenMediaToQuestion(q, row.mediaFiles)
        }
        if (key === 'comments') {
            let commentFiles = []
            if (!useDedicatedMedia) {
                commentFiles = Array.isArray(row.mediaFiles)
                    ? [...row.mediaFiles]
                    : []
            }
            const textQ = setSingleResponseText(q, row.comments)
            return {
                ...textQ,
                Files: commentFiles,
                Answers: {
                    ...(textQ.Answers || {}),
                    MediaResponse: commentFiles,
                },
                IsAnswered:
                    Boolean(String(row.comments || '').trim()) ||
                    commentFiles.length > 0,
            }
        }
        return q
    })
    return out
}

async function ensurePlatenGridServerSectionsLoaded({
    audit,
    platenRows,
    routeAuditId,
    auditNumericId,
    forcedMinGeneralCount,
    gridMode = PLATEN_GRID_MODE.SPECIAL_AUDIT,
}) {
    const filledRows = getFilledSpecialPlatenRows(platenRows)
    const filledN = filledRows.length
    const N = Math.max(
        filledN,
        typeof forcedMinGeneralCount === 'number'
            ? forcedMinGeneralCount
            : 0,
    )
    if (
        !audit?.Sections?.length ||
        !auditNumericId ||
        !routeAuditId ||
        N <= 1
    ) {
        return audit
    }

    const countGridSections = a =>
        listPlatenGridSectionsFromAudit(a?.Sections || [], gridMode).length

    let current = audit
    /* Platen: each copySection must complete before the next (server assigns SectionIds). */
    /* eslint-disable no-await-in-loop */
    while (countGridSections(current) < N) {
        const beforeCount = countGridSections(current)
        const sections = current.Sections || []
        const primary =
            resolvePrimaryPlatenGridSectionFromAudit(sections, gridMode) ||
            listPlatenGridSectionsFromAudit(sections, gridMode)[0]
        if (!primary?.SectionId) break

        const defaultSectionName =
            gridMode === PLATEN_GRID_MODE.DAILY_AUDIT ? 'Platen' : 'General'

        await InspectionService.copySection({
            AuditUniqueId: auditNumericId,
            SectionId: primary.SectionId,
            CopyAuditId: 0,
            SectionName: String(primary.SectionName || defaultSectionName).trim(),
        })
        let fresh = null
        try {
            fresh = await InspectionService.getAudit(String(routeAuditId))
        } catch (e) {
            console.error(
                '[Platen] getAudit after copySection failed:',
                e,
            )
            break
        }
        current = fresh || current
        if (countGridSections(current) <= beforeCount) {
            console.error(
                '[Platen] copySection did not increase grid section count',
                {
                    beforeCount,
                    afterCount: countGridSections(current),
                    needed: N,
                },
            )
            throw new Error(
                'Platen copySection did not create a recognizable grid section',
            )
        }
    }
    /* eslint-enable no-await-in-loop */
    return current
}

const SPECIAL_PLATEN_ERR_PRESS = '__platen_press__'
const specialPlatenRowFieldErrKey = (rowIndex, field) =>
    `__platen_row_${rowIndex}_${field}__`

const isSpecialPlatenRowTouched = row => {
    if (!row || typeof row !== 'object') return false
    const mediaCount = Array.isArray(row.mediaFiles)
        ? row.mediaFiles.length
        : 0
    return Boolean(
        row.mold?.trim() ||
            row.pressure?.trim() ||
            row.temp?.trim() ||
            row.result?.trim() ||
            row.comments?.trim() ||
            mediaCount > 0,
    )
}

/** Completion rules: Press required; any row with any field entered needs Platen #, Result. */
const buildSpecialPlatenCompletionErrors = (rows, pressQuestion, auditRecord) => {
    const errors = {}
    if (!auditRecord?.Sections?.length) {
        return errors
    }

    let pressText = ''
    const targetOid = String(SPECIAL_PLATEN_PRESS_QUESTION_ORIGINAL_ID || '')
        .trim()
        .toLowerCase()
    let foundPressQ = null
    if (pressQuestion?.Id != null) {
        auditRecord.Sections.forEach(section => {
            if (foundPressQ) return
            const q = (section.Questions || []).find(
                qs => String(qs?.Id) === String(pressQuestion.Id),
            )
            if (q) foundPressQ = q
        })
    }
    if (!foundPressQ && targetOid) {
        auditRecord.Sections.forEach(section => {
            if (foundPressQ) return
            const q = (section.Questions || []).find(
                qs =>
                    String(qs?.QuestionOriginalId || '')
                        .trim()
                        .toLowerCase() === targetOid,
            )
            if (q) foundPressQ = q
        })
    }
    if (foundPressQ) {
        pressText = getSelectedMultipleText(foundPressQ)
    }

    if (!String(pressText || '').trim()) {
        errors[SPECIAL_PLATEN_ERR_PRESS] = {
            message: 'Please select a press.',
            type: 'error',
        }
    }

    const rowList = Array.isArray(rows) ? rows : []
    rowList.forEach((row, idx) => {
        if (!isSpecialPlatenRowTouched(row)) return
        const label =
            String(row.platenLabel ?? idx + 1).trim() || String(idx + 1)
        const prefix = `Deck ${label}:`

        if (!String(row.mold || '').trim()) {
            errors[specialPlatenRowFieldErrKey(idx, 'mold')] = {
                message: `${prefix} Platen # is required.`,
                type: 'error',
            }
        }
        if (!String(row.result || '').trim()) {
            errors[specialPlatenRowFieldErrKey(idx, 'result')] = {
                message: `${prefix} Result is required.`,
                type: 'error',
            }
        }
    })

    return errors
}

/** Unique SectionId for synthetic General section copies (same style as server copy-section). */
const generatePlatenCopySectionId = () => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    let s = ''
    for (let i = 0; i < 18; i += 1) {
        s += alphabet[Math.floor(Math.random() * alphabet.length)]
    }
    return s
}

const makePlatenCopiedQuestionErrorKey = (section, item) => {
    if (item?.IsCopiedSection && item?.QuestionOriginalId) {
        return `copied|${section.SectionId}|${item.QuestionOriginalId}`
    }
    return item?.Id
}

const zeroQuestionIdsForCopiedPlatenSection = question => {
    const q = JSON.parse(JSON.stringify(question))
    // Only clear the audit-specific question row id. Keep `QuestionId` (template/FAQ id)
    // so save + PDF report can bind answers to the correct field; `QuestionId: 0`
    // caused the second General block to be dropped server-side while UI still looked fine.
    q.Id = 0
    if (
        q.QuestionDisplaySetting &&
        typeof q.QuestionDisplaySetting === 'object' &&
        q.QuestionDisplaySetting.QuestionId !== undefined
    ) {
        q.QuestionDisplaySetting = {
            ...q.QuestionDisplaySetting,
            QuestionId: 0,
        }
    }
    return q
}

/**
 * Copied Platen rows clone template answers. Clear `SingleResponse.Id` so text
 * answers do not overwrite row 1. Omit `MultipleResponse[].Id` on copied rows
 * only (delete the property — do not send `0`): the server must mint new MCQ
 * option-row ids per section, same as `copySection` does for `Section1General`.
 * Keeping duplicate MR ids across two sections is what mixes Platen#/Result in
 * the report even when `SectionName` matches the template for every row.
 */
const stripPlatenCopiedAnswerInstanceIds = question => {
    const q = JSON.parse(JSON.stringify(question))
    const answers = q.Answers && typeof q.Answers === 'object' ? { ...q.Answers } : {}
    if (answers.SingleResponse && typeof answers.SingleResponse === 'object') {
        const sr = { ...answers.SingleResponse }
        if (sr.Id != null) sr.Id = 0
        answers.SingleResponse = sr
    }
    if (Array.isArray(answers.MultipleResponse)) {
        answers.MultipleResponse = answers.MultipleResponse.map(opt => {
            if (!opt || typeof opt !== 'object') return opt
            const next = { ...opt }
            delete next.Id
            return next
        })
    }
    q.Answers = answers
    return q
}

export const MissingInspectionLink = () => {
    const navigation = useNavigate()
    return (
        <div className="container mt-5">
            <FlexColumn>
                <h2>Inspection could not be opened</h2>
                <p>
                    This link is missing an inspection id. Go back and open
                    the inspection again.
                </p>
                <div>
                    <button
                        type="button"
                        className="ui primary button"
                        onClick={() => navigation('/app/task')}
                    >
                        Back to Task List
                    </button>
                </div>
            </FlexColumn>
        </div>
    )
}

export const InspectionList = ({ publicScreen }) => {
    const { id } = useParams()
    const navigation = useNavigate()
    const location = useLocation()
    const isReopened = location.state?.isReopened
    const isPreview = location.state?.isPreview === true

    // toast notifications
    const { successToast, errorToast } = useNotification()

    // Use different hooks based on whether it's a public or private inspection
    const {
        data,
        loading: templateLoading,
        setData,
        refetch,
        error: publicError,
        submitPublicInspection,
    } = publicScreen ? GetPublicInspection(id) : GetAuditById(id)
    const [saving, setSaving] = useState(false)
    const [changed, setChanged] = useState(0)
    const [tempstate, setTempState] = useState(false)
    const [error, setError] = useState(false)
    const [errors, setErrors] = useState(false)
    const [tempFlag, setTempFlag] = useState(false)
    const [isCompleted, setIsCompleted] = useState(false)
    /** TemplateSetting: block edits on completed audits when flag is off */
    const [postCompleteEditBlocked, setPostCompleteEditBlocked] =
        useState(false)
    /** CrossUserAccess: server said this audit is view-only for the caller */
    const [crossUserReadOnlyForced, setCrossUserReadOnlyForced] =
        useState(false)
    const isCompletedAudit = isCompleted || data?.Status === 5
    const canEditCompletedInspection =
        !!isReopened && !postCompleteEditBlocked
    const auditAccessReadOnly =
        crossUserReadOnlyForced || isAuditReadOnly(data)
    const isInspectionReadOnly =
        isPreview ||
        auditAccessReadOnly ||
        (isCompletedAudit && !canEditCompletedInspection)
    const showInspectionActionButtons =
        !isPreview &&
        !auditAccessReadOnly &&
        (!isCompletedAudit || canEditCompletedInspection)
    const auditOwnerName = getAuditOwnerName(data)
    const auditReadOnlyReason = getAuditReadOnlyReason(data)
    const auditReadOnlyBanner =
        auditAccessReadOnly && !isPreview
            ? getAuditReadOnlyMessage({
                  reason: auditReadOnlyReason,
                  ownerName: auditOwnerName,
              })
            : null
    // When true, temporarily disables the navigation prompt (used for Save & Close)
    // const [suppressPrompt, setSuppressPrompt] = useState(false)
    /** Unmount effect also calls save(2); skip one cycle after explicit Save & Close to avoid duplicate POST */
    const skipUnmountSaveRef = useRef(false)
    const saveInProgressRef = useRef(false)
    /** Latest audit Status for unmount guard (avoid effect deps on Status — save() mutates data.Status then setSaving re-runs effect cleanup) */
    const auditStatusRef = useRef(undefined)
    auditStatusRef.current = data?.Status
    const isCompletedRef = useRef(isCompleted)
    isCompletedRef.current = isCompleted
    /** Latest inspection payload (for translate without stale closures). */
    const inspectionDataRef = useRef(data)
    inspectionDataRef.current = data
    const claimLastSavedPayloadRef = useRef(null)
    const reportClaimSectionEditsRef = useRef(null)
    const claimSectionWorkflowStatusesRef = useRef([])
    const [completionMessage, setCompletionMessage] = useState('')
    const [showCompletionMessage, setShowCompletionMessage] = useState(false)
    const [showValidation, setShowValidation] = useState(false)
    const [isPrefilled, setIsPrefilled] = useState(false)
    const reviewDraftAppliedRef = useRef(false)

    // Add state for template list
    const [templateList, setTemplateList] = useState([])

    // Data Import functionality states
    const [previousTemplateData, setPreviousTemplateData] = useState(null)
    const [showDataImportModal, setShowDataImportModal] = useState(false)
    const [mappingQuestions, setMappingQuestions] = useState([])
    const [selectedQuestions, setSelectedQuestions] = useState([])
    const [loadingMappings, setLoadingMappings] = useState(false)

    // Language translation states
    const languageOptions = [
        { key: 'en', text: 'English (primary)', value: 'English' },
        { key: 'es', text: 'Spanish', value: 'Spanish' },
        { key: 'ro', text: 'Romanian', value: 'Romanian' },
    ]
    const [selectedLanguage, setSelectedLanguage] = useState('English')
    const [translating, setTranslating] = useState(false)
    const [translatedData, setTranslatedData] = useState(null)
    /** Last-known inspection for features that still read a ref (e.g. notes). */
    const initialTemplateRef = useRef(null)
    const completionSelectedSectionIdsRef = useRef(null)

    const getQuestionContextById = useCallback(
        questionId => {
            const raw = String(questionId || '')
            if (raw.startsWith('copied|')) {
                const parts = raw.split('|')
                const questionOriginalId = parts[2]
                if (!questionOriginalId || !data?.Sections?.length) return null

                for (let s = 0; s < data.Sections.length; s += 1) {
                    const section = data.Sections[s]
                    const sectionQuestions = Array.isArray(section?.Questions)
                        ? section.Questions
                        : []
                    for (let q = 0; q < sectionQuestions.length; q += 1) {
                        const question = sectionQuestions[q]
                        if (
                            String(question?.QuestionOriginalId || '') ===
                            String(questionOriginalId)
                        ) {
                            return {
                                number: q + 1,
                                faq: question?.FAQ || '',
                                sectionName: section?.SectionName || '',
                            }
                        }
                    }
                }
                return null
            }

            if (!data?.Sections?.length) return null
            let runningIndex = 0

            for (let s = 0; s < data.Sections.length; s += 1) {
                const section = data.Sections[s]
                const sectionQuestions = Array.isArray(section?.Questions)
                    ? section.Questions
                    : []

                for (let q = 0; q < sectionQuestions.length; q += 1) {
                    const question = sectionQuestions[q]
                    runningIndex += 1
                    if (String(question?.Id) === String(questionId)) {
                        return {
                            number: runningIndex,
                            faq: question?.FAQ || '',
                            sectionName: section?.SectionName || '',
                        }
                    }
                }
            }

            return null
        },
        [data],
    )

    const notifyCompletionValidationError = useCallback(
        validationErrors => {
            const firstEntry = Object.entries(validationErrors || {})[0]
            if (!firstEntry) return

            const [firstQuestionId, firstError] = firstEntry
            if (String(firstQuestionId || '').startsWith('__platen_')) {
                errorToast(
                    firstError?.message
                        || 'Please complete required Platen fields.',
                )
                return
            }
            const context = getQuestionContextById(firstQuestionId)
            const message =
                firstError?.message || 'Please complete required validation.'

            if (context?.sectionName) {
                errorToast(`Check ${context.sectionName} section: ${message}`)
                return
            }

            if (context?.number) {
                const questionLabel = context?.faq
                    ? `${context.faq}`
                    : `Question ${context.number}`
                errorToast(`${questionLabel}: ${message}`)
                return
            }

            errorToast(`Question ${firstQuestionId}: ${message}`)
        },
        [errorToast, getQuestionContextById],
    )
    const user = AuthService.getUser()
    const companyId = user?.Companies?.[0]?.CompanyId ?? user?.CompanyId
    const isDailyAuditPlatenGridTemplate =
        Number(companyId) === 34930 &&
        hasDailyAuditPlatenSectionSignature(data?.Sections)
    const isSpecialPlatenTemplate =
        Number(companyId) === 34930 &&
        Number(data?.OriginalTemplateId) === SPECIAL_PLATEN_TEMPLATE_ID &&
        !isDailyAuditPlatenGridTemplate
    let platenGridMode = null
    if (isSpecialPlatenTemplate) {
        platenGridMode = PLATEN_GRID_MODE.SPECIAL_AUDIT
    } else if (isDailyAuditPlatenGridTemplate) {
        platenGridMode = PLATEN_GRID_MODE.DAILY_AUDIT
    }
    const isPlatenGridTemplate = Boolean(platenGridMode)
    const isSpecialCustomerClaimTemplate = matchSpecialCustomerClaimTemplate(
        data,
        companyId,
    )

    useEffect(() => {
        const originalId = Number(data?.OriginalTemplateId)
        if (
            Number(companyId) !== 34930 ||
            !originalId ||
            readLayoutCode(data)
        ) {
            return () => {}
        }
        let cancelled = false
        const loadLayoutCode = async () => {
            try {
                const result = await TemplateService.templateList({
                    PageNumber: 1,
                    PageSize: 500,
                    SearchText: '',
                    SortBy: 'name',
                })
                const match = (result?.Items || []).find(
                    item => Number(item?.Id) === originalId,
                )
                const layout = readLayoutCode(match)
                if (!cancelled && layout) {
                    setData(prev =>
                        prev &&
                        Number(prev.OriginalTemplateId) === originalId &&
                        !readLayoutCode(prev)
                            ? { ...prev, LayoutCode: match.LayoutCode }
                            : prev,
                    )
                }
            } catch (layoutErr) {
                console.warn(
                    '[Customer Claim] LayoutCode lookup failed:',
                    layoutErr,
                )
            }
        }
        loadLayoutCode()
        return () => {
            cancelled = true
        }
    }, [companyId, data?.OriginalTemplateId, data?.LayoutCode, setData])
    const isSpecialFinishMatTemplate =
        Number(companyId) === 34930 &&
        Number(data?.OriginalTemplateId) === SPECIAL_FINISH_MAT_TEMPLATE_ID
    const isReceivingCarpetTemplate =
        Number(companyId) === 34930 &&
        (Number(data?.OriginalTemplateId) === RECEIVING_CARPET_TEMPLATE_ID ||
            String(data?.AuditTemplateId || '').toLowerCase() ===
                RECEIVING_CARPET_AUDIT_TEMPLATE_GUID)
    const isFreezerSamplesTemplate =
        Number(companyId) === 34930 &&
        (Number(data?.OriginalTemplateId) === FREEZER_SAMPLES_TEMPLATE_ID ||
            String(data?.AuditTemplateId || '').toLowerCase() ===
                FREEZER_SAMPLES_AUDIT_TEMPLATE_GUID)
    const getSectionCopyButtonLabel = useCallback(
        section => {
            // Keep buttons hidden on sections that already have neither label.
            if (
                isFreezerSamplesTemplate &&
                isFreezerSamplesBagInfoSection(section)
            ) {
                return null
            }
            if (isReceivingCarpetTemplate) {
                return isReceivingCarpetDetailsSection(section)
                    ? 'Add New'
                    : null
            }
            if (Number(companyId) === 34930) return 'Add New'
            return 'Copy'
        },
        [companyId, isFreezerSamplesTemplate, isReceivingCarpetTemplate],
    )
    const isLanguageFeatureEnabled =
        normalizeBooleanFlag(user?.IsMultipleLanguageAllowed) ||
        normalizeBooleanFlag(user?.Companies?.[0]?.IsMultipleLanguageAllowed) ||
        companyId === 34930 ||
        companyId === 60551

    /** When sections/questions are added/removed/reordered, re-fetch translations. */
    const translationStructureFingerprint = useMemo(() => {
        const sections = data?.Sections
        if (!Array.isArray(sections) || sections.length === 0) {
            return ''
        }
        return sections
            .map(section => {
                const qKeys = (section?.Questions || [])
                    .map(q =>
                        [
                            String(q?.QuestionOriginalId ?? '')
                                .trim()
                                .toLowerCase(),
                            String(q?.Id ?? ''),
                        ].join(':'),
                    )
                    .join(',')
                return [
                    String(section?.SectionId ?? ''),
                    normalizeFaq(section?.SectionName),
                    String(
                        section?.Order ??
                            section?.SectionOrder ??
                            '',
                    ),
                    qKeys,
                ].join('|')
            })
            .join('\u00a6')
    }, [data?.Sections])

    const isAutoSave = user.isAutoSave || true
    const [platenRows, setPlatenRows] = useState([])
    const EMPTY_CLAIM_PORTAL_DRAFT = useMemo(
        () => ({
            matType: '',
            itemSketchNumber: '',
            size: '',
            quantity: '',
            sfoOrder: '',
            complaintDetails: '',
            code: '',
            rma: '',
            creditIssue: '',
            notes: '',
        }),
        [],
    )
    const [claimRows, setClaimRows] = useState([])
    const [claimPortalDraft, setClaimPortalDraft] = useState({
        matType: '',
        itemSketchNumber: '',
        size: '',
        quantity: '',
        sfoOrder: '',
        complaintDetails: '',
        code: '',
        rma: '',
        creditIssue: '',
        notes: '',
    })
    const [claimSelectedSupplier, setClaimSelectedSupplier] = useState('')
    const [claimActivePortalSectionId, setClaimActivePortalSectionId] =
        useState(null)
    const claimActivePortalSectionIdRef = useRef(null)
    claimActivePortalSectionIdRef.current = claimActivePortalSectionId
    const claimExtraAnswersRef = useRef({})
    const [claimQualityForm, setClaimQualityForm] = useState({})
    const [claimCompletionForm, setClaimCompletionForm] = useState({})
    const [claimFieldErrors, setClaimFieldErrors] = useState({})

    const resolveInspectionClaimQuestion = useCallback(
        (templateQuestionMap, aliases, inspectionSection = null) => {
            const templateQ = findQuestionByAliases(templateQuestionMap, aliases)
            if (!templateQ) {
                return null
            }
            const templateWithSection = inspectionSection
                ? {
                      ...templateQ,
                      SectionId:
                          inspectionSection.SectionId ?? templateQ.SectionId,
                      SectionName:
                          inspectionSection.SectionName ?? templateQ.SectionName,
                  }
                : templateQ
            const sectionsToSearch = inspectionSection
                ? [inspectionSection]
                : data?.Sections
            return (
                findInspectionQuestionByTemplateRef(
                    sectionsToSearch,
                    templateWithSection,
                ) || templateQ
            )
        },
        [data?.Sections],
    )

    const showSectionDropdown = useMemo(() => {
        if (!data) {
            return false
        }

        const directFlag = extractShowSectionDropdownFlag(data)
        if (typeof directFlag === 'boolean') {
            return directFlag
        }

        const companySettingFlag = Array.isArray(data?.CompanySetting)
            ? data.CompanySetting
                .map(setting => extractShowSectionDropdownFlag(setting))
                .find(value => typeof value === 'boolean')
            : undefined

        if (typeof companySettingFlag === 'boolean') {
            return companySettingFlag
        }

        if (typeof data?.IsSectionVisibleInDropdown === 'boolean') {
            return data.IsSectionVisibleInDropdown
        }

        return false
    }, [data])

    const isShowHorizontal = useMemo(
        () => !!data?.IsShowHorizontal,
        [data?.IsShowHorizontal],
    )
    const shouldForceHorizontalForTemplate = isShowHorizontal

    useEffect(() => {
        if (isLanguageFeatureEnabled && data && data.Sections) {
            initialTemplateRef.current = data
        }
    }, [data, isLanguageFeatureEnabled])

    useEffect(() => {
        if (publicScreen || reviewDraftAppliedRef.current || !data?.AuditTemplateId) {
            return
        }

        const reviewDraftKey = `inspection-review-draft-${data.AuditTemplateId}`
        const draftRaw = sessionStorage.getItem(reviewDraftKey)
        if (!draftRaw) return

        try {
            const draftData = JSON.parse(draftRaw)
            if (draftData?.AuditTemplateId === data.AuditTemplateId) {
                setData(draftData)
                setChanged(0)
            }
        } catch (e) {
            console.error('Failed to parse review draft:', e)
        } finally {
            sessionStorage.removeItem(reviewDraftKey)
            reviewDraftAppliedRef.current = true
        }
    }, [data?.AuditTemplateId, publicScreen, setData])

    // useEffect to pre-fill date/time fields and assign auto numbers on new inspection
    useEffect(() => {
        if (data && data.Sections && !isPrefilled) {
            const newData = JSON.parse(JSON.stringify(data))
            let wasChanged = false

            newData.Sections.forEach(section => {
                section.Questions.forEach((question, qIdx) => {
                    if (
                        Number(question.QuestionResponseId) ===
                        Number(ControlType.AutoNumber)
                    ) {
                        const { question: nextQ, changed: autoChanged } =
                            assignAutoNumberForInspectionQuestion(question)
                        if (autoChanged) {
                            section.Questions[qIdx] = nextQ
                            wasChanged = true
                        }
                        return
                    }

                    if (
                        question.Answers?.SingleResponse &&
                        !question.Answers.SingleResponse.Response
                    ) {
                        const now = new Date()
                        let prefilledValue = null

                        switch (question.QuestionResponseId) {
                            case ControlType.Date:
                                prefilledValue = now
                                break
                            case ControlType.Time:
                                prefilledValue = now
                                break
                            case ControlType.DateAndTime:
                                prefilledValue = now
                                break
                            default:
                                break
                        }

                        if (prefilledValue !== null) {
                            question.Answers.SingleResponse.Response =
                                prefilledValue.toISOString()
                            const answered = isQuestionAnswered(question)
                            question.IsAnswered = answered
                            if (question.Answers) {
                                question.Answers.IsAnswered = answered
                            }
                            wasChanged = true
                        }
                    }
                })
            })

            if (wasChanged) {
                setData(newData)
                setChanged(c => c + 1) // To trigger save prompt if user leaves
            }
            setIsPrefilled(true) // Ensure this runs only once
        }
    }, [data, isPrefilled, setData])

    // Fetch templates on mount
    useEffect(() => {
        async function fetchTemplates() {
            try {
                const response = await TemplateService.templateList({
                    PageNumber: 1,
                    PageSize: 20,
                })
                setTemplateList(response?.Items || [])
            } catch (e) {
                setTemplateList([])
            }
        }
        fetchTemplates()
    }, [])

    // Re-translate when language changes or section/question structure changes
    // (add/remove/reorder). Uses latest inspection snapshot, not a frozen first load.
    useEffect(() => {
        if (!isLanguageFeatureEnabled) {
            setTranslatedData(null)
            setTranslating(false)
            return undefined
        }

        if (selectedLanguage === 'English') {
            setTranslatedData(null)
            setTranslating(false)
            return undefined
        }

        let cancelled = false
        const timer = setTimeout(async () => {
            const live = inspectionDataRef.current
            if (!live?.Sections?.length) {
                if (!cancelled) {
                    setTranslatedData(null)
                    setTranslating(false)
                }
                return
            }

            if (!cancelled) setTranslating(true)
            try {
                const translated = await TranslationService.translateTemplate(
                    JSON.parse(JSON.stringify(live)),
                    selectedLanguage,
                    'English',
                )
                if (!cancelled) {
                    setTranslatedData(translated)
                }
            } catch (error) {
                console.error('Translation error:', error)
                if (!cancelled) {
                    setTranslatedData(null)
                }
            } finally {
                if (!cancelled) {
                    setTranslating(false)
                }
            }
        }, 350)

        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [
        selectedLanguage,
        isLanguageFeatureEnabled,
        translationStructureFingerprint,
    ])

    // Use translated data for labels if available (companyId 34930 only),
    // but keep original data structure for answers and interactions.
    const displayData = useMemo(() => {
        if (
            !isLanguageFeatureEnabled ||
            selectedLanguage === 'English' ||
            !translatedData ||
            translating
        ) {
            return data
        }
        return translatedData
    }, [
        data,
        translatedData,
        selectedLanguage,
        translating,
        isLanguageFeatureEnabled,
    ])

    // Merge translated labels into the live inspection data for rendering,
    // so answers remain clickable while text appears in the selected language.
    const sectionsForRender = useMemo(() => {
        // If translation is not active, just use the live data.
        if (
            !isLanguageFeatureEnabled ||
            selectedLanguage === 'English' ||
            !translatedData ||
            translating ||
            !Array.isArray(data?.Sections)
        ) {
            return data?.Sections || []
        }

        const translatedSections = translatedData.Sections || []

        return data.Sections.map(section => {
            const tSection = resolveTranslatedSectionForMerge(
                section,
                translatedSections,
            )
            if (!tSection) {
                return section
            }

            const mergedQuestions = (section.Questions || []).map(
                question => {
                    const tQuestion = resolveTranslatedQuestionForMerge(
                        question,
                        tSection.Questions,
                    )
                    if (!tQuestion) {
                        return question
                    }

                    const mergedQuestion = {
                        ...question,
                    }

                    // Translate question text and FAQ
                    if (tQuestion.Question) {
                        mergedQuestion.Question = tQuestion.Question
                    }
                    if (tQuestion.FAQ) {
                        mergedQuestion.FAQ = tQuestion.FAQ
                    }

                    const originalAnswers = question.Answers || {}
                    const translatedAnswers = tQuestion.Answers || {}
                    const translatedTemplateType =
                        tQuestion.TemplateResponseType || {}

                    let mergedAnswers = originalAnswers

                    // Helper: normalize text by stripping score in parentheses, e.g. "Conforming (5)" -> "Conforming"
                    const normalizeLabel = text => {
                        if (!text || typeof text !== 'string') return text
                        return text.replace(/\(\d+\)\s*$/, '').trim()
                    }

                    // Translate MultipleResponse answer labels by aligning with translated answers/template options
                    if (Array.isArray(originalAnswers.MultipleResponse)) {
                        const translatedAnswerOptions =
                            translatedAnswers.MultipleResponse || []
                        const translatedTemplateOptions =
                            translatedTemplateType.MultipleResponse || []
                        const translatedUserOptions =
                            translatedTemplateType.UserMultipleResponse || []

                        const mappedMultipleResponse =
                            originalAnswers.MultipleResponse.map(opt => {
                                // Try to find translated option by index (when structures match)
                                let tOpt = null

                                const idx =
                                    originalAnswers.MultipleResponse.indexOf(
                                        opt,
                                    )
                                if (
                                    idx > -1 &&
                                    translatedAnswerOptions[idx]
                                ) {
                                    tOpt = translatedAnswerOptions[idx]
                                } else {
                                    // Fallback: match by normalized label against translated template options
                                    const baseLabel = normalizeLabel(
                                        opt.DisplayText,
                                    )

                                    tOpt =
                                        translatedTemplateOptions.find(to =>
                                            baseLabel
                                                ? normalizeLabel(
                                                    to.DisplayText,
                                                ) === baseLabel
                                                : false,
                                        ) ||
                                        translatedUserOptions.find(to =>
                                            baseLabel
                                                ? normalizeLabel(
                                                    to.DisplayText,
                                                ) === baseLabel
                                                : false,
                                        ) ||
                                        null
                                }

                                if (!tOpt) {
                                    return opt
                                }

                                return {
                                    ...opt,
                                    DisplayText:
                                        tOpt.DisplayText || opt.DisplayText,
                                    HelpText: tOpt.HelpText || opt.HelpText,
                                }
                            })

                        mergedAnswers = {
                            ...mergedAnswers,
                            MultipleResponse: mappedMultipleResponse,
                        }
                    }

                    // Translate ParentChildResponse answer labels (parents and children)
                    if (
                        Array.isArray(originalAnswers.ParentChildResponse) &&
                        Array.isArray(translatedAnswers.ParentChildResponse)
                    ) {
                        const mappedParentChild =
                            originalAnswers.ParentChildResponse.map(
                                (parent, pIndex) => {
                                    const tParent =
                                        translatedAnswers.ParentChildResponse[
                                        pIndex
                                        ]
                                    if (!tParent) {
                                        return parent
                                    }

                                    const mergedParent = {
                                        ...parent,
                                    }

                                    if (tParent.ParentName) {
                                        mergedParent.ParentName =
                                            tParent.ParentName
                                    }
                                    if (tParent.ChildLabel) {
                                        mergedParent.ChildLabel =
                                            tParent.ChildLabel
                                    }

                                    if (
                                        Array.isArray(parent.Children) &&
                                        Array.isArray(tParent.Children)
                                    ) {
                                        mergedParent.Children =
                                            parent.Children.map(
                                                (child, cIndex) => {
                                                    const tChild =
                                                        tParent.Children[
                                                        cIndex
                                                        ]
                                                    if (!tChild) {
                                                        return child
                                                    }
                                                    return {
                                                        ...child,
                                                        ChildName:
                                                            tChild.ChildName ||
                                                            child.ChildName,
                                                    }
                                                },
                                            )
                                    }

                                    return mergedParent
                                },
                            )

                        mergedAnswers = {
                            ...mergedAnswers,
                            ParentChildResponse: mappedParentChild,
                        }
                    }

                    // Translate SingleResponse free-text answers (e.g. TextBox / TextArea)
                    if (
                        originalAnswers.SingleResponse &&
                        translatedAnswers.SingleResponse &&
                        typeof translatedAnswers.SingleResponse.Response ===
                        'string' &&
                        translatedAnswers.SingleResponse.Response.trim()
                    ) {
                        mergedAnswers = {
                            ...mergedAnswers,
                            SingleResponse: {
                                ...originalAnswers.SingleResponse,
                                Response:
                                    translatedAnswers.SingleResponse.Response,
                            },
                        }
                    }

                    // Translate Notes and Internal Notes text
                    if (
                        Array.isArray(question.Notes) &&
                        Array.isArray(tQuestion.Notes)
                    ) {
                        mergedQuestion.Notes = question.Notes.map(
                            (note, noteIndex) => {
                                const tNote = tQuestion.Notes[noteIndex]
                                if (
                                    !tNote ||
                                    !tNote.Notes ||
                                    typeof tNote.Notes !== 'string'
                                ) {
                                    return note
                                }

                                return {
                                    ...note,
                                    Notes: tNote.Notes,
                                }
                            },
                        )
                    }

                    // Translate SingleResponse free-text answers (e.g. TextBox / TextArea)
                    if (
                        originalAnswers.SingleResponse &&
                        translatedAnswers.SingleResponse &&
                        typeof translatedAnswers.SingleResponse.Response ===
                        'string' &&
                        translatedAnswers.SingleResponse.Response.trim()
                    ) {
                        mergedAnswers = {
                            ...mergedAnswers,
                            SingleResponse: {
                                ...originalAnswers.SingleResponse,
                                Response:
                                    translatedAnswers.SingleResponse.Response,
                            },
                        }
                    }

                    mergedQuestion.Answers = mergedAnswers

                    return mergedQuestion
                },
            )

            return {
                ...section,
                SectionName: tSection.SectionName || section.SectionName,
                Questions: mergedQuestions,
            }
        })
    }, [
        data,
        isLanguageFeatureEnabled,
        selectedLanguage,
        translatedData,
        translating,
    ])

    // In preview mode, force everything to behave as read-only by setting
    // IsImported/IsReadOnly flags on both section and question levels.
    const filterCustomerClaimSectionsFromList = useCallback(
        sections => {
            if (!isSpecialCustomerClaimTemplate) {
                return sections
            }
            const portalKey = PORTAL_SECTION_NAME.trim().toLowerCase()
            const qualityKey = QUALITY_SECTION_NAME.trim().toLowerCase()
            const completionKey = COMPLETION_SECTION_NAME.trim().toLowerCase()
            return (sections || []).filter(section => {
                const name = normalizeFaq(section?.SectionName)
                if (name === portalKey) return false
                if (name === qualityKey) return false
                if (name === completionKey) return false
                return true
            })
        },
        [isSpecialCustomerClaimTemplate],
    )

    const filterFinishMatSectionsFromList = useCallback(
        sections => {
            if (!isSpecialFinishMatTemplate) {
                return sections
            }
            return filterFinishMatGeneralInfoFromSections(sections)
        },
        [isSpecialFinishMatTemplate],
    )

    const sectionsForRenderForPreview = useMemo(() => {
        let baseSections = hydrateInspectionParentChildResponses(
            sectionsForRender,
        )

        if (isPlatenGridTemplate && data) {
            baseSections = hydrateInspectionMultipleResponsesFromTemplate(
                baseSections,
                data,
            )
        }

        baseSections = filterCustomerClaimSectionsFromList(baseSections)
        baseSections = filterFinishMatSectionsFromList(baseSections)

        if (!isPreview || companyId === 34930) return baseSections
        return (
            Array.isArray(baseSections) ? baseSections : []
        ).map(section => ({
            ...section,
            IsImported: true,
            IsReadOnly: true,
            Questions: Array.isArray(section?.Questions)
                ? section.Questions.map(q => ({
                    ...q,
                    IsImported: true,
                    IsReadOnly: true,
                }))
                : section.Questions,
        }))
    }, [
        sectionsForRender,
        isPreview,
        companyId,
        isPlatenGridTemplate,
        isSpecialCustomerClaimTemplate,
        isSpecialFinishMatTemplate,
        filterCustomerClaimSectionsFromList,
        filterFinishMatSectionsFromList,
        data,
    ])

    const sectionsForFinishMatRenderGroups = useMemo(() => {
        if (!isSpecialFinishMatTemplate) {
            return []
        }

        let baseSections = hydrateInspectionParentChildResponses(
            sectionsForRender,
        )

        if (isPlatenGridTemplate && data) {
            baseSections = hydrateInspectionMultipleResponsesFromTemplate(
                baseSections,
                data,
            )
        }

        baseSections = filterCustomerClaimSectionsFromList(baseSections)

        if (!isPreview || companyId === 34930) return baseSections
        return (
            Array.isArray(baseSections) ? baseSections : []
        ).map(section => ({
            ...section,
            IsImported: true,
            IsReadOnly: true,
            Questions: Array.isArray(section?.Questions)
                ? section.Questions.map(q => ({
                    ...q,
                    IsImported: true,
                    IsReadOnly: true,
                }))
                : section.Questions,
        }))
    }, [
        sectionsForRender,
        isPreview,
        companyId,
        isPlatenGridTemplate,
        isSpecialFinishMatTemplate,
        filterCustomerClaimSectionsFromList,
        data,
    ])

    const finishMatRenderGroups = useMemo(() => {
        if (!isSpecialFinishMatTemplate) {
            return []
        }
        return buildFinishMatOrderedRenderGroups(
            sectionsForFinishMatRenderGroups,
        )
    }, [isSpecialFinishMatTemplate, sectionsForFinishMatRenderGroups])

    const finishMatGroupCount = finishMatRenderGroups.length

    const templateTotalQuestions = useMemo(() => {
        let total = 0
        if (!data?.Sections?.length) return total
        data.Sections.forEach(section => {
            if (!Array.isArray(section?.Questions)) return
            total += section.Questions.length
        })
        return total
    }, [data?.Sections])

    const claimPortalTemplateSection = useMemo(
        () =>
            isSpecialCustomerClaimTemplate
                ? findSectionByName(data?.Sections, PORTAL_SECTION_NAME)
                : null,
        [data?.Sections, isSpecialCustomerClaimTemplate],
    )

    const claimPortalSection = useMemo(
        () =>
            isSpecialCustomerClaimTemplate
                ? findSectionByName(data?.Sections, PORTAL_SECTION_NAME)
                : null,
        [data?.Sections, isSpecialCustomerClaimTemplate],
    )

    const claimPortalQuestionMap = useMemo(() => {
        const questions = claimPortalSection?.Questions
        if (!questions?.length) return {}
        return buildQuestionMapByFaq(questions)
    }, [claimPortalSection])

    const claimSupplierOptions = useMemo(() => {
        return extractSupplierOptionsFromAudit(data?.Sections, data)
    }, [
        data?.Sections,
        isSpecialCustomerClaimTemplate,
    ])

    const claimMatTypeOptions = useMemo(() => {
        const q = findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.matType)
        return questionToDropdownOptions(q, data)
    }, [claimPortalQuestionMap, data])

    const claimSizeOptions = useMemo(() => {
        const q = findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.size)
        return questionToDropdownOptions(q, data)
    }, [claimPortalQuestionMap, data])

    const claimCreditPortalOptions = useMemo(() => {
        const q = findQuestionByAliases(
            claimPortalQuestionMap,
            PORTAL_FAQ.creditIssue,
        )
        return questionToDropdownOptions(q, data)
    }, [claimPortalQuestionMap, data])

    const claimCodeOptions = useMemo(() => {
        const q = findQuestionByAliases(claimPortalQuestionMap, PORTAL_FAQ.code)
        return questionToDropdownOptions(q, data)
    }, [claimPortalQuestionMap, data])

    const claimQualitySection = useMemo(
        () =>
            isSpecialCustomerClaimTemplate
                ? findSectionByName(data?.Sections, QUALITY_SECTION_NAME)
                : null,
        [data?.Sections, isSpecialCustomerClaimTemplate],
    )

    const claimCompletionSection = useMemo(
        () =>
            isSpecialCustomerClaimTemplate
                ? findSectionByName(data?.Sections, COMPLETION_SECTION_NAME)
                : null,
        [data?.Sections, isSpecialCustomerClaimTemplate],
    )

    const claimQualityTemplateSection = useMemo(
        () =>
            isSpecialCustomerClaimTemplate
                ? findSectionByName(data?.Sections, QUALITY_SECTION_NAME)
                : null,
        [data?.Sections, isSpecialCustomerClaimTemplate],
    )

    const claimCompletionTemplateSection = useMemo(
        () =>
            isSpecialCustomerClaimTemplate
                ? findSectionByName(data?.Sections, COMPLETION_SECTION_NAME)
                : null,
        [data?.Sections, isSpecialCustomerClaimTemplate],
    )

    const claimQualityQuestionMap = useMemo(() => {
        if (!claimQualityTemplateSection?.Questions) return {}
        return buildQuestionMapByFaq(claimQualityTemplateSection.Questions)
    }, [claimQualityTemplateSection])

    const claimCompletionQuestionMap = useMemo(() => {
        if (!claimCompletionTemplateSection?.Questions) return {}
        return buildQuestionMapByFaq(claimCompletionTemplateSection.Questions)
    }, [claimCompletionTemplateSection])

    const claimCarrierOptions = useMemo(() => {
        const q = findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.carrier)
        return questionToDropdownOptions(q, data)
    }, [claimQualityQuestionMap, data])

    const claimPackingOptions = useMemo(() => {
        const q = findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.packingMatCondition)
        return questionToDropdownOptions(q, data)
    }, [claimQualityQuestionMap, data])

    const claimDispositionOptions = useMemo(() => {
        const q = findQuestionByAliases(
            claimQualityQuestionMap,
            QUALITY_FAQ.disposition,
        )
        return questionToDropdownOptions(q, data)
    }, [claimQualityQuestionMap, data])

    const claimStatusOptions = useMemo(() => {
        const q = findQuestionByAliases(claimQualityQuestionMap, QUALITY_FAQ.status)
        return questionToDropdownOptions(q, data)
    }, [claimQualityQuestionMap, data])

    const claimReceivingDateQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimQualityQuestionMap,
                QUALITY_FAQ.receivingDate,
                claimQualitySection,
            ),
        [
            claimQualityQuestionMap,
            claimQualitySection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimReceivingDateUsesTime = useMemo(
        () =>
            Number(claimReceivingDateQuestion?.QuestionResponseId) ===
            ControlType.DateAndTime,
        [claimReceivingDateQuestion],
    )

    const claimCreditCompletionOptions = useMemo(() => {
        const q = findQuestionByAliases(
            claimCompletionQuestionMap,
            COMPLETION_FAQ.creditIssue,
        )
        return questionToDropdownOptions(q, data)
    }, [claimCompletionQuestionMap, data])

    const claimItemSketchQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimPortalQuestionMap,
                PORTAL_FAQ.itemSketch,
                claimPortalSection,
            ),
        [
            claimPortalQuestionMap,
            claimPortalSection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimQuantityQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimPortalQuestionMap,
                PORTAL_FAQ.quantity,
                claimPortalSection,
            ),
        [
            claimPortalQuestionMap,
            claimPortalSection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimSfoOrderQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimPortalQuestionMap,
                PORTAL_FAQ.sfoOrder,
                claimPortalSection,
            ),
        [
            claimPortalQuestionMap,
            claimPortalSection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimRmaQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimPortalQuestionMap,
                PORTAL_FAQ.rma,
                claimPortalSection,
            ),
        [
            claimPortalQuestionMap,
            claimPortalSection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimPortalNotesQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimPortalQuestionMap,
                PORTAL_FAQ.notes,
                claimPortalSection,
            ),
        [
            claimPortalQuestionMap,
            claimPortalSection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimQualityNotesQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimQualityQuestionMap,
                QUALITY_FAQ.notes,
                claimQualitySection,
            ),
        [
            claimQualityQuestionMap,
            claimQualitySection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimCompletionNotesQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimCompletionQuestionMap,
                COMPLETION_FAQ.notes,
                claimCompletionSection,
            ),
        [
            claimCompletionQuestionMap,
            claimCompletionSection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimDetailsQuestion = useMemo(
        () =>
            resolveInspectionClaimQuestion(
                claimPortalQuestionMap,
                PORTAL_FAQ.complaintDetails,
                claimPortalSection,
            ),
        [
            claimPortalQuestionMap,
            claimPortalSection,
            resolveInspectionClaimQuestion,
        ],
    )

    const claimLoggedRows = useMemo(
        () => (claimRows || []).filter(isClaimPortalRowFilled),
        [claimRows],
    )

    const claimPortalSectionsFingerprint = useMemo(() => {
        if (!isSpecialCustomerClaimTemplate || !Array.isArray(data?.Sections)) {
            return ''
        }
        const portals = listClaimPortalSectionsFromAudit(data.Sections)
        try {
            return JSON.stringify(
                portals.map(s => ({
                    sid: s?.SectionId,
                    qs: (s?.Questions || []).map(q => ({
                        faq: normalizeFaq(q?.FAQ),
                        sr: q?.Answers?.SingleResponse?.Response ?? null,
                        mr: (q?.Answers?.MultipleResponse || [])
                            .filter(o => o.IsSelected)
                            .map(o => o.DisplayText),
                    })),
                })),
            )
        } catch {
            return String(portals.length)
        }
    }, [data?.Sections, isSpecialCustomerClaimTemplate])

    const claimQualityCompletionFingerprint = useMemo(() => {
        if (!isSpecialCustomerClaimTemplate) {
            return ''
        }

        const encodeSection = section => ({
            sid: section?.SectionId,
            qs: (section?.Questions || []).map(q => ({
                faq: normalizeFaq(q?.FAQ),
                sr: q?.Answers?.SingleResponse?.Response ?? null,
                mr: (q?.Answers?.MultipleResponse || [])
                    .filter(o => o.IsSelected)
                    .map(o => o.DisplayText),
            })),
        })

        try {
            return JSON.stringify({
                quality: encodeSection(claimQualitySection),
                completion: encodeSection(claimCompletionSection),
            })
        } catch {
            const qLen = claimQualitySection?.Questions?.length || 0
            const cLen = claimCompletionSection?.Questions?.length || 0
            return `${qLen}:${cLen}`
        }
    }, [
        isSpecialCustomerClaimTemplate,
        claimQualitySection,
        claimCompletionSection,
    ])

    useEffect(() => {
        setClaimActivePortalSectionId(null)
        claimExtraAnswersRef.current = {}
    }, [id])

    useEffect(() => {
        if (!isSpecialCustomerClaimTemplate || !data?.Sections?.length) {
            return
        }
        const rows = buildClaimRowsFromAuditSections(data.Sections)
        setClaimRows(rows)
        setClaimQualityForm(extractQualityFormFromSection(claimQualitySection))
        setClaimCompletionForm(
            extractCompletionFormFromSection(claimCompletionSection),
        )

        const activeRow = resolveActiveClaimPortalRow(
            rows,
            claimActivePortalSectionIdRef.current,
        )
        setClaimPortalDraft(prev =>
            mergePortalDraftPreservingLocal(
                portalRowToDraft(activeRow),
                prev,
            ),
        )
        if (activeRow && isClaimPortalRowFilled(activeRow)) {
            setClaimActivePortalSectionId(activeRow.sectionId || null)
            setClaimSelectedSupplier(prev => {
                const fromServer = String(activeRow.supplier || '').trim()
                return fromServer || prev || ''
            })
        }

        try {
            claimLastSavedPayloadRef.current = buildCustomerClaimPayload(
                JSON.parse(JSON.stringify(data)),
                rows,
                extractQualityFormFromSection(claimQualitySection),
                extractCompletionFormFromSection(claimCompletionSection),
            )
        } catch (snapshotErr) {
            console.warn(
                '[Customer Claim] last-saved payload snapshot failed:',
                snapshotErr,
            )
        }
    }, [
        claimPortalSectionsFingerprint,
        isSpecialCustomerClaimTemplate,
        claimQualityCompletionFingerprint,
    ])

    useEffect(() => {
        if (!showValidation || !isSpecialCustomerClaimTemplate) {
            setClaimFieldErrors({})
            return
        }
        setClaimFieldErrors(
            buildCustomerClaimCompletionErrors(
                claimRows,
                claimSelectedSupplier,
                claimPortalDraft,
            ),
        )
    }, [
        showValidation,
        isSpecialCustomerClaimTemplate,
        claimRows,
        claimSelectedSupplier,
        claimPortalDraft,
    ])

    const extractQuestionMediaFiles = question => {
        if (!question) return []
        return mapInspectionQuestionMediaFiles(question)
    }

    const claimItemSketchMediaFiles = useMemo(
        () => extractQuestionMediaFiles(claimItemSketchQuestion),
        [claimItemSketchQuestion, data?.Sections],
    )

    const claimDetailsMediaFiles = useMemo(
        () => extractQuestionMediaFiles(claimDetailsQuestion),
        [claimDetailsQuestion, data?.Sections],
    )

    const handleClearClaimForm = useCallback(() => {
        setClaimSelectedSupplier('')
        setClaimPortalDraft({ ...EMPTY_CLAIM_PORTAL_DRAFT })
    }, [EMPTY_CLAIM_PORTAL_DRAFT])

    const buildCustomerClaimPayloadForAudit = useCallback(
        auditData => {
            if (!isSpecialCustomerClaimTemplate) return auditData
            const rowsForPayload = mergeClaimRowsWithPortalDraft(
                claimRows,
                claimSelectedSupplier,
                claimPortalDraft,
                claimActivePortalSectionId,
            )
            return buildCustomerClaimPayload(
                auditData,
                rowsForPayload,
                claimQualityForm,
                claimCompletionForm,
                claimExtraAnswersRef.current,
            )
        },
        [
            isSpecialCustomerClaimTemplate,
            claimRows,
            claimSelectedSupplier,
            claimPortalDraft,
            claimActivePortalSectionId,
            claimQualityForm,
            claimCompletionForm,
        ],
    )

    const platenGridTemplateSection = useMemo(() => {
        if (!isPlatenGridTemplate) return null
        const sections = sectionsForRenderForPreview || []
        if (platenGridMode === PLATEN_GRID_MODE.SPECIAL_AUDIT) {
            return (
                sections.find(
                    s => normalizeFaq(s?.SectionName) === 'general',
                ) || null
            )
        }
        // Daily audit: same pattern as Special — prefer primary "Platen", then any Platen / Platen (n)
        return (
            sections.find(
                s =>
                    normalizeFaq(s?.SectionName) === 'platen' &&
                    !s?.IsCopiedSection,
            ) ||
            sections.find(s => normalizeFaq(s?.SectionName) === 'platen') ||
            sections.find(s =>
                isDailyAuditPlatenSectionName(s?.SectionName),
            ) ||
            null
        )
    }, [sectionsForRenderForPreview, isPlatenGridTemplate, platenGridMode])

    const specialGeneralSection = platenGridTemplateSection

    const specialPressQuestion = useMemo(
        () => findSpecialPlatenPressQuestion(sectionsForRenderForPreview),
        [sectionsForRenderForPreview],
    )

    const specialGeneralQuestionMap = useMemo(() => {
        const map = {}
        const qs = specialGeneralSection?.Questions || []
        qs.forEach(q => {
            const key = normalizeFaq(q?.FAQ)
            if (key && !map[key]) {
                map[key] = q
            }
        })
        return map
    }, [specialGeneralSection])

    const specialPlatenQuestion = resolvePlatenDeckQuestion(
        specialGeneralQuestionMap,
    )
    const specialResultQuestion = useMemo(() => {
        const mapped = specialGeneralQuestionMap.result || null
        const mr = mapped?.Answers?.MultipleResponse || []
        if (mr.length) return mapped

        const questions = specialGeneralSection?.Questions || []
        const byFaq = questions.find(q => {
            const opts = q?.Answers?.MultipleResponse || []
            if (!opts.length) return false
            const k = normalizeFaq(q?.FAQ)
            return k.includes('result')
        })
        return byFaq || mapped
    }, [specialGeneralQuestionMap, specialGeneralSection])

    const specialPlatenLabels = useMemo(() => {
        if (platenGridMode === PLATEN_GRID_MODE.DAILY_AUDIT) {
            return Array.from({ length: DAILY_AUDIT_PLATEN_ROW_COUNT }).map(
                (_, i) => String(i + 1),
            )
        }
        const options = specialPlatenQuestion?.Answers?.MultipleResponse || []
        const labels = options
            .map(opt => String(opt?.DisplayText || '').trim())
            .filter(Boolean)
        return labels.length
            ? labels
            : Array.from({ length: DAILY_AUDIT_PLATEN_ROW_COUNT }).map(
                  (_, i) => String(i + 1),
              )
    }, [specialPlatenQuestion, platenGridMode])

    const specialPlatenRowsForDisplay = useMemo(
        () => sortPlatenRowsByDeckDescending(platenRows),
        [platenRows],
    )

    /** Re-hydrate platen grid from `data` only when General *answers* change, not when Press-only edits replace section objects. */
    const platenGeneralSectionsFingerprint = useMemo(() => {
        if (!isPlatenGridTemplate || !platenGridMode || !Array.isArray(data?.Sections)) {
            return ''
        }
        const gridSections = listPlatenGridSectionsFromAudit(
            data.Sections,
            platenGridMode,
        )
        try {
            return JSON.stringify(
                gridSections.map(s => ({
                    sid: s?.SectionId,
                    qs: (s?.Questions || []).map(q => ({
                        i: q?.Id,
                        faq: normalizeFaq(q?.FAQ),
                        sr: q?.Answers?.SingleResponse?.Response ?? null,
                        mrSel: (q?.Answers?.MultipleResponse || []).map(
                            o => [String(o?.ResponseId ?? ''), !!o?.IsSelected],
                        ),
                    })),
                })),
            )
        } catch {
            return String(gridSections.length)
        }
    }, [data?.Sections, isPlatenGridTemplate, platenGridMode])

    const specialResultDropdownOptions = useMemo(
        () =>
            buildPlatenResultDropdownOptionsFromQuestion(specialResultQuestion),
        [specialResultQuestion],
    )

    const specialPressDropdownOptions = useMemo(() => {
        const options = specialPressQuestion?.Answers?.MultipleResponse || []
        const blank = [
            {
                key: '__platen_press_blank',
                value: '',
                text: '-- Select --',
                content: (
                    <span style={{ fontWeight: 600, color: '#1e5a8f' }}>
                        -- Select --
                    </span>
                ),
            },
        ]

        const items = options.map(opt => {
            const label = String(opt.DisplayText || '').trim()
            return {
                key: opt.Id ?? label,
                text: label,
                value: label,
            }
        })

        return [...blank, ...items]
    }, [specialPressQuestion])

    const specialPlatenResultCounts = useMemo(() => {
        let conforming = 0
        let nonConforming = 0
        let bypass = 0
        const rows = Array.isArray(platenRows) ? platenRows : []
        rows.forEach(row => {
            const cat = classifySpecialPlatenResult(
                row.result,
                specialResultQuestion,
            )
            if (cat === 'conforming') conforming += 1
            else if (cat === 'nonconforming') nonConforming += 1
            else if (cat === 'bypass') bypass += 1
        })
        return { conforming, nonConforming, bypass }
    }, [platenRows, specialResultQuestion])

    const smallDailyPlatenGridSection = useMemo(() => {
        const sections = sectionsForRenderForPreview || []
        if (!Array.isArray(sections) || sections.length === 0) return null
        return (
            sections.find(section =>
                hasSmallDailyPlatenSectionSignature([section]),
            ) || null
        )
    }, [sectionsForRenderForPreview])

    const sectionsWithoutSmallDailyPlatenGrid = useMemo(() => {
        const sections = sectionsForRenderForPreview || []
        if (!smallDailyPlatenGridSection) return sections
        return sections.filter(
            section => !hasSmallDailyPlatenSectionSignature([section]),
        )
    }, [sectionsForRenderForPreview, smallDailyPlatenGridSection])

    // eslint-disable-next-line no-unused-vars
    const smallDailyPlatenGridSectionIndex = useMemo(() => {
        if (!smallDailyPlatenGridSection?.SectionId) return -1
        return (sectionsForRenderForPreview || []).findIndex(
            section =>
                String(section?.SectionId) ===
                String(smallDailyPlatenGridSection.SectionId),
        )
    }, [sectionsForRenderForPreview, smallDailyPlatenGridSection])

    const sectionsBeforeSmallDailyPlatenGrid = useMemo(() => {
        if (smallDailyPlatenGridSectionIndex < 0) {
            return sectionsForRenderForPreview || []
        }
        return (sectionsForRenderForPreview || []).slice(
            0,
            smallDailyPlatenGridSectionIndex,
        )
    }, [sectionsForRenderForPreview, smallDailyPlatenGridSectionIndex])

    const sectionsAfterSmallDailyPlatenGrid = useMemo(() => {
        const sections = sectionsForRenderForPreview || []
        if (smallDailyPlatenGridSectionIndex < 0) return []
        return sections.filter((section, idx) => {
            if (idx <= smallDailyPlatenGridSectionIndex) return false
            return !hasSmallDailyPlatenSectionSignature([section])
        })
    }, [sectionsForRenderForPreview, smallDailyPlatenGridSectionIndex])

    const inspectionSectionListForRender = useMemo(() => {
        if (isSpecialFinishMatTemplate) {
            return []
        }
        if (smallDailyPlatenGridSection) {
            return sectionsAfterSmallDailyPlatenGrid
        }
        return sectionsWithoutSmallDailyPlatenGrid
    }, [
        isSpecialFinishMatTemplate,
        smallDailyPlatenGridSection,
        sectionsAfterSmallDailyPlatenGrid,
        sectionsWithoutSmallDailyPlatenGrid,
    ])

    const inspectionSectionListIsHorizontal = useMemo(
        () => shouldForceHorizontalForTemplate,
        [shouldForceHorizontalForTemplate],
    )

    const smallDailyPlatenRows = useMemo(() => {
        const sections = sectionsForRenderForPreview || []
        return sections
            .filter(section => hasSmallDailyPlatenSectionSignature([section]))
            .map(section => {
                const byField = {};
                (section?.Questions || []).forEach(question => {
                    const key =
                        SMALL_DAILY_PLATEN_FIELD_BY_ORIGINAL_ID[
                            normalizeTemplateGuid(question?.QuestionOriginalId)
                        ]
                    if (key) byField[key] = question
                })
                return { section, byField }
            })
    }, [sectionsForRenderForPreview])

    const smallDailyPlatenColumnDefs = useMemo(() => {
        const templateSection = smallDailyPlatenGridSection
        if (!templateSection?.Questions?.length) return []
        const byOrigId = Object.fromEntries(
            (templateSection.Questions || [])
                .filter(question => question?.QuestionOriginalId)
                .map(question => [
                    normalizeTemplateGuid(question.QuestionOriginalId),
                    question,
                ]),
        )
        return SMALL_DAILY_AUDIT_PLATEN_QUESTION_ORIGINAL_IDS.map(origId => {
            const norm = normalizeTemplateGuid(origId)
            const fieldKey = SMALL_DAILY_PLATEN_FIELD_BY_ORIGINAL_ID[norm]
            const question = byOrigId[norm]
            if (!fieldKey || !question) return null
            if (fieldKey === 'platen') {
                return null
            }
            return {
                fieldKey,
                header: String(question.FAQ || '').toUpperCase(),
                placeholder: question.FAQ || '',
                width:
                    SMALL_DAILY_PLATEN_TABLE_COLUMN_WIDTH[fieldKey] ||
                    undefined,
                isMedia: fieldKey === 'media',
                isResult: fieldKey === 'result',
            }
        }).filter(Boolean)
    }, [smallDailyPlatenGridSection])

    const getSingleQuestionText = question =>
        question?.Answers?.SingleResponse?.Response || ''

    const updateSmallDailyPlatenField = (
        sectionId,
        question,
        value,
        { isMultipleResult = false } = {},
    ) => {
        if (!question?.Id || !sectionId) return
        if (isMultipleResult) {
            const updated = setMultipleSelectionByText(question, value)
            onAnswerChanged(sectionId, question, {
                MultipleResponse: updated.Answers.MultipleResponse,
            })
            return
        }
        onAnswerChanged(sectionId, question, value)
    }

    const smallDailyPlatenFieldDisabled = isInspectionReadOnly

    useEffect(() => {
        if (!isPlatenGridTemplate || !platenGridMode) return
        if (!data?.Sections?.length) return
        const gridSections = listPlatenGridSectionsFromAudit(
            data.Sections,
            platenGridMode,
        )
        const fromServer = buildPlatenRowsFromAuditSections(
            gridSections,
            specialPlatenLabels,
        )
        setPlatenRows(prev =>
            mergePlatenServerRowsWithLocalDraft(fromServer, prev),
        )
    }, [
        isPlatenGridTemplate,
        platenGridMode,
        platenGeneralSectionsFingerprint,
        specialPlatenLabels,
    ])

    // Check IsLinkTemplate and fetch previous template data
    useEffect(() => {
        const fetchPreviousTemplate = async () => {
            if (data?.IsLinkTemplate === true && data?.AuditTemplateId) {
                try {
                    const previousData = await TemplateService.getPreviousTemplate(
                        data.AuditTemplateId,
                        data.Id, // TemplateId from inspection response
                        data.CreatedBy, // CreatedBy from inspection response
                        data.CustomerId, // CustomerId from inspection response
                    )
                    if (previousData && previousData !== null) {
                        setPreviousTemplateData(previousData)
                    }
                } catch (e) {
                    console.error('Failed to fetch previous template:', e)
                    setPreviousTemplateData(null)
                }
            } else {
                setPreviousTemplateData(null)
            }
        }

        if (data && !templateLoading) {
            fetchPreviousTemplate()
        }
    }, [data?.IsLinkTemplate, data?.AuditTemplateId, data?.Id, data?.CreatedBy, data?.CustomerId, templateLoading])

    // Fetch imported mappings and add to inspection
    useEffect(() => {
        if (data?.Id) {
            const fetchImportedMappings = async () => {
                try {
                    const mappings = await TemplateService.getMappingsByTarget(
                        data.Id.toString(),
                        true,
                    )

                    // Handle different response structures
                    // Response can be: { Records: [...] } or { Header: {...}, Questions: [...] } or direct array
                    let mappingsQuestions = []
                    if (mappings?.Questions && Array.isArray(mappings.Questions)) {
                        mappingsQuestions = mappings.Questions
                    } else if (mappings?.Records && Array.isArray(mappings.Records)) {
                        mappingsQuestions = mappings.Records
                    } else if (Array.isArray(mappings)) {
                        mappingsQuestions = mappings
                    }

                    // Filter only questions with IsSelected: true
                    const selectedMappingsQuestions = mappingsQuestions.filter(
                        q => q.IsSelected === true,
                    )

                    // Add imported questions to inspection if not already present
                    if (selectedMappingsQuestions.length > 0) {
                        const newData = JSON.parse(JSON.stringify(data))

                        // Find or create "Imported section"
                        let importedSection = newData.Sections?.find(
                            s => s.SectionName === 'Imported section',
                        )

                        if (!importedSection) {
                            importedSection = {
                                SectionId: `imported-section-${Date.now()}`,
                                SectionName: 'Imported section',
                                SectionOrder: (newData.Sections?.length || 0) + 1,
                                Questions: [],
                                IsImported: true, // Flag to identify imported section
                            }
                            if (!newData.Sections) {
                                newData.Sections = []
                            }
                            newData.Sections.push(importedSection)
                        }

                        // Create a Set of existing question IDs in imported section
                        const existingQuestionIds = new Set(
                            importedSection.Questions.map(q => q.QuestionId?.toString() || q.Id?.toString()).filter(Boolean),
                        )

                        // Add questions from Mappings/Target API response (only IsSelected: true)
                        selectedMappingsQuestions.forEach(mappingQuestion => {
                            const questionIdStr = mappingQuestion.QuestionId?.toString()

                            // Skip if question already exists
                            if (existingQuestionIds.has(questionIdStr)) {
                                return
                            }

                            const isMultipleResponse = mappingQuestion.TypeOfResponse === 2
                            const isSingleResponse = mappingQuestion.TypeOfResponse === 1
                            const firstAnswer = mappingQuestion.MultipleAnswers && mappingQuestion.MultipleAnswers.length > 0
                                ? mappingQuestion.MultipleAnswers[0]
                                : null
                            const multiResponseType = firstAnswer?.MultiResponseType || 1

                            // Convert MediaAnswers to MediaResponse format
                            const mediaResponse = mappingQuestion.MediaAnswers && mappingQuestion.MediaAnswers.length > 0
                                ? mappingQuestion.MediaAnswers.map(media => {
                                    const imageUrl = media.SignedURL || media.FileKey
                                    return {
                                        MediaId: media.Unique_Id || media.FAQId,
                                        FileKey: media.FileKey,
                                        FileURL: imageUrl, // Use SignedURL if available, otherwise FileKey
                                        SignedURL: media.SignedURL, // Preserve SignedURL
                                        url: imageUrl, // Add url field for SinglePreview component
                                        FileName: media.FileName,
                                        Caption: media.Caption || '',
                                        Comment: media.Comment || '',
                                        CreatedOn: media.CreatedOn,
                                        ExpiredOn: media.ExpiredOn,
                                    }
                                })
                                : []

                            const inspectionQuestion = {
                                Id: mappingQuestion.QuestionId,
                                QuestionId: mappingQuestion.QuestionId,
                                Question: mappingQuestion.QuestionText,
                                FAQ: mappingQuestion.QuestionText,
                                QuestionResponseId: mappingQuestion.TypeOfResponse,
                                TypeOfResponse: mappingQuestion.TypeOfResponse,
                                MultiResponseType: multiResponseType,
                                DisplayOrder: mappingQuestion.DisplayOrder || mappingQuestion.QuestionOrder || 0,
                                SectionId: importedSection.SectionId,
                                Files: mappingQuestion.Files || [], // Include Files from imported question
                                IsRequired: false,
                                IsReadOnly: true, // Mark as read-only
                                IsImported: true, // Mark as imported
                                Answers: {
                                    SingleResponse: isSingleResponse && mappingQuestion.SingleAnswers && mappingQuestion.SingleAnswers.length > 0
                                        ? {
                                            Response: mappingQuestion.SingleAnswers[0].Response || '',
                                            ResponseId: mappingQuestion.SingleAnswers[0].ResponseId || null,
                                        }
                                        : null,
                                    MultipleResponse: isMultipleResponse && mappingQuestion.MultipleAnswers && mappingQuestion.MultipleAnswers.length > 0
                                        ? mappingQuestion.MultipleAnswers.map(answer => ({
                                            Id: answer.Id,
                                            DisplayText: answer.DisplayText,
                                            IsSelected: answer.IsSelected || false,
                                            BackColorCode: answer.BackColorCode || '#808080',
                                            ForeColorCode: answer.ForeColorCode || '#FFFFFF',
                                            Score: answer.Score || 0,
                                            DisplayOrder: answer.DisplayOrder || 0,
                                            IsExcludedFromScoring: answer.IsExcludedFromScoring || false,
                                            MultiResponseType: answer.MultiResponseType || multiResponseType,
                                        }))
                                        : [],
                                    MediaResponse: mediaResponse, // Include MediaAnswers as MediaResponse
                                    IsAnswered: isMultipleResponse
                                        ? (mappingQuestion.MultipleAnswers || []).some(a => a.IsSelected)
                                        : isSingleResponse && mappingQuestion.SingleAnswers && mappingQuestion.SingleAnswers.length > 0 && mappingQuestion.SingleAnswers[0].Response,
                                },
                            }

                            importedSection.Questions.push(inspectionQuestion)
                            existingQuestionIds.add(questionIdStr)
                        })

                        // Remove questions from imported section that are no longer selected (IsSelected: false)
                        if (importedSection.Questions && importedSection.Questions.length > 0) {
                            const selectedQuestionIds = new Set(
                                selectedMappingsQuestions.map(q => q.QuestionId?.toString()).filter(Boolean),
                            )
                            importedSection.Questions = importedSection.Questions.filter(
                                q => {
                                    const qId = q.QuestionId?.toString() || q.Id?.toString()
                                    // Keep question if it's in the selected list OR if it's not an imported question
                                    return selectedQuestionIds.has(qId) || !q.IsImported
                                },
                            )
                        }

                        // Sort sections to put Imported section first
                        if (newData.Sections && newData.Sections.length > 0) {
                            newData.Sections.sort((a, b) => {
                                const aIsImported = a.SectionName === 'Imported section' || a.IsImported
                                const bIsImported = b.SectionName === 'Imported section' || b.IsImported
                                if (aIsImported && !bIsImported) return -1
                                if (!aIsImported && bIsImported) return 1
                                return (a.SectionOrder || 0) - (b.SectionOrder || 0)
                            })
                        }

                        // Only update if we have questions or need to remove questions
                        setData(newData)
                        setChanged(c => c + 1)
                    } else {
                        // If no selected questions, remove imported section if it exists
                        const newData = JSON.parse(JSON.stringify(data))
                        if (newData.Sections && newData.Sections.length > 0) {
                            const importedSectionIndex = newData.Sections.findIndex(
                                s => s.SectionName === 'Imported section' || s.IsImported,
                            )
                            if (importedSectionIndex !== -1) {
                                // Remove only if it has no questions or all questions are imported
                                const importedSection = newData.Sections[importedSectionIndex]
                                if (importedSection.Questions && importedSection.Questions.length > 0) {
                                    // Remove only imported questions, keep non-imported ones
                                    importedSection.Questions = importedSection.Questions.filter(
                                        q => !q.IsImported,
                                    )
                                    // If no questions left, remove the section
                                    if (importedSection.Questions.length === 0) {
                                        newData.Sections.splice(importedSectionIndex, 1)
                                    }
                                } else {
                                    newData.Sections.splice(importedSectionIndex, 1)
                                }
                                setData(newData)
                                setChanged(c => c + 1)
                            }
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch imported mappings:', error)
                }
            }
            fetchImportedMappings()
        }
    }, [data?.Id])

    // Add effect to check inspection status when data loads
    useEffect(() => {
        if (data?.Status === 5) {
            setIsCompleted(true)
            // Don't show completion message for revisiting completed inspections
            if (!publicScreen) {
                setShowCompletionMessage(false)
            }
        }
    }, [data?.Status, publicScreen])

    // CrossUserAccess: sync read-only from GET /Inspection payload
    useEffect(() => {
        if (!data || publicScreen) return
        if (isAuditReadOnly(data)) {
            setCrossUserReadOnlyForced(true)
            if (getAuditReadOnlyReason(data) === 'COMPLETED_LOCKED') {
                setPostCompleteEditBlocked(true)
            }
        } else {
            setCrossUserReadOnlyForced(false)
        }
    }, [data?.Id, data?.IsReadOnly, data?.ReadOnlyReason, publicScreen])

    // Collection #1 / #7: load TemplateSetting when audit loads to preemptively
    // block post-complete edits (IsPostCompleteEditAvailable=false).
    useEffect(() => {
        if (publicScreen || templateLoading || !data) {
            return undefined
        }

        let cancelled = false
        const loadTemplateSetting = async () => {
            const templateSettingId = getTemplateSettingIdFromAudit(data)
            if (!templateSettingId) {
                if (!cancelled) setPostCompleteEditBlocked(false)
                return
            }
            try {
                const setting = await TemplateService.getTemplateSetting(
                    templateSettingId,
                )
                if (!cancelled) {
                    setPostCompleteEditBlocked(
                        isPostCompleteEditBlocked(setting),
                    )
                }
            } catch (error) {
                // 404 / null config => feature dormant => edits allowed
                if (!cancelled) setPostCompleteEditBlocked(false)
            }
        }

        loadTemplateSetting()
        return () => {
            cancelled = true
        }
    }, [
        publicScreen,
        templateLoading,
        data?.OriginalTemplateId,
        data?.Id,
        data?.AuditTemplateId,
    ])

    useEffect(() => {
        return () => {
            if (skipUnmountSaveRef.current) {
                skipUnmountSaveRef.current = false
                return
            }
            // Only save on unmount if it's not a completed inspection
            // For company 34930, don't save in preview mode (interactive preview)
            // Do not depend on data?.Status here: save() sets data.Status then setSaving(false),
            // which would re-fire this effect and run cleanup → duplicate saveInspection before navigation.
            if (
                !isCompletedRef.current &&
                auditStatusRef.current !== 5 &&
                !(isPreview && companyId === 34930)
            ) {
                save(2, false)
            }
        }
    }, [isCompleted, isPreview, companyId])

    // usePrompt(
    //     'Would you like to save your changes before leaving this page?',
    //     changed > 0 && !isCompleted && data?.Status !== 5 && !suppressPrompt && !(isPreview && companyId === 34930),
    //     confirmed => {
    //         if (confirmed) {
    //             save(2, false)
    //         } else {
    //             navigation(+1)
    //         }
    //     },
    // )

    useEffect(() => {
        setTimeout(() => {
            if (tempFlag) {
                // save(2, false)
            }
            setTempFlag(true)
        }, 250)
    }, [tempstate, data])

    const onBack = () => {
        navigation(-1)
    }

    useEffect(() => {
        const result = {}

        if (data?.Sections?.length) {
            data.Sections.forEach(section => {
                section.Questions.forEach(item => {
                    if (!showValidation) {
                        return
                    }
                    const validationError = getRequiredQuestionValidationError(item)
                    if (validationError) {
                        result[item.Id] = validationError
                    }
                })
            })
        }

        if (showValidation && isSpecialPlatenTemplate && data) {
            Object.assign(
                result,
                buildSpecialPlatenCompletionErrors(
                    platenRows,
                    specialPressQuestion,
                    data,
                ),
            )
        }

        console.log('VALIDATION RESULT:', result)
        setErrors(result)
    }, [
        data,
        showValidation,
        isSpecialPlatenTemplate,
        platenRows,
        specialPressQuestion,
    ])

    // Synchronous validation function
    const validateFormSync = (
        allowedSectionIds = null,
        auditSnapshot = null,
    ) => {
        const result = {}
        const allowedSet =
            Array.isArray(allowedSectionIds) && allowedSectionIds.length > 0
                ? new Set(allowedSectionIds)
                : null

        const audit = auditSnapshot || data

        if (audit?.Sections?.length) {
            audit.Sections.forEach(section => {
                if (allowedSet && !allowedSet.has(section.SectionId)) {
                    return
                }
                // Sections marked N/A (IsNAAvailable + selected) skip required validation
                if (
                    isSectionNAAvailable(section) &&
                    (section?.IsNASelected || section?.isNASelected)
                ) {
                    return
                }
                section.Questions.forEach(item => {
                    const errKey = makePlatenCopiedQuestionErrorKey(section, item)
                    const validationError = getRequiredQuestionValidationError(item)
                    if (validationError) {
                        result[errKey] = validationError
                    }
                })
            })
        }

        if (isSpecialPlatenTemplate && audit) {
            Object.assign(
                result,
                buildSpecialPlatenCompletionErrors(
                    platenRows,
                    specialPressQuestion,
                    audit,
                ),
            )
        }

        if (isSpecialCustomerClaimTemplate) {
            Object.assign(
                result,
                buildCustomerClaimCompletionErrors(
                    claimRows,
                    claimSelectedSupplier,
                    claimPortalDraft,
                ),
            )
        }

        console.log('SYNC VALIDATION RESULT:', result)
        return result
    }

    const validateForComplete = (selectedSectionIds = null) => {
        setShowValidation(true)

        let auditForValidate = null
        if (isPlatenGridTemplate && data) {
            auditForValidate = buildPlatenGridPayload(
                JSON.parse(JSON.stringify(data)),
            )
        } else if (isSpecialCustomerClaimTemplate && data) {
            auditForValidate = buildCustomerClaimPayloadForAudit(
                JSON.parse(JSON.stringify(data)),
            )
        }

        const validationErrors = validateFormSync(
            selectedSectionIds,
            auditForValidate,
        )
        if (Object.keys(validationErrors).length > 0) {
            console.log('Validation failed - cannot complete:', validationErrors)
            setErrors(validationErrors)
            notifyCompletionValidationError(validationErrors)
            return false
        }
        return true
    }

    const submit = async (status = StatusType.Save, options = {}) => {
        // CrossUserAccess / completed lock: block writes in view-only mode
        if (auditAccessReadOnly) {
            errorToast(
                auditReadOnlyBanner ||
                    getAuditReadOnlyMessage({
                        reason: auditReadOnlyReason,
                        ownerName: auditOwnerName,
                    }),
            )
            return
        }
        // Prevent submitting if inspection is completed (unless reopen + allowed)
        if (isCompletedAudit && !canEditCompletedInspection) {
            if (postCompleteEditBlocked) {
                errorToast(POST_COMPLETE_EDIT_DISABLED_MESSAGE)
            }
            return
        }

        console.log('Submit called with status:', status)

        // Validate all required fields only for completion.
        // Save & Close should allow partial progress and still persist status=Save.
        if (status === StatusType.Completed) {
            completionSelectedSectionIdsRef.current =
                options?.selectedSectionIds || null
            if (
                !validateForComplete(completionSelectedSectionIdsRef.current)
            ) {
                return // Don't allow completion with validation errors
            }
        }

        try {
            // Always show loader while saving (including Save & Close)
            const result = await save(status, true)
            // Only navigate if save was successful and not completing
            if (result && status === StatusType.Save) {
                if (publicScreen) {
                    // For public inspection, show completion message
                    setCompletionMessage(
                        'Thank you! Your inspection has been successfully completed.',
                    )
                    setShowCompletionMessage(true)
                } else {
                    // Show a neat success message on the side
                    successToast('Inspection saved successfully')
                    // Prevent the unsaved-changes prompt from showing during programmatic navigation
                    // setSuppressPrompt(true)
                    // Reset change counter so the prompt condition is false
                    setChanged(0)
                    // Unmount cleanup would POST again — skip that duplicate save
                    skipUnmountSaveRef.current = true
                    navigation(-1)
                }
            }
        } catch (err) {
            console.error('Submit Error:', err)
            setError(ErrorConstant.default)
        }
    }

    const updateSpecialPressSelection = selectedValue => {
        if (!data?.Sections?.length) return
        const targetOid = String(SPECIAL_PLATEN_PRESS_QUESTION_ORIGINAL_ID || '')
            .trim()
            .toLowerCase()
        const pressId = specialPressQuestion?.Id

        const nextSections = data.Sections.map(section => ({
            ...section,
            Questions: (section.Questions || []).map(question => {
                const oid = String(question?.QuestionOriginalId || '')
                    .trim()
                    .toLowerCase()
                const matchesId =
                    pressId != null &&
                    String(question?.Id) === String(pressId)
                const matchesOid = Boolean(targetOid) && oid === targetOid
                if (!matchesId && !matchesOid) {
                    return question
                }
                return setMultipleSelectionByText(question, selectedValue)
            }),
        }))
        setData({
            ...data,
            Sections: nextSections,
        })
        setChanged(prev => prev + 1)
    }

    const clearSpecialPlatenGrid = () => {
        const clearedRows = specialPlatenLabels.map(platenLabel => ({
            platenLabel: String(platenLabel || '').trim(),
            mold: '',
            pressure: '',
            temp: '',
            result: '',
            comments: '',
            mediaFiles: [],
        }))
        setPlatenRows(clearedRows)
        if (isSpecialPlatenTemplate) {
            updateSpecialPressSelection('')
        }
    }

    const updateSpecialRowField = (rowIndex, key, value) => {
        setPlatenRows(prev =>
            prev.map((row, idx) =>
                idx === rowIndex
                    ? {
                        ...row,
                        [key]: value,
                    }
                    : row,
            ),
        )
        setChanged(prev => prev + 1)
    }

    const addSpecialRowMedia = async (rowIndex, files) => {
        if (!Array.isArray(files) || files.length === 0) return
        const row = platenRows[rowIndex]
        if (!row) return

        let uploadedFiles = files
        let sectionsForUpload = data?.Sections || []
        let rowMatch = findPlatenGridSectionForRow(
            sectionsForUpload,
            platenGridMode,
            row,
            rowIndex,
        )

        if (
            !rowMatch.section &&
            isPlatenGridTemplate &&
            platenGridMode &&
            data?.Id &&
            id &&
            !publicScreen
        ) {
            const currentGridCount = listPlatenGridSectionsFromAudit(
                sectionsForUpload,
                platenGridMode,
            ).length
            try {
                const afterEnsure =
                    await ensurePlatenGridServerSectionsLoaded({
                        audit: data,
                        platenRows,
                        routeAuditId: id,
                        auditNumericId: data.Id,
                        forcedMinGeneralCount: currentGridCount + 1,
                        gridMode: platenGridMode,
                    })
                sectionsForUpload = mergeAuditSectionsAfterPlatenEnsure(
                    data.Sections,
                    afterEnsure.Sections,
                    platenGridMode,
                )
                setData({
                    ...data,
                    Sections: sectionsForUpload,
                })
                rowMatch = findPlatenGridSectionForRow(
                    sectionsForUpload,
                    platenGridMode,
                    row,
                    rowIndex,
                )
                if (!rowMatch.section) {
                    const list = listPlatenGridSectionsFromAudit(
                        sectionsForUpload,
                        platenGridMode,
                    )
                    if (list.length > currentGridCount) {
                        rowMatch = {
                            section: list[list.length - 1],
                            gridIndex: list.length - 1,
                        }
                    }
                }
            } catch (e) {
                console.error(
                    '[Platen] ensure grid section before media failed:',
                    e,
                )
            }
        }

        const rowSection = rowMatch.section
        const rowTargetQ = rowSection
            ? resolvePlatenRowMediaQuestion(rowSection)
            : null
        const rowSectionId = rowSection?.SectionId ?? rowSection?.Id
        if (rowTargetQ?.Id && rowSectionId) {
            const result = await addMedia(
                rowTargetQ.Id,
                files,
                rowSectionId,
                sectionsForUpload,
            )
            uploadedFiles = Array.isArray(result) ? result : files
        }

        setPlatenRows(prev =>
            prev.map((r, idx) =>
                idx === rowIndex
                    ? {
                        ...r,
                        mediaFiles: [...(r.mediaFiles || []), ...uploadedFiles],
                    }
                    : r,
            ),
        )
        setChanged(prev => prev + 1)
    }

    const resolveSpecialPlatenRowCommentsMediaTarget = rowIndex => {
        const sectionsForUpload = data?.Sections || []
        const row = platenRows[rowIndex]
        const rowMatch = findPlatenGridSectionForRow(
            sectionsForUpload,
            platenGridMode,
            row,
            rowIndex,
        )
        const rowSection = rowMatch.section
        const rowTargetQ = rowSection
            ? resolvePlatenRowMediaQuestion(rowSection)
            : null
        const rowSectionId = rowSection?.SectionId ?? rowSection?.Id
        if (rowTargetQ?.Id && rowSectionId) {
            return {
                questionId: rowTargetQ.Id,
                sectionId: rowSectionId,
                sections: sectionsForUpload,
            }
        }
        return null
    }

    const removeSpecialRowMedia = async (rowIndex, file) => {
        const target = resolveSpecialPlatenRowCommentsMediaTarget(rowIndex)
        let remainingFiles = []

        setPlatenRows(prev =>
            prev.map((row, idx) => {
                if (idx !== rowIndex) return row
                remainingFiles = filterMediaFilesList(
                    row.mediaFiles || [],
                    file,
                )
                return {
                    ...row,
                    mediaFiles: remainingFiles,
                }
            }),
        )

        if (target?.questionId && target?.sectionId) {
            await removeMedia(target.questionId, file, target.sectionId)
        }

        setData(prev =>
            syncSpecialPlatenRowCommentsMediaInAudit(
                prev,
                rowIndex,
                remainingFiles,
                platenGridMode,
                platenRows[rowIndex],
            ),
        )
        setChanged(prev => prev + 1)
    }

    const saveSpecialRowMediaComment = async (rowIndex, mediaItem, meta) => {
        const target = resolveSpecialPlatenRowCommentsMediaTarget(rowIndex)
        if (!target?.questionId || !target?.sectionId) return null

        const hasChanged =
            (meta.Comment ?? '') !== (mediaItem.Comment ?? '') ||
            (meta.Caption ?? '') !== (mediaItem.Caption ?? '')
        if (!hasChanged) return null

        setPlatenRows(prev =>
            prev.map((row, idx) => {
                if (idx !== rowIndex) return row
                return {
                    ...row,
                    mediaFiles: (row.mediaFiles || []).map(f => {
                        const match =
                            (mediaItem.MediaId &&
                                f.MediaId === mediaItem.MediaId) ||
                            (mediaItem.id && f.id === mediaItem.id)
                        return match ? { ...f, ...meta } : f
                    }),
                }
            }),
        )

        try {
            const result = await addMedia(
                target.questionId,
                [{ ...mediaItem, ...meta, local: false }],
                target.sectionId,
                target.sections,
            )
            const updated = result?.[0]
            if (updated) {
                setPlatenRows(prev =>
                    prev.map((row, idx) => {
                        if (idx !== rowIndex) return row
                        return {
                            ...row,
                            mediaFiles: (row.mediaFiles || []).map(f => {
                                const match =
                                    (updated.MediaId &&
                                        f.MediaId === updated.MediaId) ||
                                    (mediaItem.id && f.id === mediaItem.id)
                                return match
                                    ? {
                                          ...f,
                                          ...updated,
                                          Caption: String(
                                              updated?.Caption ?? '',
                                          ).trim()
                                              ? updated.Caption
                                              : meta?.Caption ?? f.Caption ?? '',
                                          Comment: String(
                                              updated?.Comment ?? '',
                                          ).trim()
                                              ? updated.Comment
                                              : meta?.Comment ?? f.Comment ?? '',
                                          id: updated.MediaId || f.id,
                                          url:
                                              updated.FileURL ||
                                              updated.SignedURL ||
                                              f.url,
                                          name: updated.FileKey || f.name,
                                      }
                                    : f
                            }),
                        }
                    }),
                )
            }
            return result
        } catch (e) {
            console.error('Error saving media caption/comment:', e)
            return null
        }
    }

    const buildPlatenGridPayload = auditData => {
        if (!auditData?.Sections?.length) return auditData
        if (!isPlatenGridTemplate || !platenGridMode) return auditData

        const sectionRef = resolvePrimaryPlatenGridSectionFromAudit(
            auditData.Sections,
            platenGridMode,
        )
        if (!sectionRef) return auditData

        const filledRows = getFilledSpecialPlatenRows(platenRows)

        const templateSectionRaw =
            auditData.Sections.find(s => {
                if (platenGridMode === PLATEN_GRID_MODE.SPECIAL_AUDIT) {
                    return isSameSpecialGeneralSection(s, sectionRef)
                }
                return (
                    String(s?.SectionId ?? '') ===
                    String(sectionRef?.SectionId ?? '')
                )
            }) || sectionRef

        const [hydratedTemplate] =
            hydrateInspectionMultipleResponsesFromTemplate(
                templateSectionRaw
                    ? [JSON.parse(JSON.stringify(templateSectionRaw))]
                    : [],
                auditData,
            )
        const templateSection = hydratedTemplate || templateSectionRaw

        const gridSections = listPlatenGridSectionsFromAudit(
            auditData.Sections || [],
            platenGridMode,
        )

        const defaultSectionName =
            platenGridMode === PLATEN_GRID_MODE.DAILY_AUDIT
                ? 'Platen'
                : 'General'

        const buildSyntheticSectionForRow = (row, rowIndex) => {
            const isCopy = rowIndex > 0
            const clone = JSON.parse(JSON.stringify(templateSection))
            const newSectionId = isCopy
                ? generatePlatenCopySectionId()
                : clone.SectionId

            const sectionDisplayName = String(
                templateSection.SectionName || defaultSectionName,
            ).trim()
            clone.SectionName = sectionDisplayName

            if (isCopy) {
                clone.SectionId = newSectionId
                clone.IsCopiedSection = true
                if (clone.Id !== undefined && clone.Id !== null) {
                    clone.Id = 0
                }
            } else {
                clone.IsCopiedSection = false
            }

            const buildQuestionsForOneRow = (
                questionMap,
                r,
                sectionId,
                sectionName,
                copied,
            ) => {
                const questions = []
                const useDedicatedMedia =
                    sectionHasDedicatedPlatenMediaQuestion({
                        Questions: Object.values(questionMap),
                    })
                const pushQ = q => {
                    if (!q) return
                    let next = {
                        ...q,
                        SectionId: sectionId,
                        SectionName: sectionName,
                    }
                    next.IsCopiedSection = copied
                    if (copied) {
                        next = zeroQuestionIdsForCopiedPlatenSection(next)
                        next.SectionId = sectionId
                        next.SectionName = sectionName
                        next.IsCopiedSection = true
                        next = stripPlatenCopiedAnswerInstanceIds(next)
                    }
                    questions.push(next)
                }

                PLATEN_GENERAL_FAQ_ORDER.forEach(({ field }) => {
                    let base = null
                    if (field === 'deck') {
                        base = resolvePlatenDeckQuestion(questionMap)
                    } else if (field === 'platenNumber') {
                        base = resolvePlatenNumberQuestion(questionMap)
                    } else if (field === 'pressure') {
                        base = questionMap.pressure
                    } else if (field === 'temp') {
                        base = questionMap.temp
                    } else if (field === 'result') {
                        base = questionMap.result
                    } else if (field === 'comments') {
                        base = questionMap.comments
                    }
                    if (!base) return
                    if (field === 'deck') {
                        pushQ(setDeckAnswerOnQuestion(base, r.platenLabel))
                    } else if (field === 'platenNumber') {
                        pushQ(setSingleResponseText(base, r.mold))
                    } else if (field === 'pressure') {
                        pushQ(setSingleResponseText(base, r.pressure))
                    } else if (field === 'temp') {
                        pushQ(setSingleResponseText(base, r.temp))
                    } else if (field === 'result') {
                        pushQ(
                            setMultipleSelectionByText(
                                base,
                                normalizePlatenResultDisplayFromQuestion(
                                    r.result,
                                    base,
                                ),
                            ),
                        )
                    } else if (field === 'comments') {
                        let commentFiles = []
                        if (!useDedicatedMedia) {
                            commentFiles = Array.isArray(r.mediaFiles)
                                ? r.mediaFiles
                                : []
                        }
                        const textQ = setSingleResponseText(base, r.comments)
                        pushQ({
                            ...textQ,
                            Files: commentFiles,
                            Answers: {
                                ...(textQ.Answers || {}),
                                MediaResponse: commentFiles,
                            },
                            IsAnswered:
                                Boolean(r.comments?.trim()) ||
                                commentFiles.length > 0,
                        })
                    }
                })

                const mediaBase = questionMap.media
                if (mediaBase) {
                    pushQ(applyPlatenMediaToQuestion(mediaBase, r.mediaFiles))
                }

                return questions
            }

            const qMap = buildQuestionMapByFaq(clone.Questions)
            clone.Questions = buildQuestionsForOneRow(
                qMap,
                row,
                newSectionId,
                sectionDisplayName,
                isCopy,
            )
            return clone
        }

        let mergedGridSections = []
        if (filledRows.length === 0) {
            if (gridSections[0]) {
                mergedGridSections = [
                    {
                        ...JSON.parse(JSON.stringify(gridSections[0])),
                        Questions: [],
                    },
                ]
            } else if (templateSection) {
                mergedGridSections = [
                    {
                        ...JSON.parse(JSON.stringify(templateSection)),
                        Questions: [],
                    },
                ]
            }
        } else {
            const assignedSectionIds = new Set()
            mergedGridSections = filledRows.map((row, rowIndex) => {
                const deckMatch = findPlatenGridSectionForRow(
                    auditData.Sections,
                    platenGridMode,
                    row,
                )
                if (
                    deckMatch.section &&
                    !assignedSectionIds.has(
                        String(deckMatch.section.SectionId ?? ''),
                    )
                ) {
                    assignedSectionIds.add(
                        String(deckMatch.section.SectionId ?? ''),
                    )
                    return mergePlatenRowIntoServerGeneralSection(
                        deckMatch.section,
                        row,
                    )
                }

                const unassigned = gridSections.find(
                    s =>
                        !assignedSectionIds.has(String(s?.SectionId ?? '')),
                )
                if (unassigned) {
                    assignedSectionIds.add(String(unassigned.SectionId ?? ''))
                    return mergePlatenRowIntoServerGeneralSection(
                        unassigned,
                        row,
                    )
                }

                return buildSyntheticSectionForRow(row, rowIndex)
            })
        }

        const nextSections = []
        let gridReplaced = false
        auditData.Sections.forEach(section => {
            if (!isPlatenGridSectionEntry(section, platenGridMode)) {
                nextSections.push(section)
                return
            }
            if (!gridReplaced) {
                mergedGridSections.forEach(block => {
                    nextSections.push(block)
                })
                gridReplaced = true
            }
        })

        return {
            ...auditData,
            Sections: nextSections,
        }
    }

    const buildInspectionAuditPayload = useCallback(
        auditData => {
            if (!auditData) return auditData
            let payload = JSON.parse(JSON.stringify(auditData))
            if (isPlatenGridTemplate) {
                payload = buildPlatenGridPayload(payload)
            } else if (isSpecialCustomerClaimTemplate) {
                payload = buildCustomerClaimPayloadForAudit(payload)
            }
            return applyInspectionScoreTotals(payload)
        },
        [
            isPlatenGridTemplate,
            isSpecialCustomerClaimTemplate,
            buildCustomerClaimPayloadForAudit,
        ],
    )

    const getInspectionReviewBasePath = useCallback(() => {
        if (location.pathname.includes('/customer-defect/')) {
            return '/app/customer-defect'
        }
        return '/app/templates'
    }, [location.pathname])

    const handleInspectionReview = useCallback(() => {
        try {
            if (!data?.AuditTemplateId) {
                errorToast(
                    'Unable to open review page. Please try again.',
                )
                return
            }

            const auditPayload = buildInspectionAuditPayload(data)

            flushSync(() => {
                setChanged(0)
            })
            skipUnmountSaveRef.current = true

            const reviewDraftKey = `inspection-review-payload-${data.AuditTemplateId}`
            sessionStorage.setItem(
                reviewDraftKey,
                JSON.stringify(auditPayload),
            )

            const reviewBase = getInspectionReviewBasePath()
            navigation(
                `${reviewBase}/inspection/review/${data.AuditTemplateId}`,
                {
                    state: {
                        auditPayload,
                    },
                },
            )
        } catch (err) {
            console.error('Review Error:', err)
            errorToast(
                'Unable to open review page. Please try again.',
            )
        }
    }, [
        data,
        buildInspectionAuditPayload,
        errorToast,
        getInspectionReviewBasePath,
        navigation,
    ])

    // status: save/complete code
    // flag: whether this function should show a loader itself
    // manageSaving: whether this function should control turning loader on/off
    const save = async (status, flag = true, manageSaving = true) => {
        // Suppress saving for interactive preview (Company 34930)
        if (isPreview && companyId === 34930) {
            console.log('Saving skipped for interactive preview (Company 34930)')
            return true
        }

        if (auditAccessReadOnly) {
            if (!manageSaving) return null
            errorToast(
                auditReadOnlyBanner ||
                    getAuditReadOnlyMessage({
                        reason: auditReadOnlyReason,
                        ownerName: auditOwnerName,
                    }),
            )
            return null
        }

        if (
            (isCompleted || data?.Status === 5) &&
            (!isReopened || postCompleteEditBlocked)
        ) {
            if (postCompleteEditBlocked) {
                errorToast(POST_COMPLETE_EDIT_DISABLED_MESSAGE)
            }
            return null
        }

        if (saveInProgressRef.current) {
            console.log('[Inspection save] Skipped — save already in progress')
            return null
        }
        saveInProgressRef.current = true

        console.log('Saving Inspection Data:', {
            status,
            currentData: data,
            errors,
            isPublic: publicScreen,
            flag,
            isCompleted,
        })

        try {
            if (manageSaving) {
                setSaving(flag)
                // Allow the loader to render before heavy work starts
                await new Promise(resolve => setTimeout(resolve, 0))
            }

            // For completion (status 5), validate all required fields synchronously
            if (status === StatusType.Completed || status === 5) {
                setShowValidation(true) // Show validation messages

                let auditForValidate = null
                if (isPlatenGridTemplate && data) {
                    auditForValidate = buildPlatenGridPayload(
                        JSON.parse(JSON.stringify(data)),
                    )
                } else if (isSpecialCustomerClaimTemplate && data) {
                    auditForValidate = buildCustomerClaimPayloadForAudit(
                        JSON.parse(JSON.stringify(data)),
                    )
                }

                const validationErrors = validateFormSync(
                    completionSelectedSectionIdsRef.current,
                    auditForValidate,
                )
                if (Object.keys(validationErrors).length > 0 || !data) {
                    console.log('Validation failed - cannot complete:', validationErrors)
                    setErrors(validationErrors)
                    notifyCompletionValidationError(validationErrors)
                    setSaving(false)
                    return null
                }
                // Reset changed state when completing to prevent save prompt
                setChanged(0)
            }

            let auditSnapshot = data
            /**
             * Add server General sections via CopySection whenever filled rows exceed
             * existing sections (autosave + Save + Complete). `ensure` does not call
             * setData — only post-save GET refreshes React so the grid is not cleared mid-edit.
             * Internal saves (`manageSaving === false`, e.g. copy-section flow) skip ensure.
             */
            const platenFilledCount = getFilledSpecialPlatenRows(platenRows).length
            const platenServerGridCount = listPlatenGridSectionsFromAudit(
                data?.Sections || [],
                platenGridMode,
            ).length
            const shouldEnsurePlatenSections =
                isPlatenGridTemplate &&
                platenGridMode &&
                !publicScreen &&
                data?.Id &&
                id &&
                manageSaving !== false &&
                platenFilledCount > platenServerGridCount &&
                platenFilledCount > 0

            if (shouldEnsurePlatenSections) {
                try {
                    const afterEnsure =
                        await ensurePlatenGridServerSectionsLoaded({
                            audit: data,
                            platenRows,
                            routeAuditId: id,
                            auditNumericId: data.Id,
                            gridMode: platenGridMode,
                        })
                    auditSnapshot = {
                        ...data,
                        Sections: mergeAuditSectionsAfterPlatenEnsure(
                            data.Sections,
                            afterEnsure.Sections,
                            platenGridMode,
                        ),
                    }
                } catch (ensureErr) {
                    console.error(
                        '[Platen] ensure server General sections failed:',
                        ensureErr,
                    )
                    if (flag) {
                        errorToast(
                            'Could not add an extra Platen block for save. Please try again.',
                        )
                    }
                    setSaving(false)
                    return null
                }
            }

            const claimFilledCount = claimLoggedRows.length
            const claimServerCount = listClaimPortalSectionsFromAudit(
                data?.Sections || [],
            ).length
            const shouldEnsureClaimSections =
                isSpecialCustomerClaimTemplate &&
                !publicScreen &&
                data?.Id &&
                id &&
                manageSaving !== false &&
                claimFilledCount > claimServerCount &&
                claimFilledCount > 0

            if (shouldEnsureClaimSections) {
                try {
                    const afterEnsure =
                        await ensureCustomerClaimServerSectionsLoaded({
                            audit: data,
                            claimRows,
                            routeAuditId: id,
                            auditNumericId: data.Id,
                        })
                    auditSnapshot = afterEnsure
                } catch (ensureErr) {
                    console.error(
                        '[Customer Claim] ensure portal sections failed:',
                        ensureErr,
                    )
                    if (flag) {
                        errorToast(
                            'Could not add an extra complaint entry for save. Please try again.',
                        )
                    }
                    setSaving(false)
                    return null
                }
            }

            let dataForSave = {
                ...auditSnapshot,
                // Prefer explicit status arg; never keep a stale Status from the snapshot
                Status:
                    status != null
                        ? status
                        : auditSnapshot?.Status ?? null,
            }
            // Down must always post as 8
            if (status === StatusType.Down || status === 8) {
                dataForSave.Status = 8
            }
            // Show-as-dropdown: on Complete, only persist sections the user selected
            // (selectedSectionIds is used for UI + validation, but was never applied to the save payload).
            const selectedIdsForComplete =
                (status === StatusType.Completed || status === 5) &&
                Array.isArray(completionSelectedSectionIdsRef.current) &&
                completionSelectedSectionIdsRef.current.length > 0
                    ? new Set(completionSelectedSectionIdsRef.current)
                    : null
            if (
                selectedIdsForComplete &&
                Array.isArray(dataForSave?.Sections)
            ) {
                dataForSave = {
                    ...dataForSave,
                    Sections: dataForSave.Sections.filter(section =>
                        selectedIdsForComplete.has(section.SectionId),
                    ),
                }
            }
            // Persist IsNASelected for sections that support N/A
            if (Array.isArray(dataForSave?.Sections)) {
                dataForSave = {
                    ...dataForSave,
                    Sections: dataForSave.Sections.map(section => {
                        if (!isSectionNAAvailable(section)) {
                            return section
                        }
                        return {
                            ...section,
                            IsNASelected: !!section.IsNASelected,
                        }
                    }),
                }
            }
            if (isPlatenGridTemplate) {
                dataForSave = buildPlatenGridPayload(dataForSave)
            } else if (isSpecialCustomerClaimTemplate) {
                dataForSave = buildCustomerClaimPayloadForAudit(dataForSave)
            }
            dataForSave = applyInspectionScoreTotals(dataForSave)
            dataForSave = omitFormulaAnswerValues(dataForSave)
            if (dataForSave?.Id) {
                console.groupCollapsed(
                    `[Inspection save] Payload → ${publicScreen ? 'publicInspection' : 'saveInspection'} (AuditId=${dataForSave.Id}, Status=${status})`,
                )
                console.log(
                    `Special Platen (${SPECIAL_PLATEN_TEMPLATE_ID}) transform:`,
                    isPlatenGridTemplate,
                )
                try {
                    console.log(JSON.stringify(dataForSave, null, 2))
                } catch (serializeErr) {
                    console.warn(
                        '[Inspection save] Payload not fully JSON-serializable; falling back to object',
                        serializeErr,
                    )
                    console.log(dataForSave)
                }
                console.groupEnd()
                if (publicScreen) {
                    // For public inspection, always use submitPublicInspection with the same endpoint
                    const publicSaved = await submitPublicInspection(dataForSave)
                    const publicFormulas = readServerFormulaList(publicSaved)
                    if (publicFormulas.length) {
                        setData(prev =>
                            applyServerFormulaResults(prev, publicFormulas),
                        )
                    }
                    // If this is a completion (status 5), call the complete endpoint
                    if (status === 5) {
                        const response =
                            await InspectionService.completePublicInspection(
                                dataForSave.AuditTemplateId,
                            )
                        if (response.success) {
                            // Show success message
                            setCompletionMessage(response.message)
                            setShowCompletionMessage(true)
                            setIsCompleted(true)
                            return false
                        }
                    }
                } else {
                    // Private inspection: POST full audit to Inspection (Save & Close and Complete)
                    const savedInspection = await InspectionService.saveInspection(dataForSave)
                    const savedFormulas = readServerFormulaList(savedInspection)
                    if (savedFormulas.length) {
                        setData(prev =>
                            applyServerFormulaResults(
                                prev,
                                savedFormulas,
                            ),
                        )
                    }
                    if (
                        isSpecialCustomerClaimTemplate &&
                        !publicScreen &&
                        reportClaimSectionEditsRef.current
                    ) {
                        try {
                            await reportClaimSectionEditsRef.current({
                                beforeAudit: claimLastSavedPayloadRef.current,
                                afterAudit: dataForSave,
                            })
                        } catch (sectionEditErr) {
                            console.warn(
                                '[Customer Claim] SectionEdit report failed:',
                                sectionEditErr,
                            )
                        }
                    }
                    if (isSpecialCustomerClaimTemplate && !publicScreen) {
                        claimLastSavedPayloadRef.current = JSON.parse(
                            JSON.stringify(dataForSave),
                        )
                    }
                    const shouldSyncAfterSave =
                        flag &&
                        (isPlatenGridTemplate ||
                            isSpecialCustomerClaimTemplate) &&
                        !publicScreen &&
                        id
                    if (shouldSyncAfterSave) {
                        try {
                            const synced = await InspectionService.getAudit(
                                String(id),
                            )
                            if (synced) {
                                setData(synced)
                            }
                        } catch (syncErr) {
                            console.warn(
                                '[Inspection] post-save getAudit skipped:',
                                syncErr,
                            )
                        }
                    }
                    if (
                        status === StatusType.Completed ||
                        status === 5
                    ) {
                        skipUnmountSaveRef.current = true
                        isCompletedRef.current = true
                        auditStatusRef.current = 5
                        setIsCompleted(true)
                        const reportBase = location.pathname.includes(
                            '/customer-defect/',
                        )
                            ? '/app/customer-defect'
                            : '/app/templates'
                        navigation(
                            `${reportBase}/inspection/report/${dataForSave.AuditTemplateId}`,
                        )
                        return false
                    }
                }
            }
            if (flag && manageSaving) {
                setSaving(false)
            }
            return true
        } catch (err) {
            console.error('Save Error:', err)
            const accessError = parseAuditAccessError(err)
            if (accessError.isAuditAccessError) {
                const isCompletedLock =
                    accessError.code === 'POST_COMPLETE_EDIT_DISABLED' ||
                    accessError.readOnlyReason === 'COMPLETED_LOCKED'
                // Customer Claim templates skip CrossUserAccess owner-lock.
                if (!isCompletedLock && isCrossUserAccessExemptAudit(data)) {
                    return null
                }
                setCrossUserReadOnlyForced(true)
                if (isCompletedLock) {
                    setPostCompleteEditBlocked(true)
                }
                // Toast already shown by BaseService when auditAccessHandled
                if (!err?.auditAccessHandled && accessError.message) {
                    errorToast(accessError.message)
                }
                return null
            }
            const postCompleteError = parsePostCompleteEditDisabledError(err)
            if (postCompleteError.isDisabled) {
                setPostCompleteEditBlocked(true)
                if (!err?.auditAccessHandled) {
                    errorToast(postCompleteError.message)
                }
                return null
            }
            setError(ErrorConstant.default)
            return null
        } finally {
            saveInProgressRef.current = false
            completionSelectedSectionIdsRef.current = null
            if (manageSaving) {
                setSaving(false)
            }
        }
    }

    // Auto-save interval logic: using a ref to always access the latest save function
    // without causing the interval to clear and reset on every render/data change.
    const saveRef = useRef(save)
    useEffect(() => {
        saveRef.current = save
    }, [save])

    useEffect(() => {
        let intervalId = null

        if (isAutoSave) {
            console.log('Auto-save enabled, starting 5-minute interval')
            intervalId = setInterval(() => {
                console.log('Auto-saving inspection...')
                if (saveRef.current) {
                    // flag=false: no loader; manageSaving=false: no post-save getAudit (keeps form state)
                    saveRef.current(2, false, false)
                }
            }, 60 * 1000) // 5 minutes interval
        }

        return () => {
            if (intervalId) {
                console.log('Clearing auto-save interval')
                clearInterval(intervalId)
            }
        }
    }, [isAutoSave])

    // Load questions from PreviousTemplate API response
    const loadMappingQuestions = async () => {
        if (!data?.AuditTemplateId || !previousTemplateData) {
            return
        }

        setLoadingMappings(true)
        try {
            // Use Questions array directly from PreviousTemplate API response
            const questionsList = []
            const preSelectedQuestions = []
            if (previousTemplateData?.Questions && Array.isArray(previousTemplateData.Questions)) {
                previousTemplateData.Questions.forEach(question => {
                    questionsList.push({
                        QuestionId: question.QuestionId,
                        QuestionText: question.QuestionText,
                        SectionName: question.SectionName,
                        SectionId: question.SectionId,
                        DisplayOrder: question.DisplayOrder,
                        QuestionOrder: question.QuestionOrder,
                        TypeOfResponse: question.TypeOfResponse,
                        SingleAnswers: question.SingleAnswers || [],
                        MultipleAnswers: question.MultipleAnswers || [],
                        InspectionId: question.InspectionId,
                        IsSelected: question.IsSelected || false,
                        MappingId: question.MappingId || 0, // Include MappingId from PreviousTemplate API
                    })

                    // Pre-select questions that have IsSelected: true
                    if (question.IsSelected === true) {
                        preSelectedQuestions.push(question.QuestionId)
                    }
                })
            }

            setMappingQuestions(questionsList)
            setSelectedQuestions(preSelectedQuestions)
        } catch (e) {
            console.error('Failed to load questions:', e)
            setMappingQuestions([])
        } finally {
            setLoadingMappings(false)
        }
    }

    // Handle question selection toggle
    const handleQuestionToggle = questionId => {
        setSelectedQuestions(prev => {
            if (prev.includes(questionId)) {
                return prev.filter(id => id !== questionId)
            }
            return [...prev, questionId]
        })
    }

    // Handle select all
    const handleSelectAll = () => {
        if (selectedQuestions.length === mappingQuestions.length) {
            setSelectedQuestions([])
        } else {
            setSelectedQuestions(mappingQuestions.map(q => q.QuestionId))
        }
    }

    // Save selected questions to inspection
    const handleSaveDataImport = async () => {
        if (selectedQuestions.length === 0) {
            return
        }

        setLoadingMappings(true)
        try {
            // Get TargetTemplateUniqueId from inspection API response (data.Id)
            const targetTemplateUniqueId = data?.Id

            if (!targetTemplateUniqueId) {
                setError('Unable to get template ID from inspection data')
                return
            }

            // Get SourceQuestionUniqueId as semicolon-separated string of QuestionIds
            const selectedQuestionsData = mappingQuestions.filter(q =>
                selectedQuestions.includes(q.QuestionId),
            )
            const sourceQuestionUniqueId = selectedQuestionsData
                .map(q => q.QuestionId)
                .join(';')

            // Get all MappingIds from selected questions as semicolon-separated string
            // Order should match the SourceQuestionUniqueId order
            const mappingIdsString = selectedQuestionsData
                .map(q => q.MappingId || 0)
                .join(';')

            // Get SourceTemplateUniqueId from PreviousTemplate API response (Header.InspectionId)
            const sourceTemplateUniqueId = previousTemplateData?.Header?.InspectionId || 0

            if (!sourceTemplateUniqueId) {
                setError('Unable to get source template ID from previous template data')
                setLoadingMappings(false)
                return
            }

            // Call Mapping API to save the mapping
            await TemplateService.saveMapping({
                MappingId: mappingIdsString, // Semicolon-separated MappingIds (e.g., "123;456;0")
                SourceTemplateUniqueId: sourceTemplateUniqueId, // InspectionId from PreviousTemplate API Header
                SourceQuestionUniqueId: sourceQuestionUniqueId, // Semicolon-separated QuestionIds
                TargetTemplateUniqueId: targetTemplateUniqueId, // Inspection ID
                IsAutoPopulate: true, // Changed to true
                IsActive: true,
            })

            // After saving mapping, fetch updated mappings to get imported questions
            const mappingsResponse = await TemplateService.getMappingsByTarget(
                targetTemplateUniqueId.toString(),
                true,
            )

            // Handle different response structures
            // Response can be: { Records: [...] } or { Header: {...}, Questions: [...] } or direct array
            let mappingsList = []
            let mappingsQuestions = []
            if (mappingsResponse?.Records && Array.isArray(mappingsResponse.Records)) {
                mappingsList = mappingsResponse.Records
            } else if (mappingsResponse?.Questions && Array.isArray(mappingsResponse.Questions)) {
                // If response has Questions array, use those questions (they have MediaAnswers)
                mappingsQuestions = mappingsResponse.Questions.filter(q => q.IsSelected === true)
                mappingsList = mappingsResponse.Questions
            } else if (Array.isArray(mappingsResponse)) {
                mappingsList = mappingsResponse
            }

            // Fetch PreviousTemplate API again to get updated IsSelected field
            if (data?.AuditTemplateId) {
                const updatedPreviousTemplate = await TemplateService.getPreviousTemplate(
                    data.AuditTemplateId,
                    data.Id, // TemplateId from inspection response
                    data.CreatedBy, // CreatedBy from inspection response
                    data.CustomerId, // CustomerId from inspection response
                )
                if (updatedPreviousTemplate && updatedPreviousTemplate.Questions) {
                    setPreviousTemplateData(updatedPreviousTemplate)
                }
            }

            const newData = JSON.parse(JSON.stringify(data))
            let questionsAdded = 0

            // Use questions from mappingsResponse if available (they have MediaAnswers), otherwise use selectedQuestionsData
            const questionsToImport = mappingsQuestions.length > 0
                ? mappingsQuestions.filter(q => selectedQuestions.includes(q.QuestionId))
                : selectedQuestionsData

            console.log('Importing questions:', questionsToImport.length)
            console.log('Mappings response:', mappingsResponse)
            console.log('Mappings questions with MediaAnswers:', mappingsQuestions.length)
            console.log('Current sections:', newData.Sections?.map(s => s.SectionName) || [])

            // Always use "Imported section" for imported questions
            const importedSectionName = 'Imported section'

            // Find or create "Imported section"
            let importedSection = newData.Sections?.find(
                s => s.SectionName === importedSectionName,
            )

            if (!importedSection) {
                importedSection = {
                    SectionId: `imported-section-${Date.now()}`,
                    SectionName: importedSectionName,
                    SectionOrder: (newData.Sections?.length || 0) + 1,
                    Questions: [],
                    IsImported: true, // Flag to identify imported section
                }
                if (!newData.Sections) {
                    newData.Sections = []
                }
                newData.Sections.push(importedSection)
            }

            // Get question IDs from mappings
            // Handle both cases: mapping objects with SourceQuestionUniqueId or question objects with QuestionId
            const questionIdsFromMappings = new Set()
            if (mappingsList.length > 0) {
                mappingsList.forEach(m => {
                    // Check if it's a mapping object with SourceQuestionUniqueId
                    if (m.SourceQuestionUniqueId) {
                        // SourceQuestionUniqueId might be semicolon-separated string
                        const ids = m.SourceQuestionUniqueId.toString().split(';')
                        ids.forEach(id => {
                            if (id && id.trim()) {
                                questionIdsFromMappings.add(id.trim())
                            }
                        })
                    } else if (m.QuestionId) {
                        // If it's a question object, use QuestionId directly
                        questionIdsFromMappings.add(m.QuestionId.toString())
                    }
                })
            }

            // Create a Set of selected question IDs for quick lookup
            const selectedQuestionIds = new Set(
                questionsToImport.map(q => q.QuestionId?.toString()).filter(Boolean),
            )

            // Remove questions from imported section that are NOT in the selected list
            if (importedSection.Questions && importedSection.Questions.length > 0) {
                importedSection.Questions = importedSection.Questions.filter(
                    existingQ => {
                        const existingQId = existingQ.QuestionId?.toString() || existingQ.Id?.toString()
                        // Keep question if it's in the selected list OR if it's not an imported question
                        return selectedQuestionIds.has(existingQId) || !existingQ.IsImported
                    },
                )
            }

            // Add or update questions in the imported section based on mappings
            questionsToImport.forEach(question => {
                const questionIdStr = question.QuestionId?.toString()
                // Only add if question is in the mappings (or add all selected if mappings list is empty/not available)
                if (questionIdsFromMappings.size > 0 && !questionIdsFromMappings.has(questionIdStr)) {
                    return
                }
                // Check if question already exists in imported section
                const existingQuestion = importedSection.Questions.find(
                    q => q.QuestionId === question.QuestionId || q.Id === question.QuestionId,
                )

                if (!existingQuestion) {
                    // Convert question from PreviousTemplate format to inspection format
                    const isMultipleResponse = question.TypeOfResponse === 2 // 2 = Multiple response
                    const isSingleResponse = question.TypeOfResponse === 1 // 1 = Single response

                    // Get MultiResponseType from first answer if available
                    const firstAnswer = question.MultipleAnswers && question.MultipleAnswers.length > 0
                        ? question.MultipleAnswers[0]
                        : null
                    const multiResponseType = firstAnswer?.MultiResponseType || 1

                    // Convert MediaAnswers to MediaResponse format
                    const mediaResponse = question.MediaAnswers && question.MediaAnswers.length > 0
                        ? question.MediaAnswers.map(media => {
                            const imageUrl = media.SignedURL || media.FileKey
                            return {
                                MediaId: media.Unique_Id || media.FAQId,
                                FileKey: media.FileKey,
                                FileURL: imageUrl, // Use SignedURL if available, otherwise FileKey
                                SignedURL: media.SignedURL, // Preserve SignedURL
                                url: imageUrl, // Add url field for SinglePreview component
                                FileName: media.FileName,
                                Caption: media.Caption || '',
                                Comment: media.Comment || '',
                                CreatedOn: media.CreatedOn,
                                ExpiredOn: media.ExpiredOn,
                            }
                        })
                        : []

                    const inspectionQuestion = {
                        Id: question.QuestionId,
                        QuestionId: question.QuestionId,
                        Question: question.QuestionText,
                        FAQ: question.QuestionText, // FAQ is used for display in inspection
                        QuestionResponseId: question.TypeOfResponse,
                        TypeOfResponse: question.TypeOfResponse,
                        MultiResponseType: multiResponseType, // Required for multiple response questions
                        DisplayOrder: question.DisplayOrder || question.QuestionOrder || 0,
                        SectionId: importedSection.SectionId,
                        Files: question.Files || [], // Include Files from imported question
                        IsRequired: false,
                        IsReadOnly: true, // Mark as read-only
                        IsImported: true, // Mark as imported
                        Answers: {
                            SingleResponse: isSingleResponse && question.SingleAnswers && question.SingleAnswers.length > 0
                                ? {
                                    Response: question.SingleAnswers[0].Response || '',
                                    ResponseId: question.SingleAnswers[0].ResponseId || null,
                                }
                                : null,
                            MultipleResponse: isMultipleResponse && question.MultipleAnswers && question.MultipleAnswers.length > 0
                                ? question.MultipleAnswers.map(answer => ({
                                    Id: answer.Id,
                                    DisplayText: answer.DisplayText,
                                    IsSelected: answer.IsSelected || false,
                                    BackColorCode: answer.BackColorCode || '#808080',
                                    ForeColorCode: answer.ForeColorCode || '#FFFFFF',
                                    Score: answer.Score || 0,
                                    DisplayOrder: answer.DisplayOrder || 0,
                                    IsExcludedFromScoring: answer.IsExcludedFromScoring || false,
                                    MultiResponseType: answer.MultiResponseType || multiResponseType,
                                }))
                                : [],
                            MediaResponse: mediaResponse, // Include MediaAnswers as MediaResponse
                            IsAnswered: isMultipleResponse
                                ? (question.MultipleAnswers || []).some(a => a.IsSelected)
                                : isSingleResponse && question.SingleAnswers && question.SingleAnswers.length > 0 && question.SingleAnswers[0].Response,
                        },
                    }

                    importedSection.Questions.push(inspectionQuestion)
                    questionsAdded += 1
                    console.log('Added question:', {
                        QuestionId: inspectionQuestion.QuestionId,
                        Question: inspectionQuestion.Question,
                        SectionName: importedSection.SectionName,
                        AnswersCount: inspectionQuestion.Answers.MultipleResponse?.length || 0,
                    })
                } else {
                    // If question exists, update its answers with imported answers
                    const isMultipleResponse = question.TypeOfResponse === 2

                    // Update MediaResponse if MediaAnswers exist
                    if (question.MediaAnswers && question.MediaAnswers.length > 0) {
                        const mediaResponse = question.MediaAnswers.map(media => {
                            const imageUrl = media.SignedURL || media.FileKey
                            return {
                                MediaId: media.Unique_Id || media.FAQId,
                                FileKey: media.FileKey,
                                FileURL: imageUrl,
                                SignedURL: media.SignedURL,
                                url: imageUrl,
                                FileName: media.FileName,
                                Caption: media.Caption || '',
                                Comment: media.Comment || '',
                                CreatedOn: media.CreatedOn,
                                ExpiredOn: media.ExpiredOn,
                            }
                        })
                        existingQuestion.Answers = {
                            ...existingQuestion.Answers,
                            MediaResponse: mediaResponse,
                        }
                        // Also update Files array
                        if (question.Files && question.Files.length > 0) {
                            existingQuestion.Files = question.Files
                        }
                    }

                    if (isMultipleResponse && question.MultipleAnswers && question.MultipleAnswers.length > 0) {
                        // Update existing answers with imported selected state
                        const existingAnswers = existingQuestion.Answers?.MultipleResponse || []
                        const importedAnswersMap = new Map()
                        question.MultipleAnswers.forEach(importedAnswer => {
                            importedAnswersMap.set(importedAnswer.Id, importedAnswer)
                        })

                        const updatedAnswers = existingAnswers.map(existingAnswer => {
                            const importedAnswer = importedAnswersMap.get(existingAnswer.Id)
                            if (importedAnswer) {
                                return {
                                    ...existingAnswer,
                                    IsSelected: importedAnswer.IsSelected || false,
                                    BackColorCode: importedAnswer.BackColorCode || existingAnswer.BackColorCode,
                                    ForeColorCode: importedAnswer.ForeColorCode || existingAnswer.ForeColorCode,
                                    Score: importedAnswer.Score !== undefined ? importedAnswer.Score : existingAnswer.Score,
                                }
                            }
                            return existingAnswer
                        })

                        // Add any new answers from imported data
                        question.MultipleAnswers.forEach(importedAnswer => {
                            if (!existingAnswers.some(existing => existing.Id === importedAnswer.Id)) {
                                updatedAnswers.push({
                                    Id: importedAnswer.Id,
                                    DisplayText: importedAnswer.DisplayText,
                                    IsSelected: importedAnswer.IsSelected || false,
                                    BackColorCode: importedAnswer.BackColorCode || '#808080',
                                    ForeColorCode: importedAnswer.ForeColorCode || '#FFFFFF',
                                    Score: importedAnswer.Score || 0,
                                    DisplayOrder: importedAnswer.DisplayOrder || 0,
                                    IsExcludedFromScoring: importedAnswer.IsExcludedFromScoring || false,
                                    MultiResponseType: importedAnswer.MultiResponseType || 1,
                                })
                            }
                        })

                        existingQuestion.Answers = {
                            ...existingQuestion.Answers,
                            MultipleResponse: updatedAnswers,
                            IsAnswered: updatedAnswers.some(a => a.IsSelected),
                        }
                    } else if (question.SingleAnswers && question.SingleAnswers.length > 0) {
                        existingQuestion.Answers = {
                            ...existingQuestion.Answers,
                            SingleResponse: {
                                Response: question.SingleAnswers[0].Response || '',
                                ResponseId: question.SingleAnswers[0].ResponseId || null,
                            },
                            IsAnswered: !!question.SingleAnswers[0].Response,
                        }
                    }
                }
            })

            // Sort sections to put Imported section first
            if (newData.Sections && newData.Sections.length > 0) {
                newData.Sections.sort((a, b) => {
                    const aIsImported = a.SectionName === importedSectionName || a.IsImported
                    const bIsImported = b.SectionName === importedSectionName || b.IsImported
                    if (aIsImported && !bIsImported) return -1
                    if (!aIsImported && bIsImported) return 1
                    return (a.SectionOrder || 0) - (b.SectionOrder || 0)
                })
            }

            console.log('Final sections after import:', newData.Sections?.map(s => ({
                SectionName: s.SectionName,
                QuestionsCount: s.Questions?.length || 0,
            })) || [])

            setData(newData)
            setChanged(c => c + 1)
            setShowDataImportModal(false)
            setSelectedQuestions([])
            successToast(`Imported ${questionsAdded} question(s) successfully`)
        } catch (e) {
            console.error('Failed to save imported data:', e)
            setError('Failed to import data')
        } finally {
            setLoadingMappings(false)
        }
    }

    const addMedia = async (
        QuestionId,
        files,
        sectionId,
        sectionsBaseline = null,
    ) => {
        // Prevent adding media if inspection is completed, except in reopen context
        if (isCompletedAudit && !canEditCompletedInspection) {
            return null
        }

        if (!data) {
            return null
        }

        const locateSectionIdx = secs => {
            const sid = secs.findIndex(
                s =>
                    String(s?.SectionId ?? '')
                    === String(sectionId ?? '')
                    || String(s?.Id ?? '') === String(sectionId ?? ''),
            )
            if (sid > -1) return sid
            return secs.findIndex(s =>
                (s.Questions || []).some(
                    q =>
                        String(q?.Id ?? '') === String(QuestionId ?? ''),
                ),
            )
        }

        const initialSections =
            sectionsBaseline != null ? sectionsBaseline : data.Sections || []

        const resolvePersistedMediaId = file => {
            const candidates = [file?.MediaId, file?.Unique_Id, file?.id]
            for (let i = 0; i < candidates.length; i += 1) {
                const raw = candidates[i]
                const num = Number(raw)
                if (Number.isFinite(num) && num > 0) {
                    return num
                }
            }
            return 0
        }

        const filesToUpload = (files || []).filter(
            file => file?.local || resolvePersistedMediaId(file) > 0,
        )
        if (!filesToUpload.length) {
            return null
        }

        const { sections, allFiles } = await filesToUpload.reduce(
            async (accPromise, ele) => {
                const acc = await accPromise
                let sectionsState = acc.sections
                const outFiles = acc.allFiles.slice()

                let uploadResponse = ele
                if (ele?.local) {
                    uploadResponse = await InspectionService.uploadMedia({
                        media: ele.file.file,
                    })
                }
                const existingMediaId = resolvePersistedMediaId(ele)

                const result = await InspectionService.saveMedia([
                    {
                        MediaId: existingMediaId,
                        FAQId: QuestionId,
                        Order: 1,
                        FolderName: '1',
                        Comment:
                            uploadResponse?.Comment ?? ele.Comment ?? '',
                        Caption:
                            uploadResponse?.Caption ?? ele.Caption ?? '',
                        FileType:
                            uploadResponse?.FileType ||
                            ele?.file?.file?.type ||
                            '',
                        SignedURL:
                            uploadResponse?.ImagePathUrl ||
                            uploadResponse?.FileURL,
                        FileURL:
                            uploadResponse?.ImagePathUrl ||
                            uploadResponse?.FileURL,
                        FileKey:
                            uploadResponse?.CloudImageId ||
                            uploadResponse?.FileKey,
                    },
                ])

                const sectionIndex = locateSectionIdx(sectionsState)
                sectionsState = sectionsState.map((section, idx) => {
                    if (idx !== sectionIndex) return section

                    const questions = (
                        section.Questions || []
                    ).map(question => {
                        if (String(question.Id) !== String(QuestionId)) {
                            return question
                        }
                        const temp = { ...question }
                        const updated = result?.[0]
                        const prevFiles = getQuestionMediaList(temp)
                        let nextFiles = prevFiles
                        if (updated?.MediaId) {
                            const pfIdx = prevFiles.findIndex(
                                f =>
                                    (f.MediaId || f.id)
                                    === updated.MediaId,
                            )
                            if (pfIdx >= 0) {
                                const next = [...prevFiles]
                                next[pfIdx] = {
                                    ...next[pfIdx],
                                    ...updated,
                                }
                                nextFiles = next
                            } else {
                                nextFiles = [...prevFiles, ...result]
                            }
                        } else {
                            nextFiles = [...prevFiles, ...result]
                        }
                        return syncQuestionMediaFields({
                            ...temp,
                            Files: mergeQuestionFilesPreservingMeta(
                                prevFiles,
                                nextFiles,
                            ),
                        })
                    })
                    return {
                        ...section,
                        Questions: questions,
                    }
                })

                if (result?.[0]) {
                    linkUploadedMediaCache([ele], result)
                    outFiles.push({ ...result[0], local: false })
                }

                return { sections: sectionsState, allFiles: outFiles }
            },
            Promise.resolve({
                sections: initialSections,
                allFiles: [],
            }),
        )

        // Platen grid: merge only this question's Files (full Sections replace clears unsaved platen rows).
        if (isPlatenGridTemplate) {
            setData(prev => {
                if (!prev?.Sections?.length) return prev
                return {
                    ...prev,
                    Sections: mergeQuestionFilesFromUpdatedSections(
                        prev.Sections,
                        sections,
                        QuestionId,
                    ),
                }
            })
            return allFiles
        }
        if (isSpecialCustomerClaimTemplate) {
            setData(prev => {
                if (!prev?.Sections?.length) return prev
                return {
                    ...prev,
                    Sections: mergeQuestionFilesFromUpdatedSections(
                        prev.Sections,
                        sections,
                        QuestionId,
                    ),
                }
            })
            return allFiles
        }
        setData({
            ...data,
            Sections: sections,
        })

        return allFiles
    }

    const filterQuestionMedia = (question, file) => {
        const next = { ...question }
        if (Array.isArray(next.Files)) {
            next.Files = filterMediaFilesList(next.Files, file)
        }
        if (Array.isArray(next.MediaAnswers)) {
            next.MediaAnswers = filterMediaFilesList(next.MediaAnswers, file)
        }
        if (Array.isArray(next.Answers?.MediaResponse)) {
            next.Answers = {
                ...next.Answers,
                MediaResponse: filterMediaFilesList(
                    next.Answers.MediaResponse,
                    file,
                ),
            }
        }
        return next
    }

    const removeMedia = async (QuestionId, file, sectionId) => {
        if (isCompletedAudit && !canEditCompletedInspection) {
            return null
        }

        const mediaId = file?.MediaId ?? file?.id
        if (mediaId != null && mediaId !== '' && !file?.local) {
            try {
                await InspectionService.deleteMedia(mediaId)
            } catch (error) {
                console.error('Error deleting media:', error)
            }
        }

        if (!data?.Sections?.length) {
            return null
        }

        let updatedQuestion = null

        const sections = data.Sections.map(section => {
            const inSection =
                String(section.SectionId ?? '') === String(sectionId ?? '') ||
                String(section.Id ?? '') === String(sectionId ?? '') ||
                (section.Questions || []).some(
                    q => String(q?.Id ?? '') === String(QuestionId ?? ''),
                )
            if (!inSection) {
                return section
            }

            return {
                ...section,
                Questions: (section.Questions || []).map(q => {
                    if (String(q.Id) !== String(QuestionId)) {
                        return q
                    }
                    updatedQuestion = filterQuestionMedia(q, file)
                    return updatedQuestion
                }),
            }
        })

        if (isPlatenGridTemplate) {
            if (updatedQuestion) {
                setData(prev => ({
                    ...prev,
                    Sections: (prev.Sections || []).map(section => ({
                        ...section,
                        Questions: (section.Questions || []).map(q =>
                            String(q.Id) === String(QuestionId)
                                ? updatedQuestion
                                : q,
                        ),
                    })),
                }))
            }
            return updatedQuestion
        }
        if (isSpecialCustomerClaimTemplate) {
            setData(prev => {
                if (!prev?.Sections?.length) return prev
                return {
                    ...prev,
                    Sections: mergeQuestionFilesFromUpdatedSections(
                        prev.Sections,
                        sections,
                        QuestionId,
                    ),
                }
            })
            return updatedQuestion
        }
        setData({
            ...data,
            Sections: sections,
        })

        return updatedQuestion
    }

    const onAnswerChanged = (sectionId, question, answer) => {
        console.log('onAnswerChanged', { sectionId, question, answer })
        // Prevent changes if completed and post-complete edit is blocked / not reopened
        if (isCompletedAudit && !canEditCompletedInspection) {
            return
        }

        console.log('onAnswerChanged - Start:', {
            sectionId,
            questionId: question.Id,
            answer,
            currentData: data?.Sections,
        })

        if (data) {
            const sections = data.Sections.map(section => {
                if (section.SectionId === sectionId) {
                    if (isUniformRadioAnswer(answer)) {
                        const uniformText = readUniformRadioDisplayText(answer)
                        return (
                            (uniformText &&
                                applyUniformRadioToSection(
                                    section,
                                    uniformText,
                                    data?.TemplateResponseType?.MultipleResponse,
                                )) ||
                            section
                        )
                    }
                    const questions = section.Questions.map(item => {
                        if (
                            (item.Id && item.Id?.toString()) ===
                            (question.Id && question.Id?.toString())
                        ) {
                            let temp = { ...item }

                            // Special case: NCReply / CNCResponse updates (C/NC selector)
                            if (
                                answer &&
                                typeof answer === 'object' &&
                                Object.prototype.hasOwnProperty.call(
                                    answer,
                                    '__ncReply',
                                )
                            ) {
                                // Update NCReply only if it already exists on the question
                                // (do not introduce NCReply when the payload model does not have it)
                                const base = Object.prototype.hasOwnProperty.call(
                                    item,
                                    'NCReply',
                                )
                                    ? { ...item, NCReply: answer.__ncReply }
                                    : { ...item }

                                // For company 34930, also maintain CNCResponse structure
                                if (companyId === 34930) {
                                    // Prefer CNCResponse stored inside Answers (if present), otherwise use root CNCResponse
                                    const existingCncInAnswers =
                                        item.Answers &&
                                            item.Answers.CNCResponse
                                            ? item.Answers.CNCResponse
                                            : null
                                    const existingCncRoot =
                                        item.CNCResponse || null
                                    const existingCnc =
                                        existingCncInAnswers ||
                                        existingCncRoot ||
                                        {}

                                    const cncPayload =
                                        answer.__cncResponse || {}

                                    const responseData = {
                                        QuestionId:
                                            cncPayload.QuestionId ||
                                            existingCnc.ResponseData
                                                ?.QuestionId ||
                                            existingCnc.QuestionId ||
                                            item.Id,
                                        CNCResponseId:
                                            cncPayload.CNCResponseId ||
                                            existingCnc.ResponseData
                                                ?.CNCResponseId ||
                                            0,
                                        CNCValue:
                                            typeof cncPayload.CNCValue ===
                                                'number'
                                                ? cncPayload.CNCValue
                                                : existingCnc.ResponseData
                                                    ?.CNCValue ?? 0,
                                        AdditionalAnswer:
                                            cncPayload.AdditionalAnswer ??
                                            existingCnc.ResponseData
                                                ?.AdditionalAnswer ??
                                            '',
                                    }

                                    // Update QuestionData.Options[].IsSelected based on selected CNCResponseId
                                    const updatedQuestionData = Array.isArray(
                                        existingCnc.QuestionData,
                                    )
                                        ? existingCnc.QuestionData.map(qd => {
                                            if (
                                                !qd ||
                                                !Array.isArray(qd.Options)
                                            ) {
                                                return qd
                                            }
                                            return {
                                                ...qd,
                                                Options: qd.Options.map(opt => {
                                                    if (
                                                        opt &&
                                                        typeof opt ===
                                                        'object'
                                                    ) {
                                                        return {
                                                            ...opt,
                                                            IsSelected:
                                                                opt.Id ===
                                                                responseData.CNCResponseId,
                                                        }
                                                    }
                                                    return opt
                                                }),
                                            }
                                        })
                                        : existingCnc.QuestionData

                                    const updatedCnc = {
                                        ...existingCnc,
                                        QuestionId:
                                            existingCnc.QuestionId ||
                                            responseData.QuestionId,
                                        ResponseData: responseData,
                                        ...(updatedQuestionData && {
                                            QuestionData: updatedQuestionData,
                                        }),
                                    }

                                    if (existingCncInAnswers) {
                                        // Keep CNCResponse under Answers, same place as in incoming payload
                                        temp = {
                                            ...base,
                                            Answers: {
                                                ...item.Answers,
                                                CNCResponse: updatedCnc,
                                            },
                                        }
                                    } else {
                                        // Fallback: root-level CNCResponse
                                        temp = {
                                            ...base,
                                            CNCResponse: updatedCnc,
                                        }
                                    }
                                } else {
                                    temp = base
                                }
                            } else if (
                                // Always merge the full answer object for MultipleResponseType.Multiple
                                temp.TypeOfResponse ===
                                TemplateResponseType.Multiple
                            ) {
                                temp = {
                                    ...item,
                                    Answers: {
                                        ...item.Answers,
                                        ...answer,
                                    },
                                }
                                // Force IsOtherAnswerSelected to true if OtherAnswerResponse is present, else false
                                if (
                                    temp.Answers.OtherAnswerResponse &&
                                    Array.isArray(
                                        temp.Answers.OtherAnswerResponse,
                                    ) &&
                                    temp.Answers.OtherAnswerResponse.length > 0
                                ) {
                                    temp.Answers.IsOtherAnswerSelected = true
                                } else {
                                    temp.Answers.IsOtherAnswerSelected = false
                                }
                                temp.IsOtherAnswerSelected =
                                    temp.Answers.IsOtherAnswerSelected
                            } else if (answer.ParentChildResponse) {
                                console.log('Processing ParentChildResponse:', {
                                    before: item.Answers.ParentChildResponse,
                                    incoming: answer.ParentChildResponse,
                                })

                                temp = {
                                    ...item,
                                    Answers: {
                                        ...item.Answers,
                                        ...answer,
                                        ParentChildResponse:
                                            applyParentChildResponseScores(
                                                answer.ParentChildResponse,
                                            ),
                                    },
                                    IsAnswered: true,
                                    IsOtherAnswerSelected:
                                        !!answer.IsOtherAnswerSelected,
                                }

                                console.log(
                                    'After ParentChildResponse Update:',
                                    {
                                        updated:
                                            temp.Answers.ParentChildResponse,
                                    },
                                )
                            } else if (
                                temp.TypeOfResponse ===
                                TemplateResponseType.Single &&
                                item.Answers.SingleResponse
                            ) {
                                console.log('Processing SingleResponse:', {
                                    questionId: item.Id,
                                    questionType: temp.TypeOfResponse,
                                    currentAnswer: item.Answers.SingleResponse,
                                    incomingAnswer: answer,
                                    answerType: typeof answer,
                                    isObject: typeof answer === 'object',
                                })

                                // Handle checkbox responses with IsSelected property
                                if (
                                    typeof answer === 'object' &&
                                    'Response' in answer &&
                                    'IsSelected' in answer
                                ) {
                                    temp = {
                                        ...item,
                                        Answers: {
                                            ...item.Answers,
                                            SingleResponse: {
                                                ...item.Answers.SingleResponse,
                                                Response: answer.Response,
                                                IsSelected: answer.IsSelected,
                                            },
                                        },
                                    }
                                } else {
                                    // Handle regular single responses
                                    temp = {
                                        ...item,
                                        Answers: {
                                            ...item.Answers,
                                            SingleResponse: {
                                                ...item.Answers.SingleResponse,
                                                Response: answer,
                                            },
                                        },
                                    }
                                }

                                console.log('Updated SingleResponse:', {
                                    questionId: item.Id,
                                    updatedAnswer: temp.Answers.SingleResponse,
                                })
                            } else {
                                const multipleResponses = (
                                    item.Answers.MultipleResponse || []
                                ).map(choice => {
                                    if (
                                        item.MultiResponseType ===
                                        TemplateQuestionResponseType.Multiple
                                    ) {
                                        return {
                                            ...choice,
                                            IsSelected: answer?.includes(
                                                choice.Id,
                                            ),
                                        }
                                    }
                                    choice.IsSelected = false
                                    if (answer.Id === choice.Id) {
                                        return {
                                            ...choice,
                                            IsSelected: !choice.IsSelected,
                                        }
                                    }
                                    return choice
                                })
                                temp = {
                                    ...item,
                                    Answers: {
                                        ...item.Answers,
                                        MultipleResponse: multipleResponses,
                                    },
                                }
                                setTempState(prev => !prev)
                            }

                            // --- SCORE LOGIC: Set question.Score based on selected answer ---
                            if (temp.TypeOfResponse === TemplateResponseType.Multiple) {
                                // For MultipleResponseType, find the selected option
                                const selectedOption = Array.isArray(temp.Answers.MultipleResponse)
                                    ? temp.Answers.MultipleResponse.find(opt => opt.IsSelected)
                                    : null;

                                // Get score from selected option, or look it up from TemplateResponseType.MultipleResponse
                                let score = 0;
                                if (selectedOption) {
                                    // First try to get score from the selected option (it should already be there from API)
                                    if (typeof selectedOption.Score === 'number') {
                                        score = selectedOption.Score;
                                    } else if (data?.TemplateResponseType?.MultipleResponse) {
                                        // If score not in selected option, look it up from TemplateResponseType.MultipleResponse
                                        // Match by QuestionResponseId and DisplayText since Ids differ between UserMultipleResponse and TemplateResponseType
                                        const templateOption = data.TemplateResponseType.MultipleResponse.find(
                                            opt =>
                                                (opt.Id === selectedOption.Id ||
                                                    opt.GroupId === selectedOption.QuestionResponseId ||
                                                    opt.Id === selectedOption.QuestionResponseId ||
                                                    (temp.QuestionResponseId && (opt.GroupId === temp.QuestionResponseId || opt.Id === temp.QuestionResponseId))) &&
                                                opt.DisplayText === selectedOption.DisplayText,
                                        );
                                        if (templateOption && typeof templateOption.Score === 'number') {
                                            score = templateOption.Score;
                                            // Also update the selected option with the score for future reference
                                            selectedOption.Score = score;
                                        }
                                    }
                                }
                                temp.Score = score;

                                // Also ensure all MultipleResponse items have their Score property preserved
                                if (Array.isArray(temp.Answers.MultipleResponse) && data?.TemplateResponseType?.MultipleResponse) {
                                    temp.Answers.MultipleResponse = temp.Answers.MultipleResponse.map(mr => {
                                        if (typeof mr.Score !== 'number') {
                                            // Look up score from TemplateResponseType if missing
                                            const templateMatch = data.TemplateResponseType.MultipleResponse.find(
                                                opt =>
                                                    (opt.Id === mr.Id ||
                                                        opt.GroupId === mr.QuestionResponseId ||
                                                        opt.Id === mr.QuestionResponseId ||
                                                        (temp.QuestionResponseId && (opt.GroupId === temp.QuestionResponseId || opt.Id === temp.QuestionResponseId))) &&
                                                    opt.DisplayText === mr.DisplayText,
                                            );
                                            if (templateMatch && typeof templateMatch.Score === 'number') {
                                                return { ...mr, Score: templateMatch.Score };
                                            }
                                        }
                                        return mr;
                                    });
                                }
                            } else if (
                                temp.Answers.ParentChildResponse &&
                                Array.isArray(temp.Answers.ParentChildResponse) &&
                                temp.Answers.ParentChildResponse.length > 0
                            ) {
                                temp.Score = getParentChildQuestionScore(
                                    temp.Answers.ParentChildResponse,
                                    getQuestionScoreFallback(item),
                                    temp.Answers.OtherAnswerResponse,
                                )
                            } else if (temp.TypeOfResponse === TemplateResponseType.Single) {
                                // For SingleResponseType, only set score if user has answered
                                if (
                                    temp.Answers.SingleResponse &&
                                    temp.Answers.SingleResponse.Response !== undefined &&
                                    temp.Answers.SingleResponse.Response !== null &&
                                    temp.Answers.SingleResponse.Response !== ''
                                ) {
                                    // Use the question's own Score property if defined, otherwise 0
                                    temp.Score = typeof item.Score === 'number' ? item.Score : 0
                                } else {
                                    temp.Score = 0
                                }
                            }
                            const answered = isQuestionAnswered(temp)
                            temp.IsAnswered = answered;
                            if (temp.Answers) {
                                temp.Answers.IsAnswered = answered;
                            }
                            return temp;
                        }
                        return item
                    })
                    const nextSection = {
                        ...section,
                        Questions: questions,
                    }
                    return shouldClearSectionAllAllow(answer)
                        ? clearSectionAllAllow(nextSection)
                        : nextSection
                }
                return section
            })

            // Before setting data
            console.log('onAnswerChanged - Before State Update:', {
                updatedSections: sections,
                fullUpdate: { ...data, Sections: sections },
            })

            setData({
                ...data,
                Sections: applyLiveFormulas(sections),
            })

            // After setting data
            console.log('onAnswerChanged - After State Update:', {
                newData: { ...data, Sections: sections },
            })
            setChanged(changed + 1)
        }
    }

    const addEditNotes = async (currentQuestion, item, add) => {
        if (item && data) {
            const sections = data.Sections.map(section => {
                if (section.SectionId === currentQuestion.SectionId) {
                    const questions = section.Questions.map(question => {
                        if (question.Id === currentQuestion.Id) {
                            const temp = { ...question }
                            temp.Notes = add
                                ? item
                                : temp.Notes.map(ele => {
                                    if (ele.NoteId === item[0].NoteId) {
                                        return { ...item[0] }
                                    }
                                    return ele
                                })
                            return temp
                        }
                        return question
                    })
                    return {
                        ...section,
                        Questions: questions,
                    }
                }
                return section
            })

            const newData = {
                ...data,
                Sections: sections,
            }

            setData(newData)

            // Keep translation source up to date so notes added after initial load
            // can also be translated when language is not English.
            if (isLanguageFeatureEnabled) {
                initialTemplateRef.current = newData
                if (selectedLanguage !== 'English') {
                    try {
                        setTranslating(true)
                        const translated = await TranslationService.translateTemplate(
                            newData,
                            selectedLanguage,
                            'English',
                        )
                        setTranslatedData(translated)
                    } catch (error) {
                        console.error('Error translating notes:', error)
                    } finally {
                        setTranslating(false)
                    }
                }
            }
        }
    }

    const handlePressReset = useCallback(() => {
        if (!isPressSelectionResetEnabled(user)) return
        if (!data?.Sections?.length) return
        if (isCompletedAudit && !canEditCompletedInspection) return

        const target = getPressResetTarget(data.Sections)
        if (!target?.questionId) return

        const clearedSections = applyLiveFormulas(
            clearInspectionKeepingPressSelection(
                data.Sections,
                target.questionId,
            ),
        )

        setData(prev => ({
            ...prev,
            Sections: clearedSections,
            Comments: [],
        }))
        setErrors(false)
        setChanged(c => c + 1)
    }, [data?.Sections, data?.Status, isCompleted, isReopened, user])

    const [pressDownModalOpen, setPressDownModalOpen] = useState(false)

    const handlePressDownClick = useCallback(() => {
        if (!isPressSelectionResetEnabled(user)) return
        if (!isPressDownTemplate(data)) return
        if (isCompletedAudit && !canEditCompletedInspection) return
        setPressDownModalOpen(true)
    }, [data, data?.Status, isCompleted, isReopened, user])

    const handlePressDownConfirm = async () => {
        setPressDownModalOpen(false)
        try {
            // Down status is always 8 (do not use a cached StatusType.Down that may still be 6)
            const result = await save(8, true)
            if (result) {
                successToast('Inspection marked as Down')
                setChanged(0)
                skipUnmountSaveRef.current = true
                navigation('/app/templates')
            }
        } catch (err) {
            console.error('Press Down Error:', err)
            setError(ErrorConstant.default)
        }
    }

    const pressResetListProps = useMemo(() => {
        if (!isPressSelectionResetEnabled(user)) return {}
        const props = { onPressReset: handlePressReset }
        // Down only for WK + Large/Small Press Daily templates
        if (isPressDownTemplate(data)) {
            props.onPressDown = handlePressDownClick
        }
        return props
    }, [data, handlePressDownClick, handlePressReset, user])

    const onSectionNAChange = useCallback(
        async (sectionId, isNA) => {
            if (isCompletedAudit && !canEditCompletedInspection) {
                return
            }
            const targetSection = (data?.Sections || []).find(
                s => String(s.SectionId) === String(sectionId),
            )
            if (!isSectionNAAvailable(targetSection)) {
                return
            }

            const previousIsNA = !!(
                targetSection?.IsNASelected ?? targetSection?.isNASelected
            )
            const nextIsNA = !!isNA

            const applySectionNA = (sections, selected) =>
                (sections || []).map(section => {
                    if (String(section.SectionId) !== String(sectionId)) {
                        return section
                    }
                    if (!isSectionNAAvailable(section)) {
                        return section
                    }
                    return {
                        ...section,
                        IsNASelected: selected,
                        Questions: (section.Questions || []).map(question => ({
                            ...question,
                            NotApplicable: selected,
                        })),
                    }
                })

            // Optimistic UI update
            setData(prev => {
                if (!prev?.Sections) return prev
                return {
                    ...prev,
                    Sections: applyLiveFormulas(
                        applySectionNA(prev.Sections, nextIsNA),
                    ),
                }
            })
            if (nextIsNA) {
                setErrors(prev => {
                    if (!prev || typeof prev !== 'object') return prev
                    const next = { ...prev }
                    const sectionQuestions = targetSection?.Questions || []
                    sectionQuestions.forEach(question => {
                        if (question?.Id != null) {
                            delete next[question.Id]
                            delete next[String(question.Id)]
                        }
                    })
                    return next
                })
            }
            setChanged(c => c + 1)

            // Persist via dedicated Section NA API
            const auditId = data?.Id ?? data?.AuditId
            if (publicScreen || auditId == null || auditId === '') {
                return
            }
            try {
                const response = await InspectionService.updateSectionNAState({
                    AuditId: auditId,
                    SectionId: sectionId,
                    IsNASelected: nextIsNA,
                })
                const success =
                    response?.Success === true ||
                    response?.Success === 1 ||
                    String(response?.Success).toLowerCase() === 'true' ||
                    response?.IsNASelected === nextIsNA
                if (!success) {
                    throw new Error(
                        response?.Message ||
                            response?.message ||
                            'Failed to update section N/A state',
                    )
                }
                if (typeof response?.IsNASelected === 'boolean') {
                    setData(prev => {
                        if (!prev?.Sections) return prev
                        return {
                            ...prev,
                            Sections: applyLiveFormulas(
                                applySectionNA(
                                    prev.Sections,
                                    !!response.IsNASelected,
                                ),
                            ),
                        }
                    })
                }
                const sectionFormulas = readServerFormulaList(response)
                if (sectionFormulas.length) {
                    setData(prev => applyServerFormulaResults(prev, sectionFormulas))
                }
            } catch (err) {
                console.error('Section NAState Error:', err)
                setData(prev => {
                    if (!prev?.Sections) return prev
                    return {
                        ...prev,
                        Sections: applyLiveFormulas(
                            applySectionNA(prev.Sections, previousIsNA),
                        ),
                    }
                })
                errorToast(
                    err?.message ||
                        err?.Message ||
                        'Failed to update section N/A. Please try again.',
                )
            }
        },
        [
            data?.AuditId,
            data?.Id,
            data?.Sections,
            data?.Status,
            errorToast,
            isCompleted,
            isReopened,
            publicScreen,
        ],
    )

    const saveNote = async (item, noteData) => {
        // Prevent saving notes if inspection is completed, except in reopen context
        if (isCompletedAudit && !canEditCompletedInspection) {
            return null
        }
        try {
            if (noteData) {
                let note = []
                if (!noteData.NoteId) {
                    note = await InspectionService.addNotes([noteData])
                    await addEditNotes(item, note, true)
                } else {
                    note = await InspectionService.updateNote([noteData])
                    await addEditNotes(item, note, false)
                }
                return note
            }
        } catch (error) {
            console.log('error', error)
            return null
        }
        return null
    }

    const [copySectionModalOpen, setCopySectionModalOpen] = useState(false)
    const [copySectionName, setCopySectionName] = useState('')
    const [copySourceSectionId, setCopySourceSectionId] = useState(null)
    const [copySourceSectionName, setCopySourceSectionName] = useState('')
    const copySourceSection =
        data?.Sections?.find(s => s.SectionId === copySourceSectionId) || null
    const copyModalSourceSection =
        copySourceSection ||
        (copySourceSectionName
            ? { SectionName: copySourceSectionName }
            : null)
    const copyModalUsesAddNew =
        !!copyModalSourceSection &&
        getSectionCopyButtonLabel(copyModalSourceSection) === 'Add New'

    const handleCopySectionClick = sectionId => {
        setCopySourceSectionId(sectionId)
        // Capture the current name of the section we are copying
        const sourceSection =
            data?.Sections?.find(s => s.SectionId === sectionId) || null
        setCopySourceSectionName(sourceSection?.SectionName || '')
        setCopySectionName('')
        setCopySectionModalOpen(true)
    }

    const handleCopySectionConfirm = async () => {
        try {
            const trimmedName = (copySectionName || '').trim()
            if (!trimmedName) {
                return
            }
            setSaving(true)
            const saved = await save(StatusType.Save, false, false)
            if (saved && copySourceSectionId) {
                await InspectionService.copySection({
                    AuditUniqueId: data.Id,
                    SectionId: copySourceSectionId,
                    CopyAuditId: 0,
                    SectionName: trimmedName,
                })
                await refetch()

                // After refetch, override the copied section's name in local state.
                // Heuristic: keep original section (by SectionId) and rename any *other*
                // sections that still have the original name.
                setData(prev => {
                    if (!prev || !Array.isArray(prev.Sections)) {
                        return prev
                    }
                    const sections = prev.Sections.map(s => {
                        const sameName =
                            (s.SectionName || '') ===
                            (copySourceSectionName || '')
                        const isOriginal =
                            s.SectionId === copySourceSectionId
                        if (sameName && !isOriginal) {
                            return { ...s, SectionName: trimmedName }
                        }
                        return s
                    })
                    return {
                        ...prev,
                        Sections: sections,
                    }
                })
            }
        } catch (error) {
            console.log('error', error)
        } finally {
            setSaving(false)
            setCopySectionModalOpen(false)
            setCopySourceSectionId(null)
            setCopySectionName('')
            setCopySourceSectionName('')
        }
    }

    const copySection = sectionId => {
        handleCopySectionClick(sectionId)
    }

    const directCopySection = async (sectionId, sectionName) => {
        try {
            const finalSectionName = String(sectionName || '').trim()
            if (!sectionId || !finalSectionName) {
                return
            }
            setSaving(true)
            const saved = await save(StatusType.Save, false, false)
            if (saved) {
                await InspectionService.copySection({
                    AuditUniqueId: data.Id,
                    SectionId: sectionId,
                    CopyAuditId: 0,
                    SectionName: finalSectionName,
                })
                await refetch()
            }
        } catch (error) {
            console.log('error', error)
        } finally {
            setSaving(false)
        }
    }

    const onTemplateAdd = async id => {
        try {
            setSaving(true)
            const saved = await save(StatusType.Save, false, false)
            if (saved) {
                await InspectionService.copySection({
                    AuditUniqueId: data.Id,
                    CopyAuditId: id,
                })
                await refetch()
            }
        } catch (error) {
            console.log('error', error)
        } finally {
            setSaving(false)
        }
    }

    const removeSection = async sectionId => {
        try {
            setSaving(true)
            // Allow React to paint the loader before starting network work
            await new Promise(resolve => setTimeout(resolve, 0))
            await InspectionService.removeSection({
                TemplateId: data?.Id,
                SectionId: sectionId,
                LoginSource: 1,
            })
            await refetch()
        } catch (error) {
            console.log('error', error)
        } finally {
            setSaving(false)
        }
    }

    const onAuditNameChange = e => {
        setData({
            ...data,
            AuditName: e.target.value,
        })
    }

    const totalScore = useMemo(() => {
        let total = 0
        if (!data?.Sections?.length) return total
        data.Sections.forEach(section => {
            if (!section?.Questions?.length) return
            section.Questions.forEach(question => {
                total += getQuestionDisplayScore(question)
            })
        })
        return total
    }, [data])

    const specialPlatenReadOnly = isInspectionReadOnly

    const claimReadOnly = specialPlatenReadOnly
    const [claimSectionWorkflowStatuses, setClaimSectionWorkflowStatuses] =
        useState([])
    const [claimSectionWorkflowBusy, setClaimSectionWorkflowBusy] =
        useState(false)
    const [claimNumberDisplay, setClaimNumberDisplay] = useState('')
    const claimTemplateUniqueId =
        data?.OriginalTemplateId || SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID
    const isCustomerClaimNonRma =
        isCustomerClaimNonRmaTemplateId(data?.OriginalTemplateId) ||
        (isSpecialCustomerClaimTemplate &&
            !isSpecialCustomerClaimTemplateId(data?.OriginalTemplateId))
    const claimAuditId = data?.Id
    const claimWorkflowTemplateIdRef = useRef(claimTemplateUniqueId)
    const resolveClaimWorkflowTemplateId = () => {
        if (!isCustomerClaimNonRma) {
            return claimTemplateUniqueId
        }
        return (
            claimWorkflowTemplateIdRef.current ||
            SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID
        )
    }

    const loadClaimSectionWorkflowStatus = useCallback(async () => {
        if (
            !isSpecialCustomerClaimTemplate ||
            !claimAuditId ||
            isPreview ||
            publicScreen
        ) {
            return
        }
        const loadForTemplate = async templateId => {
            try {
                const response = await SectionWorkflowService.getSectionStatus(
                    claimAuditId,
                    templateId,
                )
                return parseSectionStatusResponse(response)
                    .map(normalizeSectionStatusEntry)
                    .filter(Boolean)
            } catch {
                return []
            }
        }

        // Customer Claim (129321): original single-template status call.
        if (!isCustomerClaimNonRma) {
            const normalized = await loadForTemplate(claimTemplateUniqueId)
            claimWorkflowTemplateIdRef.current = claimTemplateUniqueId
            setClaimSectionWorkflowStatuses(normalized)
            return
        }

        // Non RMA only: Phase Setup is on Customer Claim (129321).
        const sharedStatuses = await loadForTemplate(
            SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
        )
        if (sharedStatuses.length) {
            claimWorkflowTemplateIdRef.current =
                SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID
            setClaimSectionWorkflowStatuses(sharedStatuses)
            return
        }
        const ownStatuses = await loadForTemplate(claimTemplateUniqueId)
        claimWorkflowTemplateIdRef.current = claimTemplateUniqueId
        setClaimSectionWorkflowStatuses(ownStatuses)
    }, [
        claimAuditId,
        claimTemplateUniqueId,
        isCustomerClaimNonRma,
        isPreview,
        isSpecialCustomerClaimTemplate,
        publicScreen,
    ])

    useEffect(() => {
        loadClaimSectionWorkflowStatus()
    }, [loadClaimSectionWorkflowStatus])

    useEffect(() => {
        const next = String(
            (data?.ClaimNumber ?? data?.claimNumber) || '',
        ).trim()
        setClaimNumberDisplay(next)
    }, [data?.ClaimNumber, data?.claimNumber])

    const claimSectionWorkflowStatusesLive = useMemo(() => {
        if (!isCustomerClaimNonRma) {
            return claimSectionWorkflowStatuses
        }
        return overlayWorkflowStatusesOntoLiveSections(
            claimSectionWorkflowStatuses,
            buildClaimLiveSectionIdsByPhase(
                claimSectionWorkflowStatuses,
                data?.Sections,
            ),
        )
    }, [
        isCustomerClaimNonRma,
        claimSectionWorkflowStatuses,
        data?.Sections,
    ])

    const claimSectionWorkflowEnabled =
        isSpecialCustomerClaimTemplate &&
        !isPreview &&
        claimSectionWorkflowStatusesLive.length > 0

    const extractWorkflowErrorMessage = err => {
        const payload = err?.error
        if (typeof payload === 'string' && payload.trim()) {
            return payload.trim()
        }
        if (payload && typeof payload === 'object') {
            const nested =
                payload.Message ||
                payload.message ||
                payload.ErrorMessage ||
                payload.errorMessage
            if (nested && String(nested).trim()) {
                return String(nested).trim()
            }
        }
        if (err?.status === 400) {
            return 'Customer must be selected before completing Phase 1.'
        }
        if (err?.status === 403) {
            return 'Caller does not satisfy the role/user assignment for this section.'
        }
        return err?.message || 'Section workflow request failed.'
    }

    const handleClaimSectionComplete = useCallback(
        async (status, notes = '') => {
            if (!claimAuditId || !status?.sectionId) return
            if (
                Number(status.phaseOrder) === 1 &&
                !String(claimSelectedSupplier || '').trim()
            ) {
                errorToast(
                    'Customer must be selected before completing Phase 1.',
                )
                return
            }
            setClaimSectionWorkflowBusy(true)
            try {
                // Complete reads customer/answers from the saved audit — persist first.
                const saved = await saveRef.current?.(
                    StatusType.Save,
                    false,
                    false,
                )
                if (saved === null) {
                    errorToast(
                        'Could not save inspection before completing. Please try again.',
                    )
                    return
                }

                const response = await SectionWorkflowService.completeSection(
                    claimAuditId,
                    status.sectionId,
                    resolveClaimWorkflowTemplateId(),
                    notes,
                )
                const claimNum =
                    response?.ClaimNumber || response?.claimNumber || null
                if (claimNum) {
                    setClaimNumberDisplay(String(claimNum))
                    setData(prev =>
                        prev ? { ...prev, ClaimNumber: claimNum } : prev,
                    )
                }
                const label = status.completeLabel || 'Section'
                if (
                    response?.Success === true ||
                    response?.success === true ||
                    response?.Success === 1
                ) {
                    successToast(`${label} completed.`)
                } else {
                    successToast(`${label} completed.`)
                }
                await loadClaimSectionWorkflowStatus()
            } catch (err) {
                errorToast(extractWorkflowErrorMessage(err))
                throw err
            } finally {
                setClaimSectionWorkflowBusy(false)
            }
        },
        [
            claimAuditId,
            claimSelectedSupplier,
            claimTemplateUniqueId,
            isCustomerClaimNonRma,
            errorToast,
            loadClaimSectionWorkflowStatus,
            setData,
            successToast,
        ],
    )

    const handleClaimSectionUncomplete = useCallback(
        async status => {
            if (!claimAuditId || !status?.sectionId) return
            setClaimSectionWorkflowBusy(true)
            try {
                const response = await SectionWorkflowService.uncompleteSection(
                    claimAuditId,
                    status.sectionId,
                    resolveClaimWorkflowTemplateId(),
                )
                const label = status.completeLabel || 'Section'
                if (
                    response?.Success === true ||
                    response?.success === true ||
                    response?.Success === 1
                ) {
                    successToast(`${label} reverted.`)
                } else {
                    successToast(`${label} reverted.`)
                }
                await loadClaimSectionWorkflowStatus()
            } catch (err) {
                errorToast(extractWorkflowErrorMessage(err))
                throw err
            } finally {
                setClaimSectionWorkflowBusy(false)
            }
        },
        [
            claimAuditId,
            claimTemplateUniqueId,
            isCustomerClaimNonRma,
            errorToast,
            loadClaimSectionWorkflowStatus,
            successToast,
        ],
    )

    const reportClaimSectionEdits = useCallback(
        async ({ beforeAudit, afterAudit }) => {
            if (!claimAuditId || !claimTemplateUniqueId) return
            if (!beforeAudit || !afterAudit) return
            await reportCompletedSectionEdits({
                auditId: claimAuditId,
                templateUniqueId: resolveClaimWorkflowTemplateId(),
                beforeAudit,
                afterAudit,
                workflowStatuses: claimSectionWorkflowStatusesRef.current,
                reportEdit: payload =>
                    SectionWorkflowService.reportSectionEdit(payload),
            })
        },
        [claimAuditId, claimTemplateUniqueId, isCustomerClaimNonRma],
    )

    useEffect(() => {
        reportClaimSectionEditsRef.current = reportClaimSectionEdits
    }, [reportClaimSectionEdits])

    useEffect(() => {
        claimSectionWorkflowStatusesRef.current = claimSectionWorkflowStatusesLive
    }, [claimSectionWorkflowStatusesLive])

    const handleClaimItemSketchMedia = useCallback(
        async files => {
            if (
                !claimItemSketchQuestion?.Id ||
                !claimPortalTemplateSection?.SectionId
            ) {
                return
            }
            await addMedia(
                claimItemSketchQuestion.Id,
                files,
                claimPortalTemplateSection.SectionId,
            )
            setChanged(c => c + 1)
        },
        [
            claimItemSketchQuestion?.Id,
            claimPortalTemplateSection?.SectionId,
            addMedia,
        ],
    )

    const handleClaimComplaintMedia = useCallback(
        async files => {
            if (
                !claimDetailsQuestion?.Id ||
                !claimPortalTemplateSection?.SectionId
            ) {
                return
            }
            await addMedia(
                claimDetailsQuestion.Id,
                files,
                claimPortalTemplateSection.SectionId,
            )
            setChanged(c => c + 1)
        },
        [claimDetailsQuestion?.Id, claimPortalTemplateSection?.SectionId, addMedia],
    )

    const handleRemoveClaimItemSketchMedia = useCallback(
        async file => {
            if (
                !claimItemSketchQuestion?.Id ||
                !claimPortalTemplateSection?.SectionId
            ) {
                return null
            }
            const updated = await removeMedia(
                claimItemSketchQuestion.Id,
                file,
                claimPortalTemplateSection.SectionId,
            )
            setChanged(c => c + 1)
            return updated
        },
        [
            claimItemSketchQuestion?.Id,
            claimPortalTemplateSection?.SectionId,
            removeMedia,
        ],
    )

    const handleRemoveClaimComplaintMedia = useCallback(
        async file => {
            if (
                !claimDetailsQuestion?.Id ||
                !claimPortalTemplateSection?.SectionId
            ) {
                return null
            }
            const updated = await removeMedia(
                claimDetailsQuestion.Id,
                file,
                claimPortalTemplateSection.SectionId,
            )
            setChanged(c => c + 1)
            return updated
        },
        [
            claimDetailsQuestion?.Id,
            claimPortalTemplateSection?.SectionId,
            removeMedia,
        ],
    )

    const makeClaimMediaSaveComment = useCallback(
        (questionId, sectionId) => async (mediaItem, meta) => {
            if (
                claimReadOnly ||
                !questionId ||
                !sectionId ||
                !mediaItem
            ) {
                return null
            }

            const hasChanged =
                (meta?.Comment ?? '') !== (mediaItem?.Comment ?? '') ||
                (meta?.Caption ?? '') !== (mediaItem?.Caption ?? '')
            if (!hasChanged) {
                return null
            }

            const matchesFile = f =>
                (mediaItem?.MediaId != null &&
                    String(f?.MediaId ?? '') === String(mediaItem.MediaId)) ||
                (mediaItem?.id != null &&
                    String(f?.id ?? '') === String(mediaItem.id))

            setData(prev => {
                if (!prev?.Sections?.length) return prev
                const qid = String(questionId ?? '')
                return {
                    ...prev,
                    Sections: prev.Sections.map(section => ({
                        ...section,
                        Questions: (section.Questions || []).map(q => {
                            if (String(q?.Id ?? '') !== qid) return q
                            const nextFiles = (q.Files || []).map(f =>
                                matchesFile(f) ? { ...f, ...meta } : f,
                            )
                            const next = { ...q, Files: nextFiles }
                            if (next.Answers && typeof next.Answers === 'object') {
                                next.Answers = {
                                    ...next.Answers,
                                    MediaResponse: nextFiles,
                                }
                            }
                            return next
                        }),
                    })),
                }
            })

            try {
                await addMedia(
                    questionId,
                    [{ ...mediaItem, ...meta, local: false }],
                    sectionId,
                )
                setChanged(c => c + 1)
            } catch (error) {
                console.error('Error saving claim media caption:', error)
            }
            return null
        },
        [claimReadOnly, addMedia, setData],
    )

    const handleItemSketchMediaSaveComment = useMemo(
        () =>
            makeClaimMediaSaveComment(
                claimItemSketchQuestion?.Id,
                claimPortalTemplateSection?.SectionId,
            ),
        [
            makeClaimMediaSaveComment,
            claimItemSketchQuestion?.Id,
            claimPortalTemplateSection?.SectionId,
        ],
    )

    const handleComplaintMediaSaveComment = useMemo(
        () =>
            makeClaimMediaSaveComment(
                claimDetailsQuestion?.Id,
                claimPortalTemplateSection?.SectionId,
            ),
        [
            makeClaimMediaSaveComment,
            claimDetailsQuestion?.Id,
            claimPortalTemplateSection?.SectionId,
        ],
    )

    const claimNotesMediaProps = useMemo(() => {
        const portalQuestionId = claimPortalNotesQuestion?.Id
        const portalSectionId = claimPortalTemplateSection?.SectionId
        const qualityQuestionId = claimQualityNotesQuestion?.Id
        const qualitySectionId = claimQualitySection?.SectionId
        const completionQuestionId = claimCompletionNotesQuestion?.Id
        const completionSectionId = claimCompletionSection?.SectionId

        return {
            portalNotesMediaFiles: extractQuestionMediaFiles(
                claimPortalNotesQuestion,
            ),
            qualityNotesMediaFiles: extractQuestionMediaFiles(
                claimQualityNotesQuestion,
            ),
            completionNotesMediaFiles: extractQuestionMediaFiles(
                claimCompletionNotesQuestion,
            ),
            portalNotesQuestionId: portalQuestionId,
            qualityNotesQuestion: claimQualityNotesQuestion,
            completionNotesQuestion: claimCompletionNotesQuestion,
            onPortalNotesMedia:
                claimReadOnly || !portalQuestionId || !portalSectionId
                    ? undefined
                    : async files => {
                          await addMedia(
                              portalQuestionId,
                              files,
                              portalSectionId,
                          )
                          setChanged(c => c + 1)
                      },
            onQualityNotesMedia:
                claimReadOnly || !qualityQuestionId || !qualitySectionId
                    ? undefined
                    : async files => {
                          await addMedia(
                              qualityQuestionId,
                              files,
                              qualitySectionId,
                          )
                          setChanged(c => c + 1)
                      },
            onCompletionNotesMedia:
                claimReadOnly ||
                !completionQuestionId ||
                !completionSectionId
                    ? undefined
                    : async files => {
                          await addMedia(
                              completionQuestionId,
                              files,
                              completionSectionId,
                          )
                          setChanged(c => c + 1)
                      },
            onRemovePortalNotesMedia:
                claimReadOnly || !portalQuestionId || !portalSectionId
                    ? undefined
                    : async file => {
                          const updated = await removeMedia(
                              portalQuestionId,
                              file,
                              portalSectionId,
                          )
                          setChanged(c => c + 1)
                          return updated
                      },
            onRemoveQualityNotesMedia:
                claimReadOnly || !qualityQuestionId || !qualitySectionId
                    ? undefined
                    : async file => {
                          const updated = await removeMedia(
                              qualityQuestionId,
                              file,
                              qualitySectionId,
                          )
                          setChanged(c => c + 1)
                          return updated
                      },
            onRemoveCompletionNotesMedia:
                claimReadOnly ||
                !completionQuestionId ||
                !completionSectionId
                    ? undefined
                    : async file => {
                          const updated = await removeMedia(
                              completionQuestionId,
                              file,
                              completionSectionId,
                          )
                          setChanged(c => c + 1)
                          return updated
                      },
            onPortalNotesMediaSaveComment: claimReadOnly
                ? undefined
                : makeClaimMediaSaveComment(
                      portalQuestionId,
                      portalSectionId,
                  ),
            onQualityNotesMediaSaveComment: claimReadOnly
                ? undefined
                : makeClaimMediaSaveComment(
                      qualityQuestionId,
                      qualitySectionId,
                  ),
            onCompletionNotesMediaSaveComment: claimReadOnly
                ? undefined
                : makeClaimMediaSaveComment(
                      completionQuestionId,
                      completionSectionId,
                  ),
        }
    }, [
        claimPortalNotesQuestion,
        claimQualityNotesQuestion,
        claimCompletionNotesQuestion,
        claimPortalTemplateSection?.SectionId,
        claimQualitySection?.SectionId,
        claimCompletionSection?.SectionId,
        data?.Sections,
        claimReadOnly,
        addMedia,
        removeMedia,
        makeClaimMediaSaveComment,
    ])

    const platenFieldErrors =
        errors && typeof errors === 'object' ? errors : {}

    if (!id) {
        return (
            <div className="container mt-5">
                <FlexColumn>
                    <h2>Inspection could not be opened</h2>
                    <p>
                        This link is missing an inspection id. Go back and
                        open the inspection again.
                    </p>
                    <div>
                        <button
                            type="button"
                            className="ui primary button"
                            onClick={() => navigation('/app/task')}
                        >
                            Back to Task List
                        </button>
                    </div>
                </FlexColumn>
            </div>
        )
    }

    if (publicError) {
        return <div className="error-message">{publicError}</div>
    }

    console.log('ERRORS OBJECT:', errors)

    return (
        // review threads (question comments) of this inspection; off for public links and previews
        <QuestionThreadsProvider
            auditId={!publicScreen && !isPreview ? data?.Id : null}
            // answer change from a reply: completed inspection in read-only view only (no unsaved local edits)
            answerChangeEnabled={isCompletedAudit && isInspectionReadOnly}
            onAnswerChanged={refetch}
        >
        <div>
            <SmallDailyPlatenResultMenuGlobalStyle />
            {!publicScreen && data?.Id ? (
                <InspectionMaintenanceBanner auditId={data.Id} />
            ) : null}
            <QuestionThreadsSummary />
            {auditReadOnlyBanner && (
                <div
                    style={{
                        margin: '12px 16px 0',
                        padding: '10px 14px',
                        background: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: 8,
                        color: '#374151',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                    }}
                >
                    <span
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            padding: '2px 8px',
                            borderRadius: 6,
                            border: '1.5px solid #9ca3af',
                            background: '#fff',
                            color: '#4b5563',
                        }}
                    >
                        View only
                    </span>
                    <span>{auditReadOnlyBanner}</span>
                    {auditOwnerName ? (
                        <span style={{ color: '#6b7280' }}>
                            Owner: {auditOwnerName}
                        </span>
                    ) : null}
                </div>
            )}
            {showCompletionMessage ? (
                <div className="container mt-5">
                    <FlexColumn>
                        <h2 className="text-center">Inspection Complete</h2>
                        <p className="text-center">{completionMessage}</p>
                        <div className="text-center mt-4">
                            {location.state?.fromDashboard ? (
                                <>
                                    <button
                                        type="button"
                                        className="ui primary button"
                                        onClick={() => navigation('/app/dashboard/templates', { state: { customerGroup: location.state?.customerGroup, groupId: location.state?.customerGroup?.CustomerGroupId, customer: location.state?.customer } })}
                                        style={{
                                            background: '#2f2ccf',
                                            color: 'white',
                                            border: 'none',
                                        }}
                                    >
                                        Back to Templates
                                    </button>
                                    <button
                                        type="button"
                                        className="ui button ms-2"
                                        onClick={() => navigation('/app/dashboard/groups')}
                                        style={{
                                            background: '#2f2ccf',
                                            color: 'white',
                                            border: 'none',
                                        }}
                                    >
                                        Go to Dashboard
                                    </button>
                                    <button
                                        type="button"
                                        className="ui button ms-2"
                                        onClick={() => navigation('/app/dashboard/groups')}
                                        style={{
                                            background: '#2f2ccf',
                                            color: 'white',
                                            border: 'none',
                                        }}
                                    >
                                        Home
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="ui primary button"
                                    onClick={() => navigation('/')}
                                >
                                    Return to Home
                                </button>
                            )}
                        </div>
                    </FlexColumn>
                </div>
            ) : (
                <>
                    {location.state?.fromDashboard && (
                        <div
                            className="mb-3"
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '8px',
                                flexWrap: 'wrap',
                            }}
                        >
                            <button
                                type="button"
                                className="ui button"
                                onClick={() => navigation('/app/dashboard/groups')}
                                style={{
                                    background: '#2f2ccf',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '0.75rem 1.5rem',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 12px rgba(47, 44, 207, 0.25)',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)'
                                    e.target.style.boxShadow = '0 6px 16px rgba(47, 44, 207, 0.35)'
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)'
                                    e.target.style.boxShadow = '0 4px 12px rgba(47, 44, 207, 0.25)'
                                }}
                            >
                                Home
                            </button>
                        </div>
                    )}
                    {isLanguageFeatureEnabled && (
                        <LanguageSelectorContainer>
                            <span className="language-label">Language:</span>
                            <Dropdown
                                className="language-dropdown"
                                selection
                                options={languageOptions}
                                value={selectedLanguage}
                                onChange={(e, { value }) => setSelectedLanguage(value)}
                                disabled={translating || !data}
                                loading={translating}
                            />
                        </LanguageSelectorContainer>
                    )}
                    <FlexColumn className="mb-4 col-12">
                            <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{ margin: 0 }}>Title: </label>
                                {previousTemplateData && (
                                    <AppPrimaryButton
                                        onClick={async () => {
                                            setShowDataImportModal(true)
                                            await loadMappingQuestions()
                                        }}
                                        disabled={loadingMappings}
                                    >
                                        Data Import
                                    </AppPrimaryButton>
                                )}
                            </FlexRow>
                            <FlexRow>
                                <Input
                                    className="col-12"
                                    value={displayData?.AuditName || data?.AuditName || ''}
                                    onChange={onAuditNameChange}
                                    disabled={isInspectionReadOnly}
                                />
                            </FlexRow>
                        </FlexColumn>
                    {saving && <LoaderBar />}
                    {translating && <LoaderBar content="Translating..." />}
                    {isDailyAuditPlatenGridTemplate &&
                        sectionsBeforeSmallDailyPlatenGrid.length > 0 &&
                        !isSpecialFinishMatTemplate && (
                        <InspectionSectionList
                            list={sectionsBeforeSmallDailyPlatenGrid}
                            loading={templateLoading}
                            submit={submit}
                            onReview={handleInspectionReview}
                            onAnswerChanged={onAnswerChanged}
                            errors={errors || {}}
                            submitError={null}
                            addMedia={isPreview ? undefined : addMedia}
                            removeMedia={
                                isPreview ? undefined : removeMedia
                            }
                            addNotes={isPreview ? undefined : saveNote}
                            onBack={onBack}
                            copySection={copySection}
                            directCopySection={directCopySection}
                            removeSection={removeSection}
                            onTemplateAdd={onTemplateAdd}
                            templateId={data?.TemplateId}
                            inspectionTemplateId={data?.Id}
                            reasonPresetSetting={data?.ReasonPresetSetting}
                            commentList={data?.Comments || []}
                            refetch={refetch}
                            publicScreen={publicScreen}
                            isReadOnly={isInspectionReadOnly}
                            showActionButtons={false}
                            showValidation={showValidation}
                            isReopened={isReopened}
                            templateList={templateList}
                            validateForComplete={validateForComplete}
                            totalScore={totalScore}
                            printByTemplate={!!data?.PrintByTemplate}
                            resetBySection={!!data?.ResetBySection}
                            showSectionDropdown={showSectionDropdown}
                            isShowHorizontal={shouldForceHorizontalForTemplate}
                            setSaving={setSaving}
                            hideFooterActions
                            {...pressResetListProps}
                            onSectionNAChange={onSectionNAChange}
                            originalTemplateId={data?.OriginalTemplateId}
                            getCopyButtonLabel={getSectionCopyButtonLabel}
                        />
                    )}
                    {isPlatenGridTemplate && (
                        <div
                            className="mb-4"
                            style={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                            }}
                        >
                            <FlexRow
                                className="mb-3"
                                style={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 16,
                                }}
                            >
                                <h4
                                    style={{
                                        margin: 0,
                                        fontWeight: 700,
                                        color: '#173a73',
                                        letterSpacing: 0.2,
                                    }}
                                >
                                    {platenGridMode === PLATEN_GRID_MODE.DAILY_AUDIT
                                        ? platenGridTemplateSection?.SectionName ||
                                          'Platen'
                                        : 'Platen Inspection'}
                                </h4>
                                {isSpecialPlatenTemplate ? (
                                <FlexRow
                                    style={{
                                        alignItems: 'center',
                                        gap: 12,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: 700,
                                            color: '#173a73',
                                            textTransform: 'uppercase',
                                            fontSize: 13,
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        Select Press
                                    </span>
                                    <Dropdown
                                        selection
                                        placeholder="-- Select --"
                                        error={
                                            showValidation &&
                                            !!platenFieldErrors[
                                                SPECIAL_PLATEN_ERR_PRESS
                                            ]
                                        }
                                        style={{
                                            minWidth: 260,
                                            borderRadius: 8,
                                            border: '1px solid #bcd4ea',
                                        }}
                                        value={
                                            getSelectedMultipleText(specialPressQuestion) || ''
                                        }
                                        options={specialPressDropdownOptions}
                                        disabled={specialPlatenReadOnly}
                                        onChange={(e, { value }) =>
                                            updateSpecialPressSelection(
                                                typeof value === 'string' ? value : '',
                                            )
                                        }
                                    />
                                </FlexRow>
                                ) : null}
                            </FlexRow>
                            <SpecialPlatenTableScroll className="w-100">
                                <Table
                                    className="special-platen-table"
                                    celled
                                    compact="very"
                                    unstackable
                                    style={{
                                        width: '100%',
                                        minWidth: 1260,
                                        tableLayout: 'fixed',
                                        marginBottom: 0,
                                        background: '#fff',
                                    }}
                                >
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell
                                                singleLine
                                                style={{
                                                    width:
                                                        SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH.rowIndex,
                                                }}
                                            >
                                                DECK #
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                style={{
                                                    width:
                                                        SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH.platen,
                                                }}
                                            >
                                                PLATEN #
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                style={{
                                                    width:
                                                        SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH.pressure,
                                                }}
                                            >
                                                PRESSURE
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                style={{
                                                    width:
                                                        SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH.temp,
                                                }}
                                            >
                                                TEMP
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                style={{
                                                    width:
                                                        SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH.result,
                                                }}
                                            >
                                                RESULT
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                style={{
                                                    width:
                                                        SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH.comments,
                                                }}
                                            >
                                                COMMENTS
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                style={{
                                                    width:
                                                        SPECIAL_PLATEN_INSPECTION_TABLE_COLUMN_WIDTH.media,
                                                }}
                                            >
                                                MEDIA
                                            </Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {specialPlatenRowsForDisplay.map(
                                            ({ row, index: rowIndex }) => {
                                            const tone = classifySpecialPlatenResult(
                                                row.result,
                                                specialResultQuestion,
                                            )
                                            return (
                                                <Table.Row
                                                    key={row.platenLabel || rowIndex}
                                                    positive={tone === 'conforming'}
                                                    negative={tone === 'nonconforming'}
                                                    warning={tone === 'bypass'}
                                                >
                                                    <Table.Cell verticalAlign="top">
                                                        {row.platenLabel}
                                                    </Table.Cell>
                                                    <Table.Cell verticalAlign="top">
                                                        <Input
                                                            fluid
                                                            value={row.mold}
                                                            placeholder="Platen #"
                                                            error={
                                                                showValidation &&
                                                                !!platenFieldErrors[
                                                                    specialPlatenRowFieldErrKey(
                                                                        rowIndex,
                                                                        'mold',
                                                                    )
                                                                ]
                                                            }
                                                            disabled={specialPlatenReadOnly}
                                                            onChange={e =>
                                                                updateSpecialRowField(
                                                                    rowIndex,
                                                                    'mold',
                                                                    e.target.value,
                                                                )
                                                            }
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell verticalAlign="top">
                                                        <Input
                                                            fluid
                                                            value={row.pressure}
                                                            placeholder="Pressure"
                                                            disabled={specialPlatenReadOnly}
                                                            onChange={e =>
                                                                updateSpecialRowField(
                                                                    rowIndex,
                                                                    'pressure',
                                                                    e.target.value,
                                                                )
                                                            }
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell verticalAlign="top">
                                                        <Input
                                                            fluid
                                                            value={row.temp}
                                                            placeholder="Temp"
                                                            disabled={specialPlatenReadOnly}
                                                            onChange={e =>
                                                                updateSpecialRowField(
                                                                    rowIndex,
                                                                    'temp',
                                                                    e.target.value,
                                                                )
                                                            }
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell verticalAlign="top">
                                                        <Dropdown
                                                            fluid
                                                            selection
                                                            className={`platen-dd-accent platen-dd-accent-${
                                                                tone === 'conforming' ||
                                                                    tone === 'nonconforming' ||
                                                                    tone === 'bypass'
                                                                    ? tone
                                                                    : 'neutral'
                                                            }`}
                                                            placeholder="-- Select --"
                                                            error={
                                                                showValidation &&
                                                                !!platenFieldErrors[
                                                                    specialPlatenRowFieldErrKey(
                                                                        rowIndex,
                                                                        'result',
                                                                    )
                                                                ]
                                                            }
                                                            style={getSpecialResultTriggerStyle(
                                                                row.result,
                                                                specialResultQuestion,
                                                            )}
                                                            options={
                                                                specialResultDropdownOptions
                                                            }
                                                            value={row.result || ''}
                                                            disabled={specialPlatenReadOnly}
                                                            onChange={(e, selected) =>
                                                                updateSpecialRowField(
                                                                    rowIndex,
                                                                    'result',
                                                                    String(
                                                                        selected?.value ??
                                                                            '',
                                                                    ).trim(),
                                                                )
                                                            }
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell verticalAlign="top">
                                                        <Input
                                                            fluid
                                                            value={row.comments}
                                                            placeholder="Comments"
                                                            disabled={specialPlatenReadOnly}
                                                            onChange={e =>
                                                                updateSpecialRowField(
                                                                    rowIndex,
                                                                    'comments',
                                                                    e.target.value,
                                                                )
                                                            }
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell verticalAlign="top">
                                                        <SpecialPlatenMediaCell
                                                            files={row.mediaFiles || []}
                                                            isReadOnly={
                                                                specialPlatenReadOnly
                                                            }
                                                            onUpload={
                                                                specialPlatenReadOnly
                                                                    ? undefined
                                                                    : files =>
                                                                          addSpecialRowMedia(
                                                                              rowIndex,
                                                                              files,
                                                                          )
                                                            }
                                                            onRemove={
                                                                specialPlatenReadOnly
                                                                    ? undefined
                                                                    : file =>
                                                                          removeSpecialRowMedia(
                                                                              rowIndex,
                                                                              file,
                                                                          )
                                                            }
                                                            onSaveComment={
                                                                specialPlatenReadOnly
                                                                    ? undefined
                                                                    : (mediaItem, meta) =>
                                                                          saveSpecialRowMediaComment(
                                                                              rowIndex,
                                                                              mediaItem,
                                                                              meta,
                                                                          )
                                                            }
                                                        />
                                                    </Table.Cell>
                                                </Table.Row>
                                            )
                                        })}
                                    </Table.Body>
                                </Table>
                            </SpecialPlatenTableScroll>
                            {isSpecialPlatenTemplate ? (
                            <FlexRow
                                className="mt-3 bottom-action-bar"
                                style={{
                                    flexWrap: 'wrap',
                                    gap: 12,
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <FlexRow
                                    style={{
                                        flexWrap: 'wrap',
                                        gap: 8,
                                        alignItems: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: 8,
                                            background: '#e8f5e9',
                                            color: '#256029',
                                            fontWeight: 700,
                                            fontSize: 13,
                                        }}
                                    >
                                        Conforming: {specialPlatenResultCounts.conforming}
                                    </span>
                                    <span
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: 8,
                                            background: '#fdecea',
                                            color: '#c62828',
                                            fontWeight: 700,
                                            fontSize: 13,
                                        }}
                                    >
                                        Non-Conforming: {specialPlatenResultCounts.nonConforming}
                                    </span>
                                    <span
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: 8,
                                            background: '#fff8e1',
                                            color: '#bf360c',
                                            fontWeight: 700,
                                            fontSize: 13,
                                        }}
                                    >
                                        By Pass: {specialPlatenResultCounts.bypass}
                                    </span>
                                </FlexRow>
                                <FlexRow
                                    style={{
                                        gap: 8,
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={clearSpecialPlatenGrid}
                                        disabled={specialPlatenReadOnly}
                                    >
                                        Clear All
                                    </AppSecondaryButton>
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={() => submit(StatusType.Save)}
                                        disabled={specialPlatenReadOnly}
                                    >
                                        Save & Close
                                    </AppSecondaryButton>
                                    <AppPrimaryButton
                                        type="button"
                                        onClick={handleInspectionReview}
                                        disabled={specialPlatenReadOnly}
                                    >
                                        Review
                                    </AppPrimaryButton>
                                    <AppPrimaryButton
                                        type="button"
                                        onClick={() => submit(StatusType.Completed)}
                                        disabled={specialPlatenReadOnly}
                                    >
                                        Submit
                                    </AppPrimaryButton>
                                </FlexRow>
                            </FlexRow>
                            ) : null}
                        </div>
                    )}
                    {isSpecialCustomerClaimTemplate && (
                        <CustomerClaimPortalInspection
                            supplierOptions={claimSupplierOptions}
                            matTypeOptions={claimMatTypeOptions}
                            sizeOptions={claimSizeOptions}
                            codeOptions={claimCodeOptions}
                            carrierOptions={claimCarrierOptions}
                            packingOptions={claimPackingOptions}
                            dispositionOptions={claimDispositionOptions}
                            statusOptions={claimStatusOptions}
                            creditIssuePortalOptions={claimCreditPortalOptions}
                            creditIssueCompletionOptions={
                                claimCreditCompletionOptions
                            }
                            selectedSupplier={claimSelectedSupplier}
                            onSupplierChange={setClaimSelectedSupplier}
                            portalForm={claimPortalDraft}
                            onPortalFormChange={setClaimPortalDraft}
                            qualityForm={claimQualityForm}
                            onQualityChange={setClaimQualityForm}
                            completionForm={claimCompletionForm}
                            onCompletionChange={setClaimCompletionForm}
                            loggedRows={claimLoggedRows}
                            onClearForm={handleClearClaimForm}
                            fieldErrors={claimFieldErrors}
                            showValidation={showValidation}
                            readOnly={claimReadOnly}
                            onSaveAndClose={() => submit(StatusType.Save)}
                            onReview={handleInspectionReview}
                            onSubmit={() => submit(StatusType.Completed)}
                            portalFieldQuestions={{
                                itemSketch: claimItemSketchQuestion,
                                quantity: claimQuantityQuestion,
                                sfoOrder: claimSfoOrderQuestion,
                                rma: claimRmaQuestion,
                                complaintDetails: claimDetailsQuestion,
                                notes: claimPortalNotesQuestion,
                            }}
                            itemSketchQuestionId={claimItemSketchQuestion?.Id}
                            complaintDetailsQuestionId={
                                claimDetailsQuestion?.Id
                            }
                            itemSketchMediaFiles={claimItemSketchMediaFiles}
                            complaintMediaFiles={claimDetailsMediaFiles}
                            onItemSketchMedia={handleClaimItemSketchMedia}
                            onComplaintMedia={handleClaimComplaintMedia}
                            onRemoveItemSketchMedia={
                                claimReadOnly
                                    ? undefined
                                    : handleRemoveClaimItemSketchMedia
                            }
                            onRemoveComplaintMedia={
                                claimReadOnly
                                    ? undefined
                                    : handleRemoveClaimComplaintMedia
                            }
                            onItemSketchMediaSaveComment={
                                claimReadOnly
                                    ? undefined
                                    : handleItemSketchMediaSaveComment
                            }
                            onComplaintMediaSaveComment={
                                claimReadOnly
                                    ? undefined
                                    : handleComplaintMediaSaveComment
                            }
                            {...claimNotesMediaProps}
                            claimPortalQuestionMap={claimPortalQuestionMap}
                            claimQualityQuestionMap={claimQualityQuestionMap}
                            claimCompletionQuestionMap={
                                claimCompletionQuestionMap
                            }
                            addMedia={
                                claimReadOnly || isPreview
                                    ? undefined
                                    : async (questionId, files, sectionId) => {
                                          await addMedia(
                                              questionId,
                                              files,
                                              sectionId,
                                          )
                                          setChanged(c => c + 1)
                                      }
                            }
                            removeMedia={
                                claimReadOnly || isPreview
                                    ? undefined
                                    : async (questionId, file, sectionId) => {
                                          const updated = await removeMedia(
                                              questionId,
                                              file,
                                              sectionId,
                                          )
                                          setChanged(c => c + 1)
                                          return updated
                                      }
                            }
                            getClaimMediaSaveComment={
                                claimReadOnly
                                    ? undefined
                                    : makeClaimMediaSaveComment
                            }
                            onCopySection={copySection}
                            portalSectionId={claimPortalTemplateSection?.SectionId}
                            qualitySectionId={claimQualitySection?.SectionId}
                            completionSectionId={
                                claimCompletionSection?.SectionId
                            }
                            receivingDateUsesTime={claimReceivingDateUsesTime}
                            sectionWorkflowEnabled={claimSectionWorkflowEnabled}
                            sectionWorkflowStatuses={claimSectionWorkflowStatusesLive}
                            claimNumber={
                                claimNumberDisplay || data?.ClaimNumber || ''
                            }
                            claimCustomerName={claimSelectedSupplier}
                            onSectionComplete={handleClaimSectionComplete}
                            onSectionUncomplete={handleClaimSectionUncomplete}
                            sectionWorkflowBusy={claimSectionWorkflowBusy}
                            auditSections={data?.Sections}
                            templateData={data}
                            answersResetKey={id}
                            onExtraAnswersChange={next => {
                                claimExtraAnswersRef.current = next || {}
                            }}
                        />
                    )}
                    {!isSpecialPlatenTemplate &&
                        !isSpecialCustomerClaimTemplate && (
                        <>
                            {isSpecialFinishMatTemplate && finishMatGroupCount > 0 &&
                                finishMatRenderGroups.map((group, groupIndex) => {
                                    const isPortalLayout = group.layout === 'portal'
                                    const isHorizontalLayout =
                                        group.layout === 'horizontal'
                                    const isLastFinishMatGroup =
                                        groupIndex === finishMatGroupCount - 1
                                    return (
                                        <InspectionSectionList
                                            key={`finish-mat-${group.layout}-${group.sections
                                                .map(section => section.SectionId)
                                                .join('_')}`}
                                            list={group.sections}
                                            loading={templateLoading}
                                            submit={submit}
                                            onReview={handleInspectionReview}
                                            onAnswerChanged={onAnswerChanged}
                                            errors={errors || {}}
                                            submitError={
                                                isPortalLayout ? null : error
                                            }
                                            addMedia={
                                                isPreview ? undefined : addMedia
                                            }
                                            removeMedia={
                                                isPreview
                                                    ? undefined
                                                    : removeMedia
                                            }
                                            addNotes={
                                                isPreview ? undefined : saveNote
                                            }
                                            onBack={onBack}
                                            copySection={copySection}
                                            directCopySection={directCopySection}
                                            removeSection={removeSection}
                                            onTemplateAdd={onTemplateAdd}
                                            templateId={data?.TemplateId}
                                            inspectionTemplateId={data?.Id}
                                            reasonPresetSetting={data?.ReasonPresetSetting}
                                            commentList={data?.Comments || []}
                                            refetch={refetch}
                                            publicScreen={publicScreen}
                                            isReadOnly={isInspectionReadOnly}
                                            showActionButtons={
                                                isLastFinishMatGroup &&
                                                !isPortalLayout &&
                                                showInspectionActionButtons
                                            }
                                            showValidation={showValidation}
                                            isReopened={isReopened}
                                            templateList={templateList}
                                            validateForComplete={validateForComplete}
                                            totalScore={
                                                isLastFinishMatGroup
                                                    ? totalScore
                                                    : undefined
                                            }
                                            totalQuestions={
                                                isLastFinishMatGroup
                                                    ? templateTotalQuestions
                                                    : undefined
                                            }
                                            printByTemplate={
                                                !!data?.PrintByTemplate
                                            }
                                            resetBySection={
                                                !!data?.ResetBySection
                                            }
                                            showSectionDropdown={
                                                showSectionDropdown
                                            }
                                            isShowHorizontal={isHorizontalLayout}
                                            portalCardSectionIds={
                                                isPortalLayout
                                                    ? [
                                                          FINISH_MAT_GENERAL_INFO_SECTION_ID,
                                                      ]
                                                    : undefined
                                            }
                                            portalCardSectionNames={
                                                isPortalLayout
                                                    ? [
                                                          FINISH_MAT_GENERAL_INFO_SECTION_NAME,
                                                      ]
                                                    : undefined
                                            }
                                            hideHorizontalAddNew={
                                                isPreview && isHorizontalLayout
                                            }
                                            setSaving={setSaving}
                                            hideFooterActions={
                                                isPortalLayout ||
                                                !isLastFinishMatGroup
                                            }
                                            hideSectionToolbar
                                            {...pressResetListProps}
                                            onSectionNAChange={onSectionNAChange}
                                            originalTemplateId={data?.OriginalTemplateId}
                                            getCopyButtonLabel={getSectionCopyButtonLabel}
                                        />
                                    )
                                })}
                            {smallDailyPlatenGridSection &&
                                sectionsBeforeSmallDailyPlatenGrid.length > 0 &&
                                !isSpecialFinishMatTemplate &&
                                !isDailyAuditPlatenGridTemplate && (
                                <InspectionSectionList
                                    list={sectionsBeforeSmallDailyPlatenGrid}
                                    loading={templateLoading}
                                    submit={submit}
                                    onReview={handleInspectionReview}
                                    onAnswerChanged={onAnswerChanged}
                                    errors={errors || {}}
                                    submitError={null}
                                    addMedia={isPreview ? undefined : addMedia}
                                    removeMedia={
                                        isPreview ? undefined : removeMedia
                                    }
                                    addNotes={isPreview ? undefined : saveNote}
                                    onBack={onBack}
                                    copySection={copySection}
                                    directCopySection={directCopySection}
                                    removeSection={removeSection}
                                    onTemplateAdd={onTemplateAdd}
                                    templateId={data?.TemplateId}
                                    inspectionTemplateId={data?.Id}
                                    reasonPresetSetting={data?.ReasonPresetSetting}
                                    commentList={data?.Comments || []}
                                    refetch={refetch}
                                    publicScreen={publicScreen}
                                    isReadOnly={isInspectionReadOnly}
                                    showActionButtons={false}
                                    showValidation={showValidation}
                                    isReopened={isReopened}
                                    templateList={templateList}
                                    validateForComplete={validateForComplete}
                                    totalScore={totalScore}
                                    printByTemplate={!!data?.PrintByTemplate}
                                    resetBySection={!!data?.ResetBySection}
                                    showSectionDropdown={showSectionDropdown}
                                    isShowHorizontal={shouldForceHorizontalForTemplate}
                                    setSaving={setSaving}
                                    hideFooterActions
                                    {...pressResetListProps}
                                    onSectionNAChange={onSectionNAChange}
                                    originalTemplateId={data?.OriginalTemplateId}
                                    getCopyButtonLabel={getSectionCopyButtonLabel}
                                />
                            )}
                            {smallDailyPlatenGridSection && !isPlatenGridTemplate && (
                                <SmallDailyPlatenSectionWrap>
                                    <h4
                                        className="mb-3"
                                        style={{
                                            margin: 0,
                                            fontWeight: 700,
                                            color: '#173a73',
                                            letterSpacing: 0.2,
                                        }}
                                    >
                                        {smallDailyPlatenGridSection?.SectionName ||
                                            'Platen'}
                                    </h4>
                                    <SmallDailyPlatenTableScroll className="w-100">
                                        <Table
                                            className="small-daily-platen-table"
                                            celled
                                            compact="very"
                                            unstackable
                                            style={{
                                                width: '100%',
                                                minWidth: 1260,
                                                tableLayout: 'fixed',
                                                marginBottom: 0,
                                                background: '#fff',
                                            }}
                                        >
                                            <Table.Header>
                                                <Table.Row>
                                                    <Table.HeaderCell
                                                        singleLine
                                                        style={{
                                                            width:
                                                                SMALL_DAILY_PLATEN_TABLE_COLUMN_WIDTH.rowIndex,
                                                        }}
                                                    >
                                                        DECK #
                                                    </Table.HeaderCell>
                                                    {smallDailyPlatenColumnDefs.map(
                                                        col => (
                                                            <Table.HeaderCell
                                                                key={col.fieldKey}
                                                                style={
                                                                    col.width
                                                                        ? {
                                                                              width: col.width,
                                                                          }
                                                                        : undefined
                                                                }
                                                            >
                                                                {col.header}
                                                            </Table.HeaderCell>
                                                        ),
                                                    )}
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {[...smallDailyPlatenRows]
                                                    .reverse()
                                                    .map((row, rowIndex) => (
                                                        <Table.Row
                                                            key={
                                                                row.section
                                                                    ?.SectionId ||
                                                                rowIndex
                                                            }
                                                        >
                                                            <Table.Cell
                                                                verticalAlign="top"
                                                                style={{
                                                                    width:
                                                                        SMALL_DAILY_PLATEN_TABLE_COLUMN_WIDTH.rowIndex,
                                                                }}
                                                            >
                                                                {smallDailyPlatenRows.length -
                                                                    rowIndex}
                                                            </Table.Cell>
                                                            {smallDailyPlatenColumnDefs.map(
                                                                col => {
                                                                    const question =
                                                                        row.byField[
                                                                            col
                                                                                .fieldKey
                                                                        ]
                                                                    const cellWidthStyle =
                                                                        col.width
                                                                            ? {
                                                                                  width: col.width,
                                                                              }
                                                                            : undefined
                                                                    if (
                                                                        col.isMedia
                                                                    ) {
                                                                        return (
                                                                            <Table.Cell
                                                                                key={
                                                                                    col.fieldKey
                                                                                }
                                                                                verticalAlign="top"
                                                                                style={
                                                                                    cellWidthStyle
                                                                                }
                                                                            >
                                                                                <SmallDailyPlatenMediaCell
                                                                                    mediaQuestion={
                                                                                        question
                                                                                    }
                                                                                    sectionId={
                                                                                        row
                                                                                            .section
                                                                                            ?.SectionId
                                                                                    }
                                                                                    sections={
                                                                                        data?.Sections ||
                                                                                        []
                                                                                    }
                                                                                    isReadOnly={
                                                                                        smallDailyPlatenFieldDisabled
                                                                                    }
                                                                                    addMedia={
                                                                                        isPreview
                                                                                            ? undefined
                                                                                            : addMedia
                                                                                    }
                                                                                    removeMedia={
                                                                                        isPreview
                                                                                            ? undefined
                                                                                            : removeMedia
                                                                                    }
                                                                                />
                                                                            </Table.Cell>
                                                                        )
                                                                    }
                                                                    if (
                                                                        col.isResult &&
                                                                        question
                                                                    ) {
                                                                        const resultValue =
                                                                            normalizePlatenResultDisplayFromQuestion(
                                                                                getSelectedMultipleText(
                                                                                    question,
                                                                                ),
                                                                                question,
                                                                            )
                                                                        const resultTone =
                                                                            classifySpecialPlatenResult(
                                                                                resultValue,
                                                                                question,
                                                                            )
                                                                        return (
                                                                            <Table.Cell
                                                                                key={
                                                                                    col.fieldKey
                                                                                }
                                                                                verticalAlign="top"
                                                                                style={
                                                                                    cellWidthStyle
                                                                                }
                                                                            >
                                                                                <SmallDailyPlatenResultDropdown
                                                                                    className={`platen-dd-accent platen-dd-accent-${
                                                                                        resultTone ===
                                                                                            'conforming' ||
                                                                                        resultTone ===
                                                                                            'nonconforming' ||
                                                                                        resultTone ===
                                                                                            'bypass'
                                                                                            ? resultTone
                                                                                            : 'neutral'
                                                                                    }`}
                                                                                    placeholder="-- Select --"
                                                                                    style={getSpecialResultTriggerStyle(
                                                                                        resultValue,
                                                                                        question,
                                                                                    )}
                                                                                    options={buildPlatenResultDropdownOptionsFromQuestion(
                                                                                        question,
                                                                                    )}
                                                                                    value={
                                                                                        resultValue ||
                                                                                        ''
                                                                                    }
                                                                                    disabled={
                                                                                        smallDailyPlatenFieldDisabled
                                                                                    }
                                                                                    onChange={selectedValue =>
                                                                                        updateSmallDailyPlatenField(
                                                                                            row
                                                                                                .section
                                                                                                ?.SectionId,
                                                                                            question,
                                                                                            String(
                                                                                                selectedValue ??
                                                                                                    '',
                                                                                            ).trim(),
                                                                                            {
                                                                                                isMultipleResult: true,
                                                                                            },
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </Table.Cell>
                                                                        )
                                                                    }
                                                                    return (
                                                                        <Table.Cell
                                                                            key={
                                                                                col.fieldKey
                                                                            }
                                                                            verticalAlign="top"
                                                                            style={
                                                                                cellWidthStyle
                                                                            }
                                                                        >
                                                                            <Input
                                                                                fluid
                                                                                value={getSingleQuestionText(
                                                                                    question,
                                                                                )}
                                                                                placeholder={
                                                                                    col.placeholder
                                                                                }
                                                                                disabled={
                                                                                    smallDailyPlatenFieldDisabled
                                                                                }
                                                                                onChange={e =>
                                                                                    updateSmallDailyPlatenField(
                                                                                        row
                                                                                            .section
                                                                                            ?.SectionId,
                                                                                        question,
                                                                                        e
                                                                                            .target
                                                                                            .value,
                                                                                    )
                                                                                }
                                                                            />
                                                                        </Table.Cell>
                                                                    )
                                                                },
                                                            )}
                                                        </Table.Row>
                                                    ),
                                                )}
                                            </Table.Body>
                                        </Table>
                                    </SmallDailyPlatenTableScroll>
                                    <FlexRow
                                        style={{
                                            justifyContent: 'flex-end',
                                            marginTop: 12,
                                        }}
                                    >
                                        <AppPrimaryButton
                                            type="button"
                                            onClick={() =>
                                                directCopySection(
                                                    smallDailyPlatenGridSection?.SectionId,
                                                    smallDailyPlatenGridSection?.SectionName,
                                                )
                                            }
                                            disabled={isInspectionReadOnly}
                                        >
                                            Add New
                                        </AppPrimaryButton>
                                    </FlexRow>
                                </SmallDailyPlatenSectionWrap>
                            )}
                            {!isSpecialFinishMatTemplate && (
                            <InspectionSectionList
                                list={inspectionSectionListForRender}
                            loading={templateLoading}
                            submit={submit}
                            onReview={handleInspectionReview}
                            onAnswerChanged={onAnswerChanged}
                            errors={errors || {}}
                            submitError={error}
                            addMedia={isPreview ? undefined : addMedia}
                            removeMedia={isPreview ? undefined : removeMedia}
                            addNotes={isPreview ? undefined : saveNote}
                            onBack={onBack}
                            copySection={copySection}
                            directCopySection={directCopySection}
                            removeSection={removeSection}
                            onTemplateAdd={onTemplateAdd}
                            templateId={data?.TemplateId}
                            inspectionTemplateId={data?.Id}
                            reasonPresetSetting={data?.ReasonPresetSetting}
                            commentList={data?.Comments || []}
                            refetch={refetch}
                            publicScreen={publicScreen}
                            isReadOnly={isInspectionReadOnly}
                            showActionButtons={showInspectionActionButtons}
                            showValidation={showValidation}
                            isReopened={isReopened}
                            templateList={templateList}
                            validateForComplete={validateForComplete}
                            totalScore={totalScore}
                            printByTemplate={!!data?.PrintByTemplate}
                            resetBySection={!!data?.ResetBySection}
                            showSectionDropdown={showSectionDropdown}
                            isShowHorizontal={inspectionSectionListIsHorizontal}
                            setSaving={setSaving}
                            hideSectionToolbar
                            {...pressResetListProps}
                            onSectionNAChange={onSectionNAChange}
                            originalTemplateId={data?.OriginalTemplateId}
                            getCopyButtonLabel={getSectionCopyButtonLabel}
                        />
                            )}
                        </>
                    )}
                </>
            )}

            <Modal
                open={copySectionModalOpen}
                onClose={() => setCopySectionModalOpen(false)}
                size="tiny"
                className="model"
            >
                <Modal.Header>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <span>
                            {copyModalUsesAddNew
                                ? 'Add New Section'
                                : 'Copy Section'}
                        </span>
                        <X
                            className="pointer"
                            size={32}
                            onClick={() => setCopySectionModalOpen(false)}
                        />
                    </div>
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <label>Section name</label>
                            <input
                                type="text"
                                value={copySectionName}
                                placeholder="Enter section name"
                                onChange={e =>
                                    setCopySectionName(e.target.value)
                                }
                            />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type="button"
                        onClick={() => setCopySectionModalOpen(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <AppPrimaryButton
                        type="button"
                        onClick={handleCopySectionConfirm}
                        disabled={saving || !copySectionName.trim()}
                    >
                        {copyModalUsesAddNew ? 'Add New' : 'Copy'}
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>

            <Modal
                open={pressDownModalOpen}
                onClose={() => setPressDownModalOpen(false)}
                size="mini"
                className="model"
            >
                <Modal.Header>Confirm Down</Modal.Header>
                <Modal.Content>Are you sure for Down?</Modal.Content>
                <Modal.Actions>
                    <Button
                        type="button"
                        onClick={() => setPressDownModalOpen(false)}
                        disabled={saving}
                    >
                        No
                    </Button>
                    <AppPrimaryButton
                        type="button"
                        onClick={handlePressDownConfirm}
                        disabled={saving}
                    >
                        Yes
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>

            {/* Data Import Modal */}
            <Modal open={showDataImportModal} onClose={() => setShowDataImportModal(false)} size="small">
                <Modal.Header>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Data Import</span>
                        <X className="pointer" size={32} onClick={() => setShowDataImportModal(false)} />
                    </div>
                </Modal.Header>
                <ScrollableContent>
                    {loadingMappings && <LoaderBar content="Loading questions..." />}
                    {!loadingMappings && mappingQuestions.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                            No questions available for import
                        </div>
                    )}
                    {!loadingMappings && mappingQuestions.length > 0 && (
                        <Table>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <Checkbox
                                            checked={selectedQuestions.length === mappingQuestions.length}
                                            indeterminate={
                                                selectedQuestions.length > 0 &&
                                                selectedQuestions.length < mappingQuestions.length
                                            }
                                            onChange={handleSelectAll}
                                            label="Select All"
                                        />
                                        <span style={{ marginLeft: '10px', color: '#666' }}>
                                            {selectedQuestions.length} of {mappingQuestions.length} selected
                                        </span>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>Question</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {mappingQuestions.map(question => (
                                    <Table.Row key={question.QuestionId}>
                                        <Table.Cell collapsing>
                                            <Checkbox
                                                checked={selectedQuestions.includes(question.QuestionId)}
                                                onChange={() => handleQuestionToggle(question.QuestionId)}
                                            />
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: question.QuestionText || '',
                                                }}
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )}
                </ScrollableContent>
                <Modal.Actions>
                    <AppSecondaryButton onClick={() => setShowDataImportModal(false)}>
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        onClick={handleSaveDataImport}
                        disabled={loadingMappings || selectedQuestions.length === 0}
                    >
                        Import Selected ({selectedQuestions.length})
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
        </div>
        </QuestionThreadsProvider>
    )
}
